/**
 * SellerLiveConsole · STEP 8-LIVE-CONSOLE-UI
 *
 * 판매자 전용 LIVE Console (mobile 세로 우선 · PC dashboard mode).
 * 방송 준비 → 3·2·1 카운트다운 → LiveKit publishing → 보조 Console 3 탭.
 *
 * REUSE (재개발 금지 · 백엔드 무변경):
 *   · POST /seller/streaming/sessions · /start · /end · /token (LiveKit publisher)
 *   · GET  /seller/streaming/config
 *   · GET  /public/streaming/sessions/{sid}/live-context (상품 · 판매자 등)
 *   · GET  /seller/broadcasts/{sid}/live-products (상세 상품 목록)
 *   · GET  /public/streaming/sessions/{sid}/chat  · POST /chat (Rate Limit)
 *   · POST/DELETE /seller/streaming/sessions/{sid}/pin
 *   · POST /seller/broadcasts/{sid}/parse-comment (댓글주문 후보 · 기존 Comment Engine)
 *   · GET  /seller/broadcasts/{sid}/comment-candidates (기존)
 *
 * Solo Seller Mode 대비: URL query `?sid=` 를 SoT 로 사용 · 상태를 컨텍스트에
 * 넣지 않아 향후 두 기기(Camera phone A + Control PC B)가 같은 세션에 각각
 * 접속 가능한 구조 유지 (multi-device backend 는 향후 STEP).
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Radio, ArrowLeft, ArrowRight, Camera, Mic, MicOff, ChevronLeft, ChevronRight, Clock,
  StopCircle, RefreshCw, Package, MessageCircle, Wifi, X, Pin, PinOff, Send,
  Users, VideoOff, AlertTriangle, ShoppingBag, Sparkles, MoreHorizontal, Radio as RadioIcon,
  EyeOff, Settings, Layers, BarChart3, LogOut, Megaphone, Copy, Heart, Plus, CheckCircle2,
  Zap, FlaskConical, Move, Eye, MonitorPlay, Gamepad2,
} from 'lucide-react';
import LivePreviewModal from '@/components/seller/live/LivePreviewModal';
import { RecordingOverview } from '@/components/seller/live/BroadcastRecordingPanel';
import { Room, RoomEvent, Track, ConnectionState } from 'livekit-client';
import api from '@/lib/api';
// STEP MOA-LIVE-ASSISTANT-UI-PREP-1 · 라모 AI 음성 도우미 캡슐 (UI only · 음성/AI 미연결)
// STEP MOA-LIVE-VOICE-1B · voice engine hook 연동 (PTT + Whisper + 명령 분류)
// STEP MOA-LIVE-VOICE-1C · Vosk-browser "라모야" 로컬 호출어 provider (PC/Android)
import RamoAiCapsule from '@/components/seller/live/RamoAiCapsule';
import useRamoVoice from '@/hooks/useRamoVoice';
import createVoskWakeWord, { voskIsIOS } from '@/lib/ramoWakeWordVosk';
import useRamoAutoVoice from '@/hooks/useRamoAutoVoice';
import StudioOverlayStage, { useOverlayLayout, useStudioNoticeHidden } from '@/components/seller/live/StudioOverlayStage';
import { useShortToast } from '@/lib/useShortToast';
import StudioNoticeLibraryList from '@/components/seller/live/StudioNoticeLibraryList';
import PcStatusStrip from '@/components/seller/live/PcStatusStrip';
import StudioCurrentProductCard from '@/components/seller/live/StudioCurrentProductCard';
import { BottomBarToggle, RailCollapsedTab } from '@/components/seller/live/ControlBarHandle';

// STEP 8-I · Basic Camera Filter presets (PREVIEW ONLY · Seller video 태그 CSS filter).
// Buyer Viewer / YouTube Egress 에는 미적용 (custom TrackProcessor 별도 STEP).
// 상품 실색상 왜곡 최소화 정책 (Commerce LIVE) — 얼굴/AR/변형 X.
const FILTER_PRESETS = [
  { key: 'original', label: '원본',
    values: { brightness: 1, saturate: 1, contrast: 1, sepia: 0 } },
  { key: 'natural',  label: '자연',
    values: { brightness: 1.05, saturate: 1.10, contrast: 1.05, sepia: 0 } },
  { key: 'bright',   label: '화사',
    values: { brightness: 1.12, saturate: 1.25, contrast: 1.08, sepia: 0 } },
  { key: 'warm',     label: '웜',
    values: { brightness: 1.05, saturate: 1.15, contrast: 1.03, sepia: 0.12 } },
  { key: 'vivid',    label: '선명',
    values: { brightness: 1.02, saturate: 1.15, contrast: 1.18, sepia: 0 } },
];
// intensity 0~100 사이에서 identity(1 / 0) 로부터 preset value 까지 linear blend.
// intensity=0 이면 CSS none · intensity=100 이면 preset 100% 적용.
const computeFilterCss = (modeKey, intensity) => {
  if (!modeKey || modeKey === 'original') return 'none';
  const preset = FILTER_PRESETS.find((p) => p.key === modeKey);
  if (!preset) return 'none';
  const t = Math.max(0, Math.min(100, Number(intensity) || 0)) / 100;
  const blend = (id, target) => id + (target - id) * t;
  const b = blend(1, preset.values.brightness).toFixed(3);
  const s = blend(1, preset.values.saturate).toFixed(3);
  const c = blend(1, preset.values.contrast).toFixed(3);
  const sp = blend(0, preset.values.sepia).toFixed(3);
  return `brightness(${b}) saturate(${s}) contrast(${c})${preset.values.sepia > 0 ? ` sepia(${sp})` : ''}`;
};
import SafeImg from '@/components/SafeImg';
import OverlayTimerControl from '@/components/OverlayTimerControl';
// STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · Common LIVE Operations hook REUSE.
// setActive / applyPreset / applyPosition / toggleSizeTable / inventory /
// QR 를 Studio 와 동일한 canonical /announce contract 로 통일.
import useLiveOperations from '@/hooks/useLiveOperations';
// STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · Shared Quick Register modal + Notice editor hook.
import QuickAddModal from '@/components/seller/live/QuickAddModal';
// STEP LIVE-STUDIO-PC-PRODUCT-SELECT-UI-1 · shared "전체 상품에서 등록" picker (LIVE Console 과 공통).
import AddProductModal from '@/components/seller/live/AddProductPicker';
import { useNoticeEditorAutoHydrate } from '@/hooks/useNoticeEditor';
import { applyTimerLocal, restoreTimerPaused } from '@/lib/timerLocal';
import OverlaySettingsSheet from '@/components/seller/live/OverlaySettingsSheet';
import useOverlayShowCombo from '@/hooks/useOverlayShowCombo';
// STEP MINI-CONSOLE-STUDIO-UX-1 · PC header 우측 상단 compact MiniOrder Badge.
import MiniOrderStudioBadge from '@/components/seller/live/MiniOrderStudioBadge';
import RemoteInputDiagPanel from '@/components/seller/live/RemoteInputDiagPanel';
import RemoteControlLayer from '@/components/seller/live/RemoteControlLayer';
import LivePresetPanel from '@/components/seller/live/LivePresetPanel';
import { ShutterCaptureButton, ShutterCalibrator, useShutterButtonRect } from '@/components/seller/live/ShutterCaptureButton';
import { applyTestIntroduce, buildTestLiveProducts, useTestLiveShare, TEST_SID, presetOps, pastListOf } from '@/lib/testLiveShare';
import usePastSlideSync from '@/hooks/usePastSlideSync';
import { useLivePresets, realPresetCtx, makeRealApply, realTimer, realNotice, realExternal } from '@/lib/livePresetsReal';
// REAL-CONTROL-INTEGRATION-1 · 자사라이브 공지는 문구 전용 (이미지 첨부 UI 숨김 · 기존 데이터 보존).
const NOTICE_IMAGE_UI = false;

const _fmt = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
};
const _won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

// ── Countdown overlay 3·2·1 ────────────────────────────────────────────────
const CountdownOverlay = ({ n }) => (
  <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        data-testid="live-console-countdown">
    <div key={n} className="text-white text-[120px] font-black animate-in zoom-in duration-300"
          data-testid={`live-console-countdown-${n}`}>
      {n}
    </div>
    <p className="absolute bottom-1/3 text-white/80 text-sm">방송 시작까지…</p>
  </div>
);

// STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 2026-10-12 · 테스트 모드 전용 상품 10개.
//   · 실제 상품 API 응답 (ctx.live_products) 과 **동일 shape** 로 공급 → ProductsTab
//     이 테스트/실 모드 모두 **동일 컴포넌트** 그대로 재사용. UI 이중 개발 금지.
//   · product_id prefix 'test_product_' 로 DB 상품과 명확히 구분 (실 등록 금지).
//   · ✅ WRITE API 호출 0 (ProductsTab 내 모든 mutator 가 isTestMode 분기 시 메모리만).
const _TEST_NT_KEY = 'livemoa_test_notice_timer';
const _loadTestNoticeTimer = () => {
  try { return JSON.parse(localStorage.getItem(_TEST_NT_KEY) || 'null') || {}; } catch { return {}; }
};
const _testTimerAction = (setLiveCtx) => (action, extra) =>
  setLiveCtx((c) => ({ ...(c || {}), timer: applyTimerLocal(c?.timer, action, extra) }));
const _buildTestLiveCtx = () => ({
  viewer_count: 0,
  active_product: null,
  active_product_display: null,
  active_product_qr: null,
  live_notice: _loadTestNoticeTimer().live_notice || null,
  timer: restoreTimerPaused(_loadTestNoticeTimer().timer),
  product_visibility_mode: 'all',
  live_products: buildTestLiveProducts(),
  _test: true,
});

// ── Products tab ──────────────────────────────────────────────────────────
// STEP 8-C — Mobile 소개하기 Bug Fix + Introduced-Only Visibility Restore
//   · Root Cause: 이전 코드는 `sessionId` (streaming session id = strm_xxx)
//     를 announce endpoint 로 보냈으나, 백엔드는 `schedule_id` (schedule_xxx)
//     를 기대 → 404 로 announce 실패.
//   · Fix: `scheduleId` prop 을 추가로 받아 announce/unintroduce/reset/
//     visibility-mode 4 API 모두 scheduleId 사용 (SellerLiveControl.js 와 동일).
//   · Visibility Foundation REUSE (server.py:6699~6793): PATCH visibility-mode,
//     POST unintroduce, POST reset-introduced. 신규 API/schema 0건.
// STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 2026-10-12
//   · isTestMode + externalCtx + setExternalCtx prop 추가.  테스트 모드에서는
//     외부 ctx 를 재사용(참조/mutate) 하여 테스트/실 UI 100% 공통화.
//   · 모든 mutator (setActive · applyPreset · applyPosition · toggleSizeTable ·
//     setInventoryMode · showQr/hideQr · unintroduce · changeVisMode ·
//     resetIntroduced · endIntro) 는 isTestMode 분기에서 외부 ctx 를 메모리로만
//     mutate → 실 API WRITE 없음.
// STUDIO-BLUETOOTH-PRODUCT-CONTROL-1 · 테스트 모드 소개 전환(메모리) — ProductsTab.setActive 와 리모컨 레이어 공통 REUSE.
// LIVE-TEST-STUDIO-CONSOLE-PARITY-1 · applyTestIntroduce 는 PC 보조 콘솔과 공용 (lib/testLiveShare).

// STUDIO-BLUETOOTH-PRODUCT-LIST-CLOSE-1 · 리모컨 전용 '목록 닫기' 가상 항목 (상품 아님 · 중앙 확정 시 목록만 닫기).
const RemoteCloseRow = ({ id, cursorPid, pos }) => {
  const on = cursorPid === id;
  return (
    <div className={`flex items-center justify-center gap-1.5 p-2 rounded-lg border border-dashed text-xs font-black ${on ? 'text-[#EF4444] border-[#EF4444]' : 'text-gray-400 border-black/15 dark:border-white/15'}`}
         style={on ? { outline: '3px solid #EF4444', outlineOffset: '-1px', backgroundColor: 'rgba(239,68,68,0.14)' } : undefined}
         data-remote-cursor={on ? '1' : undefined} data-testid={`live-console-remote-close-${pos}`}>
      <X size={12} /> 목록 닫기 (소개 변경 없음)
    </div>
  );
};

const ProductsTab = ({ sessionId, scheduleId, isLive, sessionWarning,
                        openNoticeEditor, liveNotice, onOpenQuickAdd,
                        draftPreset,
                        isTestMode = false, externalCtx = null, setExternalCtx = null,
                        isProductPinned = false, onProductPinToggle = null, onOpenOverlaySettings = null, pastSlider, remoteCursorPid = null, remoteMenu = false }) => {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // product_id in progress
  const [visBusy, setVisBusy] = useState(false);
  // STEP STUDIO-LIVE-PAST-PRODUCT-SLIDER-1 · 2026-10-12 · 지난 상품 슬라이드 설정 state.
  //   · 모두 클라이언트 state (판매자 미리보기 전용) · 신규 DB/API 0.
  //   · 지난 상품 판정: live_products.filter(p => p.is_introduced && p !== active)
  //     — 백엔드가 이미 영속 저장하는 is_introduced + introduced_at 100% REUSE.
  //   · 소비자 LIVE / Egress 반영은 후속 SELLER-LIVE-OVERLAY-REAL-E2E 작업.
  // MOBILE-FINAL-FIX-1 · 슬라이더 state 는 Console 로 lift (패널을 닫아도 영상 미리보기 유지).
  const { pastSliderMode, setPastSliderMode, pastSliderEffect, setPastSliderEffect, pastSliderInterval,
          setPastSliderInterval, previewPlaying, setPreviewPlaying, previewIndex, setPreviewIndex } = pastSlider;
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · canonical common LIVE ops.
  const ops = useLiveOperations(scheduleId);
  // STEP STUDIO-PRODUCT-PANEL-FLICKER-FIX-1 · 2~3초 깜빡임 수정.
  //   · 결함: setLoading(true) 를 useEffect 본문에서 매 재실행마다 호출 + sessionId 가
  //     상위 session polling 때문에 일시 undefined 가 되면 ctx 를 null 로 밀어내면서
  //     "방송 정보를 불러오는 중…" 뷰가 2~3초 간격으로 반짝거렸음.
  //   · 수정: (1) sessionId 공백은 기존 ctx 유지하고 polling 만 pause.
  //           (2) 매 polling 마다 loading 을 true 로 되돌리지 않음 — 백그라운드 갱신.
  //           (3) 최초 성공 1회만 loading=false 로 전이 · 이후 polling 실패도 loading 불변.
  //   · 미니주문 링크 복사 (MiniOrderStudioBadge) 는 ctx/items 를 건드리지 않음 — 복사
  //     버튼 눌러도 상품 목록은 계속 표시.
  useEffect(() => {
    // STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 테스트 모드는 외부 ctx 를 그대로 사용.
    //   · fetch 자체를 skip (실 세션이 없으므로 404 noise 를 피함).
    //   · 외부 ctx 가 들어오면 즉시 loading false 로 전환 → 로딩 뷰 깜빡임 방지.
    if (isTestMode) {
      if (externalCtx) setCtx(externalCtx);
      setLoading(false);
      return undefined;
    }
    if (!sessionId) return undefined;  // keep last-known ctx visible.
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/streaming/sessions/${sessionId}/live-context`);
        if (alive) {
          // 응답 body 가 null 이면 기존 ctx 를 지우지 않음 (soft merge).
          if (data) setCtx(data);
          setLoading(false);
        }
      } catch {
        // soft — 네트워크 실패는 기존 ctx/loading 상태 유지. 다음 tick 재시도.
      }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [sessionId, isTestMode, externalCtx]);

  // STUDIO-BLUETOOTH-PRODUCT-CONTROL-1 · 리모컨 목록 선택 위치를 화면에 보이게 (소개 상품 변경 없음).
  // SCROLL-FIX-1B · 표시 중인 모바일 시트 안 행만 대상 · 행에서 시트까지의 실제 스크롤 영역(목록 → 시트 본문) scrollTop 만 조정 ·
  // window · scrollIntoView 미사용 · 렌더·시트 열림 애니메이션 뒤 재계산.
  useEffect(() => {
    if (!remoteCursorPid) { return undefined; }
    const follow = () => {
      const root = document.querySelector('[data-testid="live-console-mobile-sheet"]');
      const el = root && root.querySelector('[data-remote-cursor="1"]');
      if (!el || !el.offsetHeight) return;
      for (let box = el.parentElement; box && box !== root; box = box.parentElement) {
        const oy = getComputedStyle(box).overflowY;
        if ((oy !== 'auto' && oy !== 'scroll') || box.scrollHeight <= box.clientHeight + 1) continue;
        const er = el.getBoundingClientRect();
        const br = box.getBoundingClientRect();
        if (er.top < br.top + 4) box.scrollTop -= br.top - er.top + 8;
        else if (er.bottom > br.bottom - 4) box.scrollTop += er.bottom - br.bottom + 8;
      }
    };
    const raf = requestAnimationFrame(follow);
    const t1 = setTimeout(follow, 160);
    const t2 = setTimeout(follow, 420);
    return () => { cancelAnimationFrame(raf); clearTimeout(t1); clearTimeout(t2); };
  }, [remoteCursorPid]);

  // STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 테스트 모드 전용 메모리 mutator.
  //   · 외부 ctx (parent liveCtx) 를 직접 setter 로 업데이트 →
  //     fetch effect 가 externalCtx 변경을 감지하고 local ctx 도 sync.
  //   · 실제 API 호출은 전부 skip (DB 오염 0 · 서버 WRITE 0).
  const _testMutate = (fn) => {
    if (!setExternalCtx) return;
    setExternalCtx((prev) => fn(prev || { live_products: [] }));
  };

  // STEP LIVE-STUDIO-PRESET-UNIFIED-1 · P1 FIX · 상품 변경 시 draftPreset 유지.
  //   · ops.announce(pid) 만 호출하면 서버가 canonicalize_preset(None) → 'default' 로
  //     fallback 하여 선택된 options/price/mini 가 사라진다 (사용자 보고 P1 증상).
  //   · 이번 STEP: draftPreset 이 있으면 함께 전달 → 서버 build_display_state 가
  //     해당 preset 으로 active_product_display 재생성 → 상품 변경 후에도 유지.
  //   · 기존 /announce endpoint REUSE · 신규 API 0 · overlay engine 0 touch.
  const setActive = async (pid) => {
    if (!isLive || busy) return;
    if (isTestMode) {
      // STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 테스트 모드 메모리 전환.
      _testMutate((c) => applyTestIntroduce(c, pid, draftPreset));
      toast.success('🧪 테스트 소개 전환 (가상)');
      // 연타 보호 — 실제 LIVE 의 busy 가드와 동일 효과 (테스트 전환은 즉시라 짧게 유지).
      setBusy(pid);
      setTimeout(() => setBusy(null), 600);
      return;
    }
    if (!scheduleId) { toast.error('편성표 방송이 아니어서 소개 불가 · 편성표에서 방송 시작해주세요.'); return; }
    setBusy(pid);
    try {
      const _preset = (draftPreset && ['default','full','price','options','mini'].includes(draftPreset))
        ? draftPreset : undefined;
      await ops.announce(pid, _preset);
      toast.success('현재 소개상품을 변경했어요.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '변경 실패');
    } finally { setBusy(null); }
  };

  // STEP OVERLAY-2C-UI-COMPLETE · Seller Console Overlay Preset control.
  // 동일 /announce endpoint REUSE (Studio 와 동기화 LOCK · 전용 Console endpoint 금지).
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · ops.applyPreset / hideOverlay REUSE.
  const applyPreset = async (preset) => {
    if (!isLive || busy) return;
    if (isTestMode) {
      _testMutate((c) => {
        const pid = c?.active_product?.product_id;
        if (!pid && preset !== 'hidden') { return c; }
        if (preset === 'hidden') {
          return {
            ...c,
            active_product_display: { ...(c.active_product_display || {}), preset: c?.active_product_display?.preset || 'default', mode: 'hidden' },
          };
        }
        return {
          ...c,
          active_product_display: { ...(c.active_product_display || {}), preset, mode: 'visible' },
        };
      });
      return;
    }
    if (!scheduleId) { toast.error('편성표 방송이 아닙니다.'); return; }
    const pid = ctx?.active_product?.product_id;
    if (!pid && preset !== 'hidden') { toast.error('먼저 소개중 상품을 선택해주세요.'); return; }
    setBusy('preset');
    try {
      if (pid) {
        await ops.applyPreset(pid, preset);
      } else {
        // 활성 상품 없이 'hidden' 호출 → 소개 자체 해제.
        await ops.clearAnnounce();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Overlay 조작 실패'); }
    finally { setBusy(null); }
  };
  const currentPreset = (ctx?.active_product_display?.preset) || 'default';
  const currentPosition = (ctx?.active_product_display?.position) || 'bottom';
  const overlayIsHidden = ctx?.active_product_display?.mode === 'hidden';
  // STEP OVERLAY-SIZE-TABLE-1 · 상품 상세 사이즈표 Overlay 토글.
  //   · 기존 /announce endpoint + build_display_state 를 REUSE (신규 API 금지).
  //   · 활성 상품에 size_table 데이터가 있을 때만 활성화.
  //   · size_table_visible 는 display override — 기존 preset 은 보존.
  const sizeTableAvailable = !!(ctx?.active_product?.size_table
      && ((ctx.active_product.size_table.columns || []).length
          || (ctx.active_product.size_table.rows || []).length
          || (ctx.active_product.size_table.guide_text || '').trim()
          || (ctx.active_product.size_table.material || '').trim()
          || (ctx.active_product.size_table.etc || '').trim()));
  const sizeTableVisible = !!ctx?.active_product_display?.size_table_visible;
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · ops.toggleSizeTable REUSE.
  const toggleSizeTable = async () => {
    if (!isLive || busy) return;
    if (isTestMode) {
      _testMutate((c) => ({
        ...c,
        active_product_display: { ...(c.active_product_display || {}), size_table_visible: !c?.active_product_display?.size_table_visible },
      }));
      return;
    }
    if (!scheduleId) { toast.error('편성표 방송이 아닙니다.'); return; }
    const pid = ctx?.active_product?.product_id;
    if (!pid) { toast.error('먼저 소개중 상품을 선택해주세요.'); return; }
    setBusy('size_table');
    try {
      await ops.toggleSizeTable(pid, currentPreset, !sizeTableVisible);
      toast.success(sizeTableVisible ? '상품설명 숨김' : '상품설명 표시');
    } catch (e) { toast.error(e?.response?.data?.detail || '상품설명 조작 실패'); }
    finally { setBusy(null); }
  };

  // STEP OVERLAY-UX-FINAL-2B · Overlay 위치 (상단/중앙/하단).
  //   · 기존 /announce endpoint REUSE (신규 API 금지).
  //   · 현재 preset 을 함께 보내 position 변경이 preset 을 "default" 로 되돌리지 않게 한다.
  //   · active_product_display.position 1곳만 SoT (신규 state 금지).
  //   · EgressTemplate + Seller Preview 가 동일 좌표를 사용하므로 즉시 반영된다.
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · ops.setPosition REUSE.
  const applyPosition = async (pos) => {
    if (!isLive || busy) return;
    if (isTestMode) {
      _testMutate((c) => ({
        ...c,
        active_product_display: { ...(c.active_product_display || {}), position: pos },
      }));
      return;
    }
    if (!scheduleId) { toast.error('편성표 방송이 아닙니다.'); return; }
    const pid = ctx?.active_product?.product_id;
    if (!pid) { toast.error('먼저 소개중 상품을 선택해주세요.'); return; }
    setBusy('position');
    try {
      await ops.setPosition(pid, currentPreset, pos);
      toast.success(`위치 · ${pos === 'top' ? '상단' : pos === 'middle' ? '중앙' : '하단'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || '위치 변경 실패'); }
    finally { setBusy(null); }
  };

  const unintroduce = async (pid) => {
    if (isTestMode) {
      _testMutate((c) => ({
        ...c,
        live_products: (c.live_products || []).map((p) =>
          p.product_id === pid ? { ...p, is_introduced: false } : p),
        active_product: c?.active_product?.product_id === pid ? null : c?.active_product,
      }));
      return;
    }
    if (!scheduleId || busy) return;
    setBusy(pid);
    try {
      await api.post(`/seller/broadcasts/${scheduleId}/live-products/${pid}/unintroduce`);
      toast.success('다시 가리기 완료 · 곧 진행 상태로 복귀했어요.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '다시 가리기 실패');
    } finally { setBusy(null); }
  };

  const changeVisMode = async (mode) => {
    if (isTestMode) {
      _testMutate((c) => ({ ...c, product_visibility_mode: mode }));
      return;
    }
    if (!scheduleId || visBusy) return;
    if (mode === (ctx?.product_visibility_mode || 'all')) return;
    setVisBusy(true);
    try {
      await api.patch(`/seller/broadcasts/${scheduleId}/visibility-mode`, { mode });
      toast.success(mode === 'all' ? '전체 상품 바로 공개 모드' : '소개한 상품만 공개 모드');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '변경 실패');
    } finally { setVisBusy(false); }
  };

  const resetIntroduced = async () => {
    if (isTestMode) {
      _testMutate((c) => ({
        ...c,
        active_product: null,
        active_product_display: null,
        live_products: (c.live_products || []).map((p) => ({ ...p, is_introduced: false })),
      }));
      toast.success('🧪 테스트 소개상태 초기화');
      return;
    }
    if (!scheduleId || visBusy) return;
    if (!window.confirm('현재 방송의 상품 소개 상태를 모두 초기화하시겠습니까?\n상품 순서와 주문 데이터는 유지됩니다.')) return;
    setVisBusy(true);
    try {
      const r = await api.post(`/seller/broadcasts/${scheduleId}/live-products/reset-introduced`);
      toast.success(`소개상태 초기화 완료 (${r.data?.reset_count ?? 0}건)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '초기화 실패');
    } finally { setVisBusy(false); }
  };

  // STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 소개 종료 — 활성 상품을 완전 해제.
  //   · ops.clearAnnounce() 를 REUSE (신규 API 금지). 테스트 모드는 메모리만 clear.
  const endIntro = async () => {
    if (isTestMode) {
      _testMutate((c) => ({
        ...c,
        active_product: null,
        active_product_display: null,
        active_product_qr: null,
      }));
      toast.success('🧪 테스트 소개 종료');
      return;
    }
    if (!scheduleId || busy) return;
    setBusy('end-intro');
    try {
      await ops.clearAnnounce();
      toast.success('소개 종료');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '소개 종료 실패');
    } finally { setBusy(null); }
  };

  // STEP STUDIO-LIVE-PAST-PRODUCT-SLIDER-1 · 미리보기 interval — Console 로 이동 (MOBILE-FINAL-FIX-1).

  const active = ctx?.active_product;
  const items = ctx?.live_products || [];
  const visMode = ctx?.product_visibility_mode || 'all';
  const next = active
    ? items[items.findIndex((p) => p.product_id === active.product_id) + 1]
    : items[0];
  // STUDIO-BLUETOOTH-PRODUCT-CONTROL-1 · 콘솔 '이전 상품' 대응 — next 와 동일 규칙 · setActive REUSE.
  const prev = active
    ? items[items.findIndex((p) => p.product_id === active.product_id) - 1]
    : null;

  // STEP 8-I-4C · Empty state 오진 방지 · session mapping mismatch 는 명확 안내.
  const isMismatch = sessionWarning === 'active_session_schedule_mismatch';
  // STEP STUDIO-PRODUCT-PANEL-FLICKER-FIX-1 · 최초 데이터 수신 전에만 로딩 뷰 노출.
  //   · "불러오는 중" 은 ctx 가 아직 한 번도 응답되지 않은 '진짜 최초 로딩' 에서만.
  //   · 백그라운드 polling (ctx 이미 존재) 중에는 절대 로딩뷰로 되돌아가지 않음.
  const neverLoaded = !ctx;
  const isInitialLoading = neverLoaded && (loading || !sessionId || (!scheduleId && !ctx));
  if (isMismatch) {
    return (
      <div className="p-4 space-y-2" data-testid="live-console-products-mismatch">
        <div className="rounded-xl border-2 border-amber-500/60 bg-amber-500/10 p-3 text-xs leading-relaxed">
          <p className="font-black text-amber-700 dark:text-amber-300 mb-1">⚠ 이전 방송이 아직 라이브 상태예요</p>
          <p className="text-amber-800 dark:text-amber-200">
            다른 방송이 <b>라이브 중</b>이라 이 화면 상품이 표시되지 않아요. 이전 방송을 먼저 정리한 뒤 이 방송의 상품을 로드하세요.
          </p>
        </div>
      </div>
    );
  }
  if (isInitialLoading && items.length === 0) {
    return (
      <div className="p-4 text-center text-xs text-gray-500" data-testid="live-console-products-loading">
        방송 정보를 불러오는 중…
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col gap-3" data-testid="live-console-tab-products">
      {/* STEP STUDIO-LIVE-PAST-PRODUCT-SLIDER-1 (2026-10-12) · 라이브 상품 공개 방식 → details.
          STEP STUDIO-MOBILE-NOTICE-TIMER-1 · 최상단 (order-0). */}
      {scheduleId && (
        <details className="order-2 rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
                 data-testid="live-console-visibility-details">
          <summary className="list-none cursor-pointer select-none px-3 py-2.5 flex items-center justify-between gap-2 text-xs font-black text-black dark:text-white"
                   data-testid="live-console-visibility-summary">
            <span>라이브 상품 공개 방식</span>
            <span className="text-[10px] font-bold text-gray-500">
              {visMode === 'introduced_only' ? '소개한 상품만' : '전체 공개'}
            </span>
          </summary>
          <div className="px-2.5 pb-2.5 pt-1 space-y-2"
                data-testid="live-console-visibility-mode">
          <div className="inline-flex rounded-full border border-black/10 dark:border-white/15 overflow-hidden">
            <button type="button" onClick={() => changeVisMode('all')} disabled={visBusy}
                    className={`px-2.5 py-1 text-[10px] font-black ${
                      visMode === 'all'
                        ? 'bg-[#FF3B30] text-white'
                        : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'
                    }`}
                    data-testid="live-console-vis-mode-all">
              전체 공개
            </button>
            <button type="button" onClick={() => changeVisMode('introduced_only')} disabled={visBusy}
                    className={`px-2.5 py-1 text-[10px] font-black ${
                      visMode === 'introduced_only'
                        ? 'bg-[#FF3B30] text-white'
                        : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'
                    }`}
                    data-testid="live-console-vis-mode-introduced">
              소개한 상품만
            </button>
          </div>
          {visMode === 'introduced_only' && (
            <p className="text-[10px] text-gray-500 leading-snug">
              소개 전 상품은 소비자 화면에서 숨겨져요. 소개했다가 다시 감출 수 있어요.
            </p>
          )}
          <button type="button" onClick={resetIntroduced} disabled={visBusy || items.length === 0}
                  className="text-[10px] font-bold text-gray-500 hover:text-[#FF3B30] disabled:opacity-40 underline underline-offset-2"
                  data-testid="live-console-reset-introduced">
            소개상태 초기화
          </button>
          </div>
        </details>
      )}

      {/* STEP STUDIO-LIVE-PAST-PRODUCT-SLIDER-1 · 지난 상품 슬라이드 설정 (판매자 전용 미리보기).
          · 지난 상품 = is_introduced=true & !active · introduced_at ASC 정렬 · 중복 제거.
          · 설정값 신규 DB 저장 X · 미리보기만 UI 반영.  실 송출/Egress 미반영 (후속 STEP). */}
      {scheduleId && (() => {
        const activeId = active?.product_id;
        const past = (items || [])
          .filter((p) => p.is_introduced && p.product_id !== activeId)
          .slice()
          .sort((a, b) => {
            const ta = a.introduced_at ? new Date(a.introduced_at).getTime() : (a.slot_number || 0);
            const tb = b.introduced_at ? new Date(b.introduced_at).getTime() : (b.slot_number || 0);
            return ta - tb;
          });
        return (
          <details className="order-1 rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
                   data-testid="live-console-past-slider-details">
            <summary className="list-none cursor-pointer select-none px-3 py-2.5 flex items-center justify-between gap-2 text-xs font-black text-black dark:text-white"
                     data-testid="live-console-past-slider-summary">
              <span className="inline-flex items-center gap-2">
                <Layers size={14} className="text-[#FF3B30]" /> 지난 상품 슬라이드
              </span>
              <span className="text-[10px] font-bold text-gray-500">
                {pastSliderMode === 'off' ? '사용 안 함' : pastSliderMode === 'once' ? '한 번 보여주기' : '계속 보여주기'} · {past.length}개
              </span>
            </summary>
            <div className="px-2.5 pb-2.5 pt-1 space-y-2.5" data-testid="live-console-past-slider-body">
              <div className="grid grid-cols-3 gap-1" data-testid="live-console-past-slider-mode">
                {[['off', '사용 안 함'], ['once', '한 번 보여주기'], ['loop', '계속 보여주기']].map(([k, label]) => (
                  <button key={k} type="button"
                          onClick={() => { setPastSliderMode(k); if (k === 'off') setPreviewPlaying(false); }}
                          className={`min-h-[32px] px-2 rounded-lg text-[10px] font-black transition ${
                            pastSliderMode === k
                              ? 'bg-[#FF3B30] text-white'
                              : 'bg-white dark:bg-white/5 border border-black/10 dark:border-white/15 text-gray-700 dark:text-gray-200 hover:border-[#FF3B30]'
                          }`}
                          data-testid={`live-console-past-slider-mode-${k}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-1" data-testid="live-console-past-slider-effect">
                {[['slide', '슬라이드'], ['fade', '페이드']].map(([k, label]) => (
                  <button key={k} type="button"
                          onClick={() => setPastSliderEffect(k)}
                          className={`min-h-[30px] rounded-lg text-[10px] font-black transition ${
                            pastSliderEffect === k
                              ? 'bg-[#FF3B30]/15 text-[#FF3B30] border-2 border-[#FF3B30]'
                              : 'bg-white dark:bg-white/5 border border-black/10 dark:border-white/15 text-gray-600 dark:text-gray-300 hover:border-[#FF3B30]'
                          }`}
                          data-testid={`live-console-past-slider-effect-${k}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="inline-flex rounded-full border border-black/10 dark:border-white/15 overflow-hidden"
                   data-testid="live-console-past-slider-interval">
                {[5, 8, 10].map((sec) => (
                  <button key={sec} type="button"
                          onClick={() => setPastSliderInterval(sec)}
                          className={`px-2.5 py-1 text-[10px] font-black ${
                            pastSliderInterval === sec
                              ? 'bg-[#FF3B30] text-white'
                              : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10'
                          }`}
                          data-testid={`live-console-past-slider-interval-${sec}`}>
                    {sec}초
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button type="button"
                        onClick={() => {
                          if (pastSliderMode === 'off' || past.length === 0) return;
                          setPreviewIndex(0);
                          setPreviewPlaying(true);
                        }}
                        disabled={pastSliderMode === 'off' || past.length === 0 || previewPlaying}
                        className="flex-1 min-h-[36px] rounded-full bg-[#FF3B30] text-white text-xs font-black hover:bg-[#D32F2F] disabled:opacity-40"
                        data-testid="live-console-past-slider-preview-start">
                  미리보기
                </button>
                <button type="button"
                        onClick={() => setPreviewPlaying(false)}
                        disabled={!previewPlaying}
                        className="min-h-[36px] px-3 rounded-full border-2 border-[#FF3B30] text-[#FF3B30] text-xs font-black hover:bg-[#FF3B30]/10 disabled:opacity-40"
                        data-testid="live-console-past-slider-preview-stop">
                  중지
                </button>
              </div>
              {past.length === 0 && (
                <p className="text-[10px] text-gray-500 leading-snug" data-testid="live-console-past-slider-empty">
                  아직 소개한 상품이 없어요. 상품을 1회 이상 소개하면 지난 상품 슬라이드에 표시돼요.
                </p>
              )}
              {previewPlaying && past.length > 0 && (() => {
                const p = past[previewIndex % past.length];
                return (
                  <div className={`rounded-xl border border-[#FF3B30]/30 bg-black/[0.02] dark:bg-white/[0.03] p-2 ${pastSliderEffect === 'fade' ? 'animate-in fade-in duration-500' : 'animate-in slide-in-from-right duration-300'}`}
                       data-testid="live-console-past-slider-preview-card">
                    <p className="text-[9px] font-black text-gray-500 mb-1 inline-flex items-center gap-1">
                      <span className="px-1 py-[1px] rounded bg-gray-600 text-white text-[8px]">지난 상품</span>
                      {previewIndex + 1}/{past.length} · {pastSliderInterval}초
                    </p>
                    <div className="flex items-center gap-2">
                      <SafeImg src={p?.image} kind="product" alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] text-gray-500">Slot {p?.slot_number}</p>
                        <p className="text-[11px] font-black text-black dark:text-white truncate">{p?.name}</p>
                        <p className="text-[11px] font-black text-[#FF3B30]">{_won(p?.sale_price)}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <p className="text-[10px] text-gray-500 leading-snug" data-testid="live-console-past-slider-note">
                미리보기는 판매자 전용입니다. 실제 소비자 LIVE·외부 송출 표시는 후속 작업에서 반영됩니다.
              </p>
            </div>
          </details>
        );
      })()}
      {/* 미리보기 interval 효과 — slider 가 재생 중일 때만 index 를 회전시킴. */}
      {(() => { return null; })()}

      {active ? (
        <div className="rounded-xl border-2 border-[#FF3B30] p-3 bg-[#FF3B30]/[0.03] order-0" data-testid="live-console-active-product">
          <p className="text-[10px] font-black text-[#FF3B30] inline-flex items-center gap-1 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse" /> 지금 소개중
          </p>
          <div className="flex items-center gap-3">
            <SafeImg src={active.image} kind="product" alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500">Slot {active.slot_number}</p>
              <p className="text-sm font-black truncate text-black dark:text-white">{active.name}</p>
              <p className="text-sm font-black text-[#FF3B30]">{_won(active.sale_price)}</p>
            </div>
          </div>
          {prev && prev.product_id !== active?.product_id && (
            <button type="button" onClick={() => setActive(prev.product_id)}
                    disabled={!isLive || !!busy || (!scheduleId && !isTestMode)}
                    className="mt-2.5 w-full min-h-[44px] px-3 rounded-full border-2 border-black/15 dark:border-white/20 text-black dark:text-white text-xs font-black inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="live-console-prev-goto">
              <span className="shrink-0 inline-flex items-center"><ChevronLeft size={14} /> 이전 소개</span>
              <span className="truncate flex-1 text-right" data-testid="live-console-prev-name">{prev.name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-black/70 dark:bg-white/20 text-white shrink-0">PREV</span>
            </button>
          )}
          {/* STUDIO-OVERLAY-REMAINING-FIX-1 · 기존 NEXT 블록 복구 (next + setActive REUSE · 마지막 상품이면 미표시). */}
          {next && next.product_id !== active?.product_id && (
            <button type="button" onClick={() => setActive(next.product_id)}
                    disabled={!isLive || !!busy || (!scheduleId && !isTestMode)}
                    className="mt-2.5 w-full min-h-[44px] px-3 rounded-full bg-black dark:bg-white text-white dark:text-black text-xs font-black inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="live-console-next-goto">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FF3B30] text-white shrink-0">NEXT</span>
              <span className="truncate flex-1 text-left" data-testid="live-console-next-name">{next.name}</span>
              <span className="shrink-0 inline-flex items-center">다음 소개 <ChevronRight size={14} /></span>
            </button>
          )}
          {/* STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 활성 상품 액션: 상품 변경 / 소개 종료
              STEP STUDIO-MOBILE-PANEL-VISIBILITY-1 · 상품 고정 toggle (판매자 전용). */}
          <div className="mt-2.5 flex gap-2" data-testid="live-console-active-actions">
            {typeof onProductPinToggle === 'function' && (
              <button type="button"
                      onClick={onProductPinToggle}
                      disabled={!!busy}
                      className={`min-h-[36px] px-3 rounded-full text-xs font-black border-2 inline-flex items-center gap-1 disabled:opacity-40 ${
                        isProductPinned
                          ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                          : 'border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10'
                      }`}
                      aria-label={isProductPinned ? '상품 고정 해제' : '상품 고정'}
                      data-testid="live-console-product-pin-toggle">
                {isProductPinned ? <PinOff size={12} /> : <Pin size={12} />} 고정
              </button>
            )}
            <button type="button"
                    onClick={() => {
                      // 상품 목록으로 스크롤 (아래 ② 섹션). 활성 상품 변경은 목록에서 선택.
                      try {
                        const el = document.querySelector('[data-testid="live-console-product-list"]');
                        if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      } catch {}
                    }}
                    disabled={!!busy}
                    className="flex-1 min-h-[36px] rounded-full border-2 border-[#FF3B30] text-[#FF3B30] text-xs font-black hover:bg-[#FF3B30]/10 disabled:opacity-40"
                    data-testid="live-console-active-change">
              상품 변경
            </button>
            <button type="button"
                    onClick={endIntro}
                    disabled={!!busy}
                    className="flex-1 min-h-[36px] rounded-full bg-[#FF3B30] text-white text-xs font-black hover:bg-[#D32F2F] disabled:opacity-40"
                    data-testid="live-console-active-end">
              소개 종료
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-black/10 dark:border-white/10 p-4 text-center text-xs text-gray-500 order-0"
              data-testid="live-console-no-active">
          아직 소개 중인 상품이 없어요.
        </div>
      )}

      {/* STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 타이머는 우측 rail 전용. 상품 패널 inline 제거. */}

      {/* STEP OVERLAY-REAL-UI-CHECK · Overlay Preset / Inventory / QR control (Console).
          STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · <details> 기본 접힘.  필요할 때만 펼침.
          STEP STUDIO-MOBILE-CONTROLS-UI-1 · 접근성 향상 — `open` 기본값 TRUE 로 전환.
          기본 펼침이지만 사용자가 접을 수 있어 화면 공간 조절 가능. */}
      {isLive && scheduleId && (
        <details open className="order-4 rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02]"
                 data-testid="live-console-overlay-details">
          <summary className="list-none cursor-pointer select-none px-3 py-2.5 flex items-center justify-between gap-2 text-xs font-black text-black dark:text-white"
                   data-testid="live-console-overlay-details-summary">
            <span className="inline-flex items-center gap-2">
              <Package size={14} className="text-[#FF3B30]" />
              Overlay 설정
            </span>
            <span className="text-[10px] font-bold text-gray-500">접힘 · 필요할 때 펼침</span>
          </summary>
          <div className="px-2.5 pb-2.5 pt-1 space-y-2.5 flex flex-col"
                data-testid="live-console-overlay-row"
                data-disabled={!active?.product_id ? 'true' : 'false'}>
          {onOpenOverlaySettings && (
            <button type="button" onClick={onOpenOverlaySettings}
                    className="w-full min-h-[44px] px-4 rounded-xl text-sm font-black bg-black text-white dark:bg-white dark:text-black inline-flex items-center justify-center gap-2"
                    data-testid="live-console-overlay-open-unified">
              <Layers size={15} /> 화면 프리셋 · Overlay 설정 열기
            </button>
          )}
          {/* STEP OVERLAY-UX-FINAL-2B · prominent "Overlay 내리기" — 가장 빠르게
              잘못 띄운 상품을 내리기 위한 1차 액션.  Bottom Sheet close 와는
              완전히 별개 (hidden preset 송신 → Egress Overlay unmount). */}
          <button type="button"
                  onClick={() => applyPreset('hidden')}
                  disabled={!!busy || !active?.product_id || overlayIsHidden}
                  className={`w-full min-h-[44px] px-4 rounded-xl text-sm font-black border-2 transition inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${
                    overlayIsHidden
                      ? 'border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/5 text-gray-500'
                      : 'border-[#FF3B30] bg-white dark:bg-[#141414] text-[#FF3B30] hover:bg-[#FF3B30]/10 shadow-sm'
                  }`}
                  data-testid="live-console-overlay-hide-primary"
                  aria-label="상품 Overlay 내리기">
            <EyeOff size={16} />
            {overlayIsHidden ? 'Overlay 내려짐' : 'Overlay 내리기'}
          </button>

          {/* Preset */}
          <div data-testid="live-console-overlay-preset-row"
               data-overlay-order="3"
               style={{ order: 3 }}>
          <p className="text-[10px] text-gray-500 leading-snug mb-1">
            {/* STEP OVERLAY-PRODUCT-UI-FINAL · 판매자-facing 간소화 (지시 §4/§12).
                백엔드 preset 8종 LOCK · UI 는 '표시 형태' 3개 + '방송 중 동작' 2개 로 분리. */}
            예: 풀 · 이미지 · 옵션
            {!active?.product_id && (<span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>)}
          </p>
          <div className="flex items-center gap-1 flex-wrap"
               data-testid="live-console-overlay-mode-row">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">표시 형태</span>
            {[
              ['full', '풀'],
              ['default', '이미지'],
              ['options', '옵션'],
            ].map(([pk, label]) => {
              const cur = ctx?.active_product_display?.preset || 'default';
              const activeBtn = cur === pk && ctx?.active_product_display?.mode !== 'hidden';
              return (
                <button key={pk} onClick={() => applyPreset(pk)}
                        disabled={!!busy || !active?.product_id}
                        className={`min-h-[36px] px-3 rounded-full text-[11px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                          activeBtn ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                                     : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'
                        }`}
                        data-testid={`live-console-overlay-preset-${pk}`}>{label}</button>
              );
            })}
            <span className="mx-1 text-gray-300">·</span>
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">방송 중 동작</span>
            {[
              ['emphasized', '강조'],
              ['mini', '미니'],
            ].map(([pk, label]) => {
              const cur = ctx?.active_product_display?.preset || 'default';
              const activeBtn = cur === pk && ctx?.active_product_display?.mode !== 'hidden';
              return (
                <button key={pk} onClick={() => applyPreset(pk)}
                        disabled={!!busy || !active?.product_id}
                        className={`min-h-[36px] px-3 rounded-full text-[11px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                          activeBtn ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                                     : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'
                        }`}
                        data-testid={`live-console-overlay-preset-${pk}`}>{label}</button>
              );
            })}
          </div>
          </div>
          {/* STEP OVERLAY-UX-FINAL-2B · Position (상단/중앙/하단).
              기존 active_product_display.position SoT REUSE · 신규 state/API/collection 금지.
              하단 = Safe Area 내부 bottom (화면 최하단 아님) · Seller Preview 즉시 반영. */}
          <div data-testid="live-console-overlay-position-row"
               data-overlay-order="1"
               style={{ order: 1 }}>
          <p className="text-[10px] text-gray-500 leading-snug mb-1">
            위치: 영상 내 Safe Area 안에서 상품 Overlay가 뜨는 위치
            {!active?.product_id && (<span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>)}
          </p>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">위치</span>
            {[['top', '상단'], ['middle', '중앙'], ['bottom', '하단']].map(([pk, label]) => {
              const activeBtn = currentPosition === pk;
              return (
                <button key={pk} type="button"
                        onClick={() => applyPosition(pk)}
                        disabled={!!busy || !active?.product_id}
                        className={`min-h-[36px] px-3 rounded-full text-[11px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                          activeBtn ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                                     : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'
                        }`}
                        data-testid={`live-console-overlay-position-${pk}`}>{label}</button>
              );
            })}
          </div>
          </div>
          {/* STEP OVERLAY-SIZE-TABLE-1 · 사이즈표 Overlay 토글.
              · active_product.size_table 이 있을 때만 활성화 · 없으면 disabled.
              · 기존 `사이즈` preset (SKU S/M/L) 과 혼동되지 않도록 별도 섹션. */}
          <div data-testid="live-console-overlay-size-table-row"
               data-overlay-order="4"
               style={{ order: 4 }}>
          <p className="text-[10px] text-gray-500 leading-snug mb-1">
            상품설명 (사이즈·소재·기타)
            {!sizeTableAvailable && active?.product_id && (
              <span className="ml-1 text-gray-400">· 상품 수정에서 상품설명을 등록하면 사용 가능</span>
            )}
            {!active?.product_id && (<span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>)}
          </p>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">상품설명</span>
            <button type="button"
                    onClick={toggleSizeTable}
                    disabled={!!busy || !active?.product_id || !sizeTableAvailable || overlayIsHidden}
                    className={`min-h-[36px] px-3 rounded-full text-[11px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                      sizeTableVisible
                        ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                        : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'
                    }`}
                    data-testid="live-console-overlay-size-table-toggle">
              {sizeTableVisible ? '상품설명 내리기' : '상품설명 표시'}
            </button>
          </div>
          </div>
          {/* Inventory mode */}
          <div data-testid="live-console-overlay-inventory-row"
               data-overlay-order="5"
               style={{ order: 5 }}>
          <p className="text-[10px] text-gray-500 leading-snug mb-1">
            예: 남은 수량 · 품절 임박
            {!active?.product_id && (<span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>)}
          </p>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">재고</span>
            {[['hidden', '숨김'], ['exact', '수량'], ['status', '상태']].map(([mk, label]) => {
              const cur = ctx?.active_product_display?.inventory_mode || 'hidden';
              const activeBtn = cur === mk;
              return (
                <button key={mk} disabled={!!busy || !active?.product_id}
                        onClick={async () => {
                          if (isTestMode) {
                            _testMutate((c) => ({
                              ...c,
                              active_product_display: { ...(c.active_product_display || {}), inventory_mode: mk },
                            }));
                            return;
                          }
                          // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · ops.setInventoryMode REUSE.
                          setBusy('inv');
                          try {
                            await ops.setInventoryMode(active.product_id, mk);
                            toast.success(`재고 표시 · ${label}`);
                          } catch (e) { toast.error(e?.response?.data?.detail || '설정 실패'); }
                          finally { setBusy(null); }
                        }}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border disabled:opacity-40 disabled:cursor-not-allowed ${
                          activeBtn ? 'bg-black text-white border-black dark:bg-white dark:text-black dark:border-white'
                                     : 'border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10'
                        }`}
                        data-testid={`live-console-overlay-inventory-${mk}`}>{label}</button>
              );
            })}
            {(() => {
              const ap = ctx?.active_product;
              if (!ap) return null;
              const status = ap.stock_status;
              const avail = ap.available_stock;
              let txt = status === 'unlimited' ? '무제한'
                        : status === 'unknown' ? '-'
                        : Number.isFinite(Number(avail)) ? `${Number(avail).toLocaleString('ko-KR')}개` : '-';
              return (
                <span className="ml-auto text-[10px] font-bold text-gray-500"
                      data-testid="live-console-overlay-inventory-current">
                  {txt}
                </span>
              );
            })()}
          </div>
          </div>
          {/* QR */}
          <div data-testid="live-console-overlay-qr-row"
               data-overlay-order="2"
               style={{ order: 2 }}>
          {/* STEP OVERLAY-CONTROL-UX-1 · QR 설명 (지시 §C) — 임의 URL 이 아니라
              현재 소개 상품 상세 페이지 이동 수단임을 명시. */}
          <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-snug mb-1"
             data-testid="live-console-overlay-qr-description">
            방송 화면에 상품 QR을 표시합니다. 시청자가 스캔하면 해당 상품 페이지로 이동합니다.
          </p>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-black tracking-widest uppercase text-gray-500 mr-1">QR</span>
          <p className="text-[10px] text-gray-500 leading-snug mb-1 w-full"
             data-testid="live-console-overlay-qr-helper">
            예: PC·TV 시청자 스캔 이동
            {!active?.product_id && (<span className="ml-1 text-gray-400">· 상품 소개 후 사용 가능</span>)}
          </p>
            <button disabled={!!busy || !active?.product_id}
                    onClick={async () => {
                      if (isTestMode) {
                        _testMutate((c) => ({
                          ...c,
                          active_product_qr: { visible: true, duration_seconds: 10 },
                        }));
                        return;
                      }
                      // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · ops.showQr REUSE.
                      setBusy('qr');
                      try {
                        await ops.showQr(active.product_id, 10);
                        toast.success('QR 표시 (10초)');
                      } catch (e) { toast.error(e?.response?.data?.detail || 'QR 표시 실패'); }
                      finally { setBusy(null); }
                    }}
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="live-console-overlay-qr-show">QR 표시</button>
            <button disabled={!!busy || !active?.product_id}
                    onClick={async () => {
                      if (isTestMode) {
                        _testMutate((c) => ({
                          ...c,
                          active_product_qr: { visible: false },
                        }));
                        return;
                      }
                      // STEP LIVE-STUDIO-CONSOLE-SPLIT-2 · ops.hideQr REUSE.
                      setBusy('qr');
                      try {
                        await ops.hideQr(active.product_id);
                        toast.success('QR 내림');
                      } catch (e) { toast.error(e?.response?.data?.detail || 'QR 내리기 실패'); }
                      finally { setBusy(null); }
                    }}
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="live-console-overlay-qr-hide">QR 내리기</button>
            {ctx?.active_product_qr?.visible && (
              <span className="text-[10px] text-gray-500 ml-auto"
                    data-testid="live-console-overlay-qr-active">
                표시 중
              </span>
            )}
          </div>
          </div>
        </div>
        </details>
      )}

      {/* STEP STUDIO-MOBILE-NOTICE-TIMER-1 · 기타 설정 details — 공지는 타이머 패널로 이동.
          STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 빠른 등록도 rail 전용 버튼으로 이동 →
          기타 details 는 제거.  (ProductsTab 안 중복 UI 정리.) */}
      {/* details removed · 접근 경로는 rail(상단) + 타이머 sheet(공지) 로 완전 이관. */}

      <div className="order-3" data-testid="live-console-product-list-section">
        <p className="text-[11px] font-black text-gray-500 mb-1.5">
          방송 연결 상품 목록 · 총 {items.length}개
        </p>
        <div className="max-h-[22rem] overflow-y-auto space-y-1.5 pr-1" data-testid="live-console-product-list">
          {remoteCursorPid && items.length > 0 && <RemoteCloseRow id="__remote_close_top__" cursorPid={remoteCursorPid} pos="top" />}
          {items.length === 0 && (
            <p className="text-xs text-gray-400 py-4 text-center">
              방송 상품이 아직 없어요. 상품 등록은 판매자센터에서 진행해 주세요.
            </p>
          )}
          {items.map((p) => {
            const isActive = p.product_id === active?.product_id;
            const isIntroduced = !!p.is_introduced;
            const isCursor = remoteCursorPid === p.product_id;
            return (
              <div key={p.product_id}
                    className={`flex items-center gap-2 p-2 rounded-lg border ${
                      isActive ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'
                    }`}
                    style={isCursor ? { outline: '3px solid #EF4444', outlineOffset: '-1px', backgroundColor: 'rgba(239,68,68,0.14)' } : undefined}
                    data-remote-cursor={isCursor ? '1' : undefined}
                    data-testid={`live-console-product-${p.product_id}`}>
                {isCursor && (
                  <span className="shrink-0 px-1.5 py-0.5 rounded bg-[#EF4444] text-white text-[9px] font-black leading-none"
                        data-testid="live-console-remote-cursor-badge">선택</span>
                )}
                <span className="w-6 h-6 rounded-full bg-black/[0.06] dark:bg-white/10 text-[10px] font-black flex items-center justify-center text-black dark:text-white shrink-0">{p.slot_number}</span>
                <SafeImg src={p.image} kind="product" alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate text-black dark:text-white">{p.name}</p>
                  <p className="text-[11px] font-black text-[#FF3B30]">{_won(p.sale_price)}</p>
                </div>
                {isActive ? (
                  <span className="shrink-0 px-2 py-1 rounded-full text-[10px] font-black bg-[#FF3B30] text-white inline-flex items-center gap-0.5"
                        data-testid={`live-console-active-badge-${p.product_id}`}>
                    소개중
                  </span>
                ) : isIntroduced ? (
                  <>
                    <span className="shrink-0 px-2 py-1 rounded-full text-[10px] font-black bg-emerald-600 text-white"
                          data-testid={`live-console-introduced-badge-${p.product_id}`}>
                      소개완료
                    </span>
                    <button type="button" onClick={() => setActive(p.product_id)}
                            disabled={!isLive || busy === p.product_id || !scheduleId}
                            className="shrink-0 px-2 py-1 rounded-full text-[10px] font-bold border border-black/15 dark:border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] disabled:opacity-40"
                            data-testid={`live-console-product-reintroduce-${p.product_id}`}>
                      다시 소개
                    </button>
                    {visMode === 'introduced_only' && (
                      <button type="button" onClick={() => unintroduce(p.product_id)}
                              disabled={busy === p.product_id || !scheduleId}
                              className="shrink-0 p-1 rounded-full text-gray-500 hover:text-red-500 disabled:opacity-40"
                              title="다시 가리기"
                              data-testid={`live-console-product-unintroduce-${p.product_id}`}>
                        <VideoOff size={11} />
                      </button>
                    )}
                  </>
                ) : (
                  <button type="button" onClick={() => setActive(p.product_id)}
                          disabled={!isLive || busy === p.product_id || !scheduleId}
                          className="shrink-0 px-2 py-1 rounded-full text-[10px] font-bold border border-black/15 dark:border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`live-console-product-activate-${p.product_id}`}>
                    소개하기
                  </button>
                )}
              </div>
            );
          })}
          {remoteCursorPid && items.length > 0 && <RemoteCloseRow id="__remote_close_bottom__" cursorPid={remoteCursorPid} pos="bottom" />}
        </div>
      </div>
    </div>
  );
};

// ── Comments · Orders tab (LIVEMOA chat + comment-order candidates + pin) ──
// STEP 8-B — SELLER LIVE COMMENT UX SYNC · 실시간 채팅 UX 우선 재배치.
//   · 3-region flex column: [pinned+counter · shrink-0] [chat feed · flex-1
//     scroll] [input + candidates collapsible · shrink-0]
//   · REUSE ONLY — chat API / candidates API / pin API 그대로 사용.
//   · scheduleId 는 candidates 엔드포인트용 (streaming session 과 schedule 은
//     분리) · scheduleId 없으면 candidate 카운터 hide.
const CommentsOrdersTab = ({ sessionId, scheduleId, channels, isLive, isOwner, sharedMessages,
                              isPinned = false, onPinToggle = null }) => {
  // STEP 8-I-2A · sharedMessages 가 상위에서 주어지면 그것을 authoritative messages 로 사용.
  //   → 중복 폴링 0건 · Overlay 와 완전히 동일한 데이터 공유.
  // sharedMessages 가 없으면 (backward-compat, standalone 사용) 기존 내부 폴링 fallback.
  const [ownMessages, setOwnMessages] = useState([]);
  const messages = sharedMessages || ownMessages;
  const [candidates, setCandidates] = useState([]);
  const [pinned, setPinned] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'livemoa' | 'youtube' | 'tiktok' | 'instagram'
  const [candidatesOpen, setCandidatesOpen] = useState(false); // STEP 8-B · 기본 접힘
  const [autoScroll, setAutoScroll] = useState(true);          // STEP 8-B · sticky bottom
  const lastTsRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const chatScrollRef = useRef(null);

  // Chat polling (fallback · sharedMessages 없을 때만) + pinned message polling (독립)
  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    const load = async () => {
      try {
        if (!sharedMessages) {
          const params = lastTsRef.current ? { since: lastTsRef.current } : { limit: 50 };
          const { data } = await api.get(`/public/streaming/sessions/${sessionId}/chat`, { params });
          if (!alive) return;
          const incoming = (data?.messages || []).filter((m) => !seenIdsRef.current.has(m.message_id));
          incoming.forEach((m) => seenIdsRef.current.add(m.message_id));
          if (incoming.length) {
            setOwnMessages((prev) => [...prev, ...incoming].slice(-200));
            const last = incoming[incoming.length - 1];
            if (last?.created_at) lastTsRef.current = last.created_at;
          }
        }
        const { data: ctx } = await api.get(`/public/streaming/sessions/${sessionId}/live-context`);
        if (alive) setPinned(ctx?.pinned_message || null);
      } catch { /* soft */ }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [sessionId, sharedMessages]);

  // Candidates polling (schedule-scoped · optional)
  useEffect(() => {
    if (!scheduleId) { setCandidates([]); return; }
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/seller/broadcasts/${scheduleId}/comment-candidates`);
        const arr = Array.isArray(data) ? data : (data?.items || []);
        if (alive) setCandidates(arr);
      } catch { /* soft · 스케줄 없으면 404 → 무시 */ }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [scheduleId]);

  // STEP 8-B · sticky bottom (auto-scroll to bottom on new msg, unless user
  // scrolled up to read history · then pause auto-scroll).
  useEffect(() => {
    if (!autoScroll) return;
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, autoScroll]);

  const onChatScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (nearBottom !== autoScroll) setAutoScroll(nearBottom);
  };
  const jumpToLatest = () => {
    setAutoScroll(true);
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // Source counters — STEP 8-H-6 · 통합댓글 (messages) 기준으로 source 별 카운트.
  // 이전에는 livemoa 는 messages.length, youtube 는 candidates.filter(source) 로 계산해
  // 통합댓글에 없는 YouTube 원문 카운트가 candidate 로만 잡히던 부정합을 해소.
  // candidate 는 여전히 [주문후보] 접이식 리스트에서 별개 필터에 활용.
  const sourceCounts = useMemo(() => {
    let livemoa = 0, youtube = 0, tiktok = 0, instagram = 0;
    for (const m of messages) {
      const src = (m.source || 'livemoa').toLowerCase();
      if (src.startsWith('youtube')) youtube += 1;
      else if (src.startsWith('tiktok')) tiktok += 1;
      else if (src.startsWith('instagram')) instagram += 1;
      else livemoa += 1; // 'livemoa' | undefined | 'livemoa-native-chat'
    }
    const ttProductionReady = !!channels?.tiktok?.production_ready;
    return {
      total: livemoa + youtube + tiktok + instagram,
      livemoa, youtube,
      tiktok: (tiktok > 0 && ttProductionReady) ? tiktok : 0,
      instagram: instagram > 0 ? instagram : 0,
      tiktok_visible: tiktok > 0 && ttProductionReady,
      instagram_visible: instagram > 0,
    };
  }, [messages, channels]);

  // Track social proof toasts by polling chat (chat GET returns text/masked; social proof
  // isn't persisted — this section reads local ref if we ever attach a hook, otherwise empty).
  // For now this stays empty on the seller side; buyers see it live. (Do NOT re-fire events.)

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/public/streaming/sessions/${sessionId}/chat`, { text: t });
      if (data?.message_id && !seenIdsRef.current.has(data.message_id)) {
        seenIdsRef.current.add(data.message_id);
        // sharedMessages(부모 폴링)가 없을 때만 로컬 상태 즉시 업데이트.
        // sharedMessages 사용 중이면 부모 폴링이 곧 최신 메시지를 반영함.
        if (!sharedMessages) {
          setOwnMessages((prev) => [...prev, {
            message_id: data.message_id, author_masked: data.author_masked,
            author_role: data.author_role || 'seller', text: data.text, created_at: data.created_at,
          }].slice(-200));
        }
        lastTsRef.current = data.created_at;
      }
      setText('');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) toast.error(e?.response?.data?.detail || '너무 빠르게 작성하고 있어요.');
      else toast.error(e?.response?.data?.detail || '전송 실패');
    } finally { setBusy(false); }
  };

  const pin = async (mid) => {
    try {
      const { data } = await api.post(`/seller/streaming/sessions/${sessionId}/pin`, { message_id: mid });
      setPinned(data?.pinned_message || null);
      toast.success('댓글을 고정했어요.');
    } catch (e) { toast.error(e?.response?.data?.detail || '고정 실패'); }
  };
  const unpin = async () => {
    try {
      await api.delete(`/seller/streaming/sessions/${sessionId}/pin`);
      setPinned(null);
      toast.success('고정을 해제했어요.');
    } catch (e) { toast.error(e?.response?.data?.detail || '해제 실패'); }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="live-console-tab-comments">
      {/* [1/3] TOP · pinned + source counter (shrink-0) */}
      <div className="shrink-0 px-3 pt-3 pb-2 space-y-2 border-b border-black/5 dark:border-white/5">
        {pinned && (
          <div className="rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-2.5 flex items-start gap-2"
                data-testid="live-console-pinned">
            <Pin size={12} className="text-[#FF3B30] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black text-[#FF3B30] uppercase tracking-wider mb-0.5">고정 댓글</p>
              <p className="text-xs leading-snug text-black dark:text-white line-clamp-2">
                <span className="font-black text-gray-700 dark:text-gray-300 mr-1">{pinned.author_masked}</span>
                {pinned.text}
              </p>
            </div>
            {isOwner && (
              <button type="button" onClick={unpin}
                      className="text-[10px] font-bold text-[#FF3B30] hover:underline inline-flex items-center gap-0.5 shrink-0"
                      data-testid="live-console-pinned-unpin">
                <PinOff size={11} /> 해제
              </button>
            )}
          </div>
        )}

        {/* STEP 8-LIVE-CONSOLE-WIRING · 통합 댓글 Source Counter (Mock 숫자 X · 실 데이터만) */}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="live-console-source-counter">
          {typeof onPinToggle === 'function' && (
            <button type="button" onClick={onPinToggle}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black border transition ${
                      isPinned
                        ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                        : 'border-black/15 dark:border-white/20 text-gray-700 dark:text-gray-200 hover:border-[#FF3B30]'
                    }`}
                    aria-label={isPinned ? '댓글 고정 해제' : '댓글 고정'}
                    data-testid="live-console-comments-pin-toggle">
              {isPinned ? <PinOff size={11} /> : <Pin size={11} />} 고정
            </button>
          )}
          <button type="button" onClick={() => setSourceFilter('all')}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-black border transition ${
                    sourceFilter === 'all'
                      ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                      : 'border-black/10 dark:border-white/15 text-gray-600 dark:text-gray-300 hover:border-[#FF3B30]'
                  }`}
                  data-testid="live-console-src-all">
            전체 <span className="ml-0.5" data-testid="live-console-src-count-all">{sourceCounts.total}</span>
          </button>
          <button type="button" onClick={() => setSourceFilter('livemoa')}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-black border transition ${
                    sourceFilter === 'livemoa'
                      ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                      : 'border-black/10 dark:border-white/15 text-gray-600 dark:text-gray-300 hover:border-[#FF3B30]'
                  }`}
                  data-testid="live-console-src-livemoa">
            LIVEMOA <span className="ml-0.5" data-testid="live-console-src-count-livemoa">{sourceCounts.livemoa}</span>
          </button>
          <button type="button" onClick={() => setSourceFilter('youtube')}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-black border transition ${
                    sourceFilter === 'youtube'
                      ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                      : 'border-black/10 dark:border-white/15 text-gray-600 dark:text-gray-300 hover:border-[#FF3B30]'
                  }`}
                  data-testid="live-console-src-youtube">
            YouTube <span className="ml-0.5" data-testid="live-console-src-count-youtube">{sourceCounts.youtube}</span>
          </button>
          {sourceCounts.tiktok_visible && (
            <button type="button" onClick={() => setSourceFilter('tiktok')}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-black border transition ${
                      sourceFilter === 'tiktok'
                        ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                        : 'border-black/10 dark:border-white/15 text-gray-600 dark:text-gray-300 hover:border-[#FF3B30]'
                    }`}
                    data-testid="live-console-src-tiktok">
              TikTok <span className="ml-0.5" data-testid="live-console-src-count-tiktok">{sourceCounts.tiktok}</span>
            </button>
          )}
          {sourceCounts.instagram_visible && (
            <button type="button" onClick={() => setSourceFilter('instagram')}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-black border transition ${
                      sourceFilter === 'instagram'
                        ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                        : 'border-black/10 dark:border-white/15 text-gray-600 dark:text-gray-300 hover:border-[#FF3B30]'
                    }`}
                    data-testid="live-console-src-instagram">
              Instagram <span className="ml-0.5">{sourceCounts.instagram}</span>
            </button>
          )}
          {!scheduleId && (
            <span className="text-[10px] text-gray-400 ml-1" data-testid="live-console-src-hint">
              외부 SNS 카운터는 편성표 방송 시작 후 표시돼요.
            </span>
          )}
        </div>
      </div>

      {/* [2/3] MIDDLE · 실시간 채팅 피드 (유일한 스크롤 영역) */}
      <div className="flex-1 min-h-0 flex flex-col relative bg-black/[0.02] dark:bg-white/[0.02]">
        <div className="shrink-0 flex items-center justify-between px-3 py-1.5">
          <p className="text-[11px] font-black text-gray-500">
            실시간 댓글 ({messages.length})
            <span className="ml-1 text-gray-400 font-medium">
              {sourceFilter !== 'all' && sourceFilter !== 'livemoa'
                ? `· ${sourceFilter.toUpperCase()} 필터 · 외부 SNS는 아래 주문 후보`
                : '· LIVEMOA 자체 채팅'}
            </span>
          </p>
          {!autoScroll && messages.length > 0 && (
            <button type="button" onClick={jumpToLatest}
                    className="text-[10px] font-black text-[#FF3B30] hover:underline"
                    data-testid="live-console-chat-jump-latest">
              최신으로 ↓
            </button>
          )}
        </div>
        <div ref={chatScrollRef} onScroll={onChatScroll}
              className="flex-1 min-h-0 overflow-y-auto px-3 pb-2 space-y-1"
              data-testid="live-console-chat-list">
          {messages.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-6">아직 댓글이 없어요.</p>
          )}
          {messages
            .filter((m) => {
              // STEP 8-H-6 · Source filter 를 통합댓글 리스트에도 적용.
              if (sourceFilter === 'all') return true;
              const src = (m.source || 'livemoa').toLowerCase();
              if (sourceFilter === 'livemoa') return !src || src.startsWith('livemoa');
              return src.startsWith(sourceFilter);
            })
            .map((m) => {
            // STEP 8-I-4 · Activity (Social Proof) 는 nickname 없이 아이콘 문구만.
            if (m._activity) {
              // STEP 8-I-4E · CommentsOrdersTab 은 history 뷰라 대부분 정적 렌더.
              // 실시간 도착만 sparkle · sharedActivitySeenRef 가 없으면 mount 이후 등장 판정.
              const isFresh = !(window.__lm_seen_act ||= new Set()).has(m.message_id);
              if (isFresh) window.__lm_seen_act.add(m.message_id);
              const emojiCls = isFresh ? 'lm-emoji-sparkle' : '';
              // STEP 8-I-5 Group C · Activity 는 일반 댓글보다 살짝 강조 (subtle accent tint · border-left).
              return (
                <div key={m.message_id}
                      className="text-sm font-black text-gray-800 dark:text-gray-100 py-1 pl-2 pr-1 my-0.5 flex items-center gap-2 rounded-md bg-[#FFF4F3] dark:bg-[#FF3B30]/10 border-l-2 border-[#FF3B30]"
                      data-testid={`live-console-activity-${m.message_id}`}>
                  <span className={`text-[26px] leading-none ${emojiCls}`}>🎉</span>
                  <span>{m.text.replace(/^🎉\s*/, '')}</span>
                </div>
              );
            }
            const isPinned = pinned?.message_id === m.message_id;
            const src = (m.source || 'livemoa').toLowerCase();
            const srcBadge = src.startsWith('youtube') ? { label: 'YT', cls: 'bg-red-600' }
              : src.startsWith('tiktok') ? { label: 'TT', cls: 'bg-black' }
              : src.startsWith('instagram') ? { label: 'IG', cls: 'bg-pink-500' }
              : null;
            // STEP 8-I-3 · Seller-view unmask 지원 · author_display 우선 · fallback = masked.
        const authorName = m.author_display || m.author_masked || '익명';
        return (
              <div key={m.message_id}
                    className={`group text-xs leading-snug break-words flex items-start gap-1 py-0.5 ${isPinned ? 'bg-[#FF3B30]/5 rounded px-1' : ''}`}
                    data-testid={`live-console-msg-${m.message_id}`}>
                <div className="flex-1 min-w-0">
                  <span className={`font-black mr-1 ${m.author_role === 'seller' ? 'text-[#FF3B30]' : 'text-black dark:text-white'}`}>
                    {srcBadge && (
                      <span className={`mr-1 px-1 py-0.5 rounded text-white text-[9px] align-middle font-black ${srcBadge.cls}`}
                            data-testid={`live-console-msg-src-${m.message_id}`}>{srcBadge.label}</span>
                    )}
                    {m.author_role === 'seller' && (
                      <span className="mr-1 px-1 py-0.5 rounded bg-[#FF3B30] text-white text-[9px] align-middle">판매자</span>
                    )}
                    {authorName}
                  </span>
                  <span className="text-gray-700 dark:text-gray-200">{m.text}</span>
                </div>
                {isOwner && !isPinned && (
                  <button type="button" onClick={() => pin(m.message_id)}
                          className="opacity-0 group-hover:opacity-100 shrink-0 text-[10px] font-bold text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-0.5"
                          data-testid={`live-console-pin-${m.message_id}`}
                          title="댓글 고정">
                    <Pin size={11} /> 고정
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* [3/3] BOTTOM · 입력창 (항상 노출) + 주문 후보 접이식 (shrink-0) */}
      <div className="shrink-0 border-t border-black/10 dark:border-white/10 bg-white dark:bg-[#0F0F0F]">
        <div className="p-2.5 flex gap-1.5">
          <input type="text" value={text} maxLength={200} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
                  placeholder="댓글 입력…"
                  disabled={!isLive || busy}
                  className="flex-1 min-w-0 px-3 py-2 rounded-full text-xs bg-black/[0.04] dark:bg-white/[0.06] border border-black/10 dark:border-white/10 focus:outline-none focus:border-[#FF3B30] text-black dark:text-white"
                  data-testid="live-console-chat-input" />
          <button type="button" onClick={send} disabled={!isLive || busy || !text.trim()}
                  className="shrink-0 px-3 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                  data-testid="live-console-chat-send">
            <Send size={12} /> 전송
          </button>
        </div>

        {/* 주문 후보 · 접이식 (default 접힘) · 펼쳐도 입력창은 유지 */}
        <div className="border-t border-black/5 dark:border-white/5">
          <button type="button" onClick={() => setCandidatesOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-black text-gray-600 dark:text-gray-300 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition"
                  data-testid="live-console-candidates-toggle"
                  aria-expanded={candidatesOpen}>
            <span className="inline-flex items-center gap-1.5">
              <ShoppingBag size={11} />
              주문 후보
              <span className="px-1.5 py-0.5 rounded-full bg-orange-500/15 text-orange-600 dark:text-orange-400 text-[10px] font-black"
                    data-testid="live-console-candidates-count">
                {candidates.length}
              </span>
              {sourceFilter !== 'all' && sourceFilter !== 'livemoa' && (
                <span className="text-gray-400 font-medium">
                  · {sourceFilter.toUpperCase()} {sourceCounts[sourceFilter] || 0}건
                </span>
              )}
              <span className="text-gray-400 font-medium hidden sm:inline">· Comment Engine · OMS 재사용</span>
            </span>
            <ChevronRight size={12}
                          className={`text-gray-400 transition-transform ${candidatesOpen ? 'rotate-90' : ''}`} />
          </button>
          {candidatesOpen && (
            <div className="max-h-32 overflow-y-auto px-3 pb-2 space-y-1"
                  data-testid="live-console-candidates">
              {candidates.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-3">주문 후보가 없어요.</p>
              )}
              {candidates
                .filter((c) => {
                  if (sourceFilter === 'all' || sourceFilter === 'livemoa') return true;
                  const src = (c.source || '').toLowerCase();
                  return src.startsWith(sourceFilter);
                })
                .slice(0, 20)
                .map((c) => {
                  const src = (c.source || '').toLowerCase();
                  const platformLabel = src.startsWith('youtube') ? 'YT'
                    : src.startsWith('tiktok') ? 'TT'
                    : src.startsWith('instagram') ? 'IG'
                    : '';
                  return (
                    <div key={c.candidate_id} className="text-xs flex items-center gap-2"
                          data-testid={`live-console-candidate-${c.candidate_id}`}>
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${
                        c.status === 'confirmed' ? 'bg-emerald-500 text-white'
                        : c.status === 'cancelled' ? 'bg-gray-400 text-white'
                        : 'bg-orange-500 text-white'
                      }`}>{c.status}</span>
                      {platformLabel && (
                        <span className="text-[9px] font-black px-1 py-0.5 rounded bg-black text-white"
                              data-testid={`live-console-cand-src-${c.candidate_id}`}>{platformLabel}</span>
                      )}
                      <span className="text-gray-500">{c.slot_number}번</span>
                      <span className="font-bold text-black dark:text-white truncate flex-1">{c.product_name}</span>
                      <span className="text-gray-500 shrink-0">× {c.quantity}</span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Platform status tab ────────────────────────────────────────────────────
// STEP 8-LIVE-CONSOLE-WIRING · read-only 실 상태 표시.
// STEP 8-LIVE-MULTISTREAM-B/C · YouTube Manual Stream-Key egress 연결 UI 추가.
// - 계정 연결됨 vs 송출중 절대 동일 표시 금지 (2줄 분리).
// - 가짜 CONNECTED 없음. 값은 기존 API 재사용.
// - OAuth 연결/해제는 판매자센터 담당 · 여기서는 read-only.
// - Stream Key 는 절대 저장/응답 재노출 X.
const StatusTab = ({ liveKitState, sessionId, sessionStatus, channels, refreshChannels,
                     egressStatus, egressCapability, onOpenYtEgress, onStopYtEgress,
                     onOpenYtComment, ytStatus,
                     onOpenIgEgress, onStopIgEgress }) => {
  const [meta, setMeta] = useState(null);
  // STEP TT-LIVE-PREP-1 · TikTok Partner readiness 표시 (실 송출 활성화 X · 문구 표시만).
  const [ttStatus, setTtStatus] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/seller/streaming/config').then(({ data }) => { if (alive) setMeta(data); }).catch(() => {});
    api.get('/seller/tiktok/status').then(({ data }) => { if (alive) setTtStatus(data); }).catch(() => {});
    return () => { alive = false; };
  }, [sessionId]);

  const yt = channels?.youtube || {};
  const tt = channels?.tiktok || {};

  // STEP 8-LIVE-SESSION-STATE-AND-BUYER-ROUTE-FIX · 서버 SoT 반영
  // liveKitState = local React (this device 의 publisher room state)
  // sessionStatus = 서버 streaming_sessions.streaming_status (모든 device 공유 SoT)
  const livekitLabel = (() => {
    if (!meta?.livekit_configured) return { text: '미설정', tone: 'gray' };
    if (liveKitState === 'publishing') return { text: '송출 중', tone: 'green' };
    if (liveKitState === 'reconnecting') return { text: '재연결 중…', tone: 'orange' };
    if (liveKitState === 'connecting') return { text: '연결 중…', tone: 'orange' };
    if (liveKitState === 'error') return { text: '오류', tone: 'red' };
    // local idle 이지만 서버가 live 면 · STEP 8-E 문구 중립화 (heuristic 판정 없이 세션만 활성)
    if (sessionStatus === 'live') return { text: 'LIVE 세션 활성', tone: 'green' };
    return { text: '대기', tone: 'gray' };
  })();

  const Dot = ({ tone }) => {
    const cls = tone === 'green' ? 'bg-emerald-500 animate-pulse'
      : tone === 'orange' ? 'bg-orange-500 animate-pulse'
      : tone === 'red' ? 'bg-red-500' : 'bg-gray-400';
    return <span className={`w-1.5 h-1.5 rounded-full ${cls}`} />;
  };

  return (
    <div className="p-3 space-y-2" data-testid="live-console-tab-status">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-black text-gray-500">송출 플랫폼 상태</p>
        <button type="button" onClick={refreshChannels}
                className="text-[10px] font-bold text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-0.5"
                data-testid="live-console-status-refresh">
          <RefreshCw size={10} /> 새로고침
        </button>
      </div>

      {/* LiveKit — 실 송출 상태만 */}
      <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-black/10 dark:border-white/10"
            data-testid="live-console-status-livekit">
        <div className="flex items-center gap-2 min-w-0">
          <Wifi size={12} className="text-gray-400 shrink-0" />
          <p className="text-xs font-bold text-black dark:text-white truncate">LiveKit</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Dot tone={livekitLabel.tone} />
          <span className="text-[10px] font-black text-gray-600 dark:text-gray-300">{livekitLabel.text}</span>
        </div>
      </div>

      {/* YouTube — 계정 연결 (OAuth) + 송출 상태 2줄 분리 */}
      <div className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 space-y-1"
            data-testid="live-console-status-youtube">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Wifi size={12} className="text-gray-400 shrink-0" />
            <p className="text-xs font-bold text-black dark:text-white truncate">YouTube</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Dot tone={yt.connected ? 'green' : 'gray'} />
            <span className="text-[10px] font-black text-gray-600 dark:text-gray-300"
                  data-testid="live-console-yt-account">
              {yt.connected ? '채널 연결됨' : '계정 미연결'}
            </span>
          </div>
        </div>
        {yt.connected && (yt.channel_title || yt.channel_id) && (
          <p className="text-[10px] text-gray-500 pl-6 truncate" data-testid="live-console-yt-channel">
            {yt.channel_title ? `${yt.channel_title}` : ''}{yt.channel_id ? ` · ${yt.channel_id.slice(0, 12)}…` : ''}
          </p>
        )}
        <div className="flex items-center justify-between pl-6">
          <span className="text-[10px] text-gray-400">송출</span>
          <div className="flex items-center gap-1.5">
            {(() => {
              const ytDest = (egressStatus?.destinations || [])
                .filter((d) => d.platform === 'youtube' && d.status !== 'stopped')
                .slice(-1)[0];
              const tone = ytDest?.status === 'active' ? 'green'
                : ytDest?.status === 'starting' ? 'orange'
                : ytDest?.status === 'error' ? 'red' : 'gray';
              const label = ytDest?.status === 'active' ? '송출 중'
                : ytDest?.status === 'starting' ? '연결 중…'
                : ytDest?.status === 'error' ? '오류' : '미연결';
              return (<>
                <Dot tone={tone} />
                <span className="text-[10px] font-black text-gray-600 dark:text-gray-300"
                      data-testid="live-console-yt-streaming">{label}</span>
              </>);
            })()}
          </div>
        </div>
        {/* STEP 8-LIVE-MULTISTREAM-B/C · Manual Stream-Key egress 연결 */}
        {(() => {
          const ytDest = (egressStatus?.destinations || [])
            .filter((d) => d.platform === 'youtube' && d.status !== 'stopped')
            .slice(-1)[0];
          const canConnect = liveKitState === 'publishing' && sessionId
                              && egressCapability?.available !== false;
          const isActive = ytDest && ytDest.status !== 'stopped';
          return (
            <div className="pt-1 pl-6 space-y-1" data-testid="live-console-yt-egress-box">
              {isActive ? (
                <>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-gray-400">RTMPS 목적지</span>
                    <span className="font-mono text-gray-500 truncate flex-1" title={ytDest.url_hint}>
                      {ytDest.url_hint}
                    </span>
                    <span className="font-mono text-gray-400" data-testid="live-console-yt-key-mask">•••••••••</span>
                  </div>
                  {ytDest.last_error && ytDest.status === 'error' && (
                    <p className="text-[10px] text-red-500 leading-snug" data-testid="live-console-yt-error">
                      {ytDest.last_error}
                    </p>
                  )}
                  <div className="flex gap-1.5">
                    {ytDest.status === 'error' && (
                      <button type="button" onClick={onOpenYtEgress}
                              className="px-2.5 py-1 rounded-full text-[10px] font-black border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                              data-testid="live-console-yt-retry">
                        다시 연결
                      </button>
                    )}
                    <button type="button" onClick={onStopYtEgress}
                            className="px-2.5 py-1 rounded-full text-[10px] font-black border border-black/15 dark:border-white/20 text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10"
                            data-testid="live-console-yt-stop">
                      YouTube 송출 중지
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button type="button" onClick={onOpenYtEgress} disabled={!canConnect}
                          className="px-3 py-1.5 rounded-full text-[10px] font-black bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                          data-testid="live-console-yt-connect">
                    <Radio size={10} /> YouTube 송출 연결
                  </button>
                  {!canConnect && liveKitState !== 'publishing' && (
                    <p className="text-[10px] text-gray-400" data-testid="live-console-yt-hint-notlive">
                      LIVEMOA 방송 시작 후에 YouTube 송출을 연결할 수 있어요.
                    </p>
                  )}
                  {egressCapability && egressCapability.available === false && (
                    <p className="text-[10px] text-orange-500" data-testid="live-console-yt-billing-blocked">
                      LiveKit Cloud Egress 사용 불가 · {egressCapability.reason === 'billing_blocked' ? '요금제 확인 필요' : egressCapability.reason}
                    </p>
                  )}
                </>
              )}
            </div>
          );
        })()}
        {/* STEP 8-H-3 · YouTube 댓글 (Comment Poller) 상태 · Egress 와 독립.
            송출이 이미 active 여도, 아니어도, LIVEMOA 방송 시작 후라면 별도 연결 가능. */}
        {(() => {
          const ytSess = ytStatus?.session || null;
          const commentActive = ytSess && ytSess.status === 'connected';
          const canConnect = liveKitState === 'publishing' && sessionId;
          const commentTone = commentActive ? 'green'
            : (ytSess?.status === 'error' ? 'red' : 'gray');
          const commentLabel = commentActive ? '연결됨'
            : (ytSess?.status === 'error' ? '오류' : '미연결');
          return (
            <div className="mt-2 pt-2 border-t border-black/5 dark:border-white/5"
                  data-testid="live-console-yt-comment-box">
              <div className="flex items-center gap-2 pl-6">
                <span className="text-[10px] text-gray-400 shrink-0">댓글</span>
                <div className="flex items-center gap-1.5 flex-1">
                  <Dot tone={commentTone} />
                  <span className="text-[10px] font-black text-gray-600 dark:text-gray-300"
                        data-testid="live-console-yt-comment-status">
                    {commentLabel}
                  </span>
                  {commentActive && ytSess?.video_title && (
                    <span className="text-[10px] text-gray-400 truncate" title={ytSess.video_title}>
                      · {ytSess.video_title}
                    </span>
                  )}
                </div>
              </div>
              <div className="pl-6 mt-1.5">
                {commentActive ? (
                  <p className="text-[10px] text-gray-500 leading-snug">
                    YouTube 시청자 댓글이 통합댓글에 실시간으로 표시돼요. (auth: {ytSess?.auth_mode})
                  </p>
                ) : (
                  <>
                    <button type="button" onClick={onOpenYtComment} disabled={!canConnect}
                            className="px-3 py-1.5 rounded-full text-[10px] font-black bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                            data-testid="live-console-yt-comment-connect">
                      <MessageCircle size={10} /> YouTube 댓글 연결
                    </button>
                    <p className="text-[10px] text-gray-400 mt-1 leading-snug">
                      송출과 별도로 시청 URL 만 입력해 댓글 poller 를 시작합니다.
                    </p>
                  </>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Instagram — STEP MP-EGRESS-PREP-1 · Manual Stream URL + Key Egress destination.
          IG OAuth/Reels/Webhook 은 별도 · 여기서는 IG Live Producer 발급 URL/Key 로 RTMPS push. */}
      <div className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 space-y-1"
            data-testid="live-console-status-instagram">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Wifi size={12} className="text-gray-400 shrink-0" />
            <p className="text-xs font-bold text-black dark:text-white truncate">Instagram</p>
          </div>
          {(() => {
            const igDest = ((egressStatus?.destinations || [])
              .filter((d) => d.platform === 'instagram')).slice(-1)[0];
            const isActive = igDest && igDest.status !== 'stopped';
            return (
              <div className="flex items-center gap-1.5 shrink-0">
                <Dot tone={isActive ? (igDest.status === 'error' ? 'red' : 'green') : 'gray'} />
                <span className="text-[10px] font-black text-gray-600 dark:text-gray-300"
                      data-testid="live-console-ig-status">
                  {isActive ? (igDest.status === 'error' ? '오류' : '송출 중') : '미연결'}
                </span>
              </div>
            );
          })()}
        </div>
        {(() => {
          const igDest = ((egressStatus?.destinations || [])
            .filter((d) => d.platform === 'instagram')).slice(-1)[0];
          const canConnect = liveKitState === 'publishing' && sessionId
                              && egressCapability?.available !== false;
          const isActive = igDest && igDest.status !== 'stopped';
          return (
            <div className="pt-1 pl-6 space-y-1" data-testid="live-console-ig-egress-box">
              {isActive ? (
                <>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-gray-400">RTMPS 목적지</span>
                    <span className="font-mono text-gray-500 truncate flex-1" title={igDest.url_hint}>
                      {igDest.url_hint}
                    </span>
                    <span className="font-mono text-gray-400" data-testid="live-console-ig-key-mask">•••••••••</span>
                  </div>
                  {igDest.last_error && igDest.status === 'error' && (
                    <p className="text-[10px] text-red-500 leading-snug" data-testid="live-console-ig-error">
                      {igDest.last_error}
                    </p>
                  )}
                  <div className="flex gap-1.5">
                    {igDest.status === 'error' && (
                      <button type="button" onClick={onOpenIgEgress}
                              className="px-2.5 py-1 rounded-full text-[10px] font-black border border-[#FF4F3D]/40 text-[#FF4F3D] hover:bg-[#FF4F3D]/10"
                              data-testid="live-console-ig-retry">
                        다시 연결
                      </button>
                    )}
                    <button type="button" onClick={onStopIgEgress}
                            className="px-2.5 py-1 rounded-full text-[10px] font-black border border-black/15 dark:border-white/20 text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10"
                            data-testid="live-console-ig-stop">
                      Instagram 송출 중지
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button type="button" onClick={onOpenIgEgress} disabled={!canConnect}
                          className="px-3 py-1.5 rounded-full text-[10px] font-black bg-[#E1306C] text-white hover:bg-[#C13584] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                          data-testid="live-console-ig-connect">
                    <Radio size={10} /> Instagram 송출 연결
                  </button>
                  <p className="text-[10px] text-gray-400 leading-snug">
                    Instagram Live Producer 에서 발급받은 Stream URL/Key 를 붙여넣기 하세요.
                    OAuth/Reels/Webhook 과는 별도 · Meta 심사와 독립.
                  </p>
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* STEP TT-LIVE-PREP-1 · TikTok LIVE 는 공식 Integration/Partner Access 승인 전까지
          "준비 중 / 승인 후 사용 가능" 문구만 노출. Mock 결과를 Production 성공으로 표시 X. */}
      <div className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 space-y-1"
            data-testid="live-console-status-tiktok">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Wifi size={12} className="text-gray-400 shrink-0" />
            <p className="text-xs font-bold text-black dark:text-white truncate">TikTok</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Dot tone={ttStatus?.connected ? 'green' : 'gray'} />
            <span className="text-[10px] font-black text-gray-600 dark:text-gray-300"
                  data-testid="live-console-tt-account">
              {ttStatus?.connected ? '계정 연결됨' : '계정 미연결'}
            </span>
          </div>
        </div>
        {ttStatus?.connected && ttStatus?.display_name && (
          <p className="text-[10px] text-gray-500 pl-6 truncate"
             data-testid="live-console-tt-username">
            @{ttStatus.display_name}
          </p>
        )}

        {/* STEP TT-LIVE-PREP-1 · Partner Access 상태 (승인 전 static · 문구만) */}
        <div className="flex items-center justify-between pl-6">
          <span className="text-[10px] text-gray-400">TikTok LIVE 연동</span>
          <div className="flex items-center gap-1.5">
            <Dot tone="orange" />
            <span className="text-[10px] font-black text-orange-600"
                  data-testid="live-console-tt-live-access">
              Partner Access 준비 중
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between pl-6">
          <span className="text-[10px] text-gray-400">TikTok 동시송출</span>
          <div className="flex items-center gap-1.5">
            <Dot tone="gray" />
            <span className="text-[10px] font-black text-gray-500"
                  data-testid="live-console-tt-simulcast">
              승인 후 사용 가능
            </span>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 pl-6 leading-snug">
          TikTok 공식 LIVE Integration/Partner Access 승인 후 활성화됩니다.
        </p>
      </div>

      {/* Instagram — Production API 미승인 · 항상 연결 대기 */}
      <div className="px-3 py-2 rounded-lg border border-black/10 dark:border-white/10 space-y-1"
            data-testid="live-console-status-instagram">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Wifi size={12} className="text-gray-400 shrink-0" />
            <p className="text-xs font-bold text-black dark:text-white truncate">Instagram</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Dot tone="gray" />
            <span className="text-[10px] font-black text-gray-500"
                  data-testid="live-console-ig-account">계정 미연결</span>
          </div>
        </div>
        <div className="flex items-center justify-between pl-6">
          <span className="text-[10px] text-gray-400">송출</span>
          <div className="flex items-center gap-1.5">
            <Dot tone="gray" />
            <span className="text-[10px] font-black text-gray-500"
                  data-testid="live-console-ig-streaming">연결 대기 · API 승인 대기</span>
          </div>
        </div>
      </div>

      <div className="pt-1 flex items-center justify-between">
        <p className="text-[10px] text-gray-400 leading-relaxed">
          채널 연결·해제·자동감지 설정은{' '}
          <Link to="/seller/live-control" className="text-[#FF3B30] font-bold underline"
                data-testid="live-console-status-goto-center">
            LIVE Console 설정
          </Link>
          에서 관리해 주세요.
        </p>
      </div>
    </div>
  );
};

// ── Sidebar (Desktop only · minimal · LIVE 관련만) ─────────────────────────
// STEP LIVE-STUDIO-PC-UI-3 · PC-only Overlay Operations panel (시안 하단 운영 패널).
//   · 탭 5개 (화면 오버레이 / 공지·타이머 / 이미지·텍스트 / QR 코드 / 화면 프리셋).
//   · useLiveOperations canonical hook 재사용 (중복 action 금지).
//   · 활성 상품 (active_product) 미선택 상태에서 action 은 disable 처리 + 안내.
//   · 미구현 기능/프리셋 fabrication 금지 — 기존 preset ID 만 노출.
// STEP LIVE-STUDIO-PC-UI-5 · 프리셋 subtitle (시안 매칭 · 각 preset 의 성격을 한 줄로).
const OVERLAY_PRESETS = [
  { k: 'default',    label: '기본형',    hint: '상품명 + 가격' },
  { k: 'full',       label: '전체정보형', hint: '상품명 + 가격 + 배송안내' },
  { k: 'price',      label: '가격형',     hint: '가격 강조형' },
  { k: 'options',    label: '옵션형',     hint: '옵션 정보 표시' },
  { k: 'mini',       label: '미니형',     hint: '작은 배너형 (화면 가림 최소)' },
];
const OV_TABS = [
  { k: 'overlay', label: '화면 오버레이' },
  { k: 'notice',  label: '공지 / 타이머' },
  { k: 'image',   label: '이미지 / 텍스트' },
  { k: 'qr',      label: 'QR 코드' },
  { k: 'preset',  label: '화면 프리셋' },
];

// STEP LIVE-STUDIO-PRESET-PREVIEW-REAL-1 (2026-10-08) · 로컬 Overlay layer.
//   · iframe (white-screen) 사용 X · 썸네일과 동일한 레이아웃을 full-scale 로 노출.
//   · video 컨테이너 inside absolute positioning · pcAspect 에 따라 bottom spacing 조정.
//   · 데이터 fabrication 금지 — previewProduct (실 상품) + overlay 없으면 null 반환.
// STEP LIVE-SELF-OVERLAY-UI-1 · 공통 LivePresetOverlay 로 추출.
//   · 기존 Studio preview 와 자사라이브 소비자 Viewer 가 동일 렌더 정의를 공유.
//   · PcPresetOverlay 는 shared component 를 그대로 re-export (alias) — 기존 import
//     경로/anchors 전부 그대로 유지되고 중복 코드 0.
import LivePresetOverlay, { PRESET_POSITION_CLS } from '@/components/live/LivePresetOverlay';
const PcPresetOverlay = LivePresetOverlay;

// STEP LIVE-SELF-OVERLAY-UI-1 · 자사라이브 Overlay ON/OFF 토글 — 기존 seller/settings REUSE.
//   · 기본 OFF · 새로고침 복원 · PC·모바일 공통 (서버 저장) · LIVE 중 변경 가능.
//   · 저장 실패 시 UI 상태 롤백 — '성공' 표시 금지.
const SelfLiveOverlayToggle = ({ settingKey = 'self_live_overlay_enabled', title = '자사라이브 상품 Overlay',
  onText = '소비자 LIVE 영상 위에 선택한 프리셋으로 표시됩니다.', offText = '소비자 LIVE 영상 위에 상품 Overlay 를 표시하지 않습니다 (기본값).',
  testid = 'self-live-overlay-toggle' }) => {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/seller/settings');
        if (!alive) return;
        setEnabled(!!data?.[settingKey]);
      } catch { /* soft */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, []);
  const flip = async () => {
    if (busy) return;
    const next = !enabled;
    const prev = enabled;
    setBusy(true); setEnabled(next);
    try {
      await api.put('/seller/settings', { [settingKey]: next });
      toast.success(`${title} 표시 ${next ? 'ON' : 'OFF'}`);
    } catch (e) {
      setEnabled(prev);
      toast.error(e?.response?.data?.detail || '저장 실패 · 다시 시도해주세요.');
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#F7F8FA] border border-black/5 dark:bg-white/5 dark:border-white/10"
          data-testid={`${testid}-wrap`}>
      <div className="min-w-0">
        <p className="text-[12px] font-black text-black dark:text-white">{title}</p>
        <p className="text-[10px] text-gray-500 mt-0.5">{enabled ? onText : offText}</p>
      </div>
      <button type="button" onClick={flip} disabled={busy || !loaded}
               role="switch" aria-checked={enabled}
               className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors shrink-0 disabled:opacity-40 ${
                 enabled ? 'bg-[#FF3B30]' : 'bg-gray-300 dark:bg-white/20'
               }`}
               data-testid={testid}
               data-enabled={enabled ? 'true' : 'false'}>
        <span className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`} />
      </button>
    </div>
  );
};

const PcOverlayOpsPanel = ({ scheduleId, liveCtx, isLive, openNoticeEditor, draftPreset, setDraftPreset, isTestMode = false, setLiveCtx = null, onEditLayout = null,
                             status = null, presetCtx = null, presetApply = null, presetStart, connected = false, onNoticeSelect = null }) => {
  const [tab, setTab] = useState('overlay');
  const [busy, setBusy] = useState(null);
  // STEP PRESET-PREVIEW-REAL-1 · draftPreset 은 parent 로 승격됨 (localStorage 저장 포함).
  const liveOps = useLiveOperations(scheduleId);
  // PC-STUDIO-SYNC-RESPONSIVE-FIX-1 · TEST 는 공유 세션 상태만 변경 (실제 방송 API 미호출).
  const _tm = (fn) => { setLiveCtx((c) => (c ? fn(c) : c)); return Promise.resolve(); };
  const _disp = (c, patch) => ({ ...c, active_product_display: { ...(c.active_product_display || {}), ...patch } });
  const ops = isTestMode && setLiveCtx ? {
    applyPreset: (_pid, k) => _tm((c) => _disp(c, { preset: k, mode: 'visible' })),
    setPosition: (_pid, _p, k) => _tm((c) => _disp(c, { position: k })),
    hideOverlay: () => _tm((c) => _disp(c, { mode: 'hidden' })),
    showQr: (_pid, s) => _tm((c) => ({ ...c, active_product_qr: { visible: true, duration_seconds: s } })),
    hideQr: () => _tm((c) => ({ ...c, active_product_qr: { visible: false } })),
  } : liveOps;
  const active = liveCtx?.active_product || null;
  // STEP LIVE-STUDIO-PC-UI-4 · 썸네일 미리보기용 product fallback.
  //   active 가 없으면 live_products 첫 상품으로 미리보기 (실 데이터 only · fabrication 금지).
  const previewProduct = active || (Array.isArray(liveCtx?.live_products) ? liveCtx.live_products[0] : null) || null;
  const display = liveCtx?.active_product_display || {};
  const curPreset = display?.preset || 'default';
  const curPos = display?.position || 'bottom';
  const overlayHidden = display?.mode === 'hidden';
  const qrOn = !!liveCtx?.active_product_qr?.visible;
  const disabledMsg = !isLive ? 'LIVE 시작 후 적용 가능 · 미리보기만 확인 가능'
    : !scheduleId ? '편성표 방송에서만 사용 가능'
    : !active?.product_id ? '먼저 상품을 소개해 주세요'
    : null;
  const wrap = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(e?.response?.data?.detail || '적용 실패'); }
    finally { setBusy(null); }
  };

  return (
    <section className="hidden lg:flex flex-col shrink-0 lg:max-h-[42vh] min-h-0 border-t border-[#E5E7EB] dark:border-white/10 bg-white dark:bg-[#0F0F0F]"
             data-testid="pc-overlay-ops-panel">
      {status && <PcStatusStrip {...status} />}
      {/* 탭 바 — 시안 매칭 (red underline) */}
      <div className="flex items-stretch gap-5 px-5 pt-3 border-b border-[#E5E7EB] dark:border-white/10"
           data-testid="pc-overlay-ops-tabs">
        {OV_TABS.map((t) => {
          const isActive = tab === t.k;
          return (
            <button key={t.k} type="button" onClick={() => setTab(t.k)}
                    aria-pressed={isActive}
                    className={`pb-2 text-[13px] font-black transition border-b-2 ${
                      isActive
                        ? 'text-[#FF3B30] border-[#FF3B30]'
                        : 'text-gray-500 border-transparent hover:text-black dark:hover:text-white'
                    }`}
                    data-testid={`pc-overlay-ops-tab-${t.k}`}>
              {t.label}
            </button>
          );
        })}
        {disabledMsg && (
          <span className="ml-auto self-center text-[10px] font-bold text-gray-400"
                data-testid="pc-overlay-ops-disabled-msg">
            {disabledMsg}
          </span>
        )}
      </div>

      <div className="p-4 overflow-y-auto min-h-0 overscroll-contain" data-testid={`pc-overlay-ops-content-${tab}`}>
        {/* 1. 화면 오버레이 : preset + position + hide */}
        {tab === 'overlay' && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="pc-ov-overlay">
            <div className="inline-flex items-center gap-1.5">
              <span className="text-[11px] font-black text-gray-500 mr-1">표시 형태</span>
              {[['full', '풀'], ['default', '이미지'], ['options', '옵션']].map(([k, l]) => (
                <button key={k} type="button"
                        onClick={() => wrap('preset', () => ops.applyPreset(active?.product_id, k), '표시 형태 변경')}
                        disabled={!!disabledMsg || !!busy}
                        className={`min-h-[32px] px-3 rounded-full text-[11px] font-black border transition disabled:opacity-40 ${
                          !overlayHidden && curPreset === k
                            ? 'bg-black text-white border-black dark:bg-white dark:text-black'
                            : 'border-[#E5E7EB] text-gray-700 hover:border-[#FF3B30]'
                        }`}
                        data-testid={`pc-ov-preset-${k}`}>
                  {l}
                </button>
              ))}
            </div>
            <div className="inline-flex items-center gap-1.5">
              <span className="text-[11px] font-black text-gray-500 mr-1">위치</span>
              {[['top', '상단'], ['middle', '중앙'], ['bottom', '하단']].map(([k, l]) => (
                <button key={k} type="button"
                        onClick={() => wrap('position', () => ops.setPosition(active?.product_id, curPreset, k), `위치 · ${l}`)}
                        disabled={!!disabledMsg || !!busy}
                        className={`min-h-[32px] px-3 rounded-full text-[11px] font-black border transition disabled:opacity-40 ${
                          curPos === k
                            ? 'bg-black text-white border-black dark:bg-white dark:text-black'
                            : 'border-[#E5E7EB] text-gray-700 hover:border-[#FF3B30]'
                        }`}
                        data-testid={`pc-ov-position-${k}`}>
                  {l}
                </button>
              ))}
            </div>
            <button type="button"
                    onClick={() => wrap('hide', () => ops.hideOverlay(active?.product_id), overlayHidden ? null : 'Overlay 내림')}
                    disabled={!!disabledMsg || !!busy || overlayHidden}
                    className="ml-auto min-h-[32px] px-3 rounded-full text-[11px] font-black border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40 inline-flex items-center gap-1"
                    data-testid="pc-ov-hide">
              <EyeOff size={11} /> {overlayHidden ? 'Overlay 내려짐' : 'Overlay 내리기'}
            </button>
          </div>
        )}

        {/* 2. 공지 / 타이머 */}
        {tab === 'notice' && (
          <div className="space-y-3" data-testid="pc-ov-notice">
            <button type="button"
                    onClick={openNoticeEditor}
                    disabled={!isLive}
                    className="w-full min-h-[40px] px-4 rounded-lg bg-[#FFF4F1] hover:bg-[#FFEAE6] border border-[#FFD6CE] text-[#17181C] text-[12px] font-black inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid="pc-ov-notice-open">
              <Megaphone size={13} className="text-[#FF3B30]" />
              <span className="flex-1 text-left truncate">
                {liveCtx?.live_notice?.text ? `공지중 · ${liveCtx.live_notice.text}` : '방송 공지 작성'}
              </span>
              <span className="text-[10px] text-[#FF4F3D] shrink-0">{liveCtx?.live_notice?.text ? '수정' : '작성'}</span>
            </button>
            {isLive && onNoticeSelect && (
              <StudioNoticeLibraryList activeId={liveCtx?.live_notice?.notice_id || null} current={liveCtx?.live_notice?.text || null}
                                       onSelect={onNoticeSelect} onClear={() => onNoticeSelect(null)} />
            )}
            {scheduleId && isLive && (
              <OverlayTimerControl scheduleId={scheduleId} timer={liveCtx?.timer} compact
                                    disabled={!!busy}
                                    onLocalAction={isTestMode && setLiveCtx ? _testTimerAction(setLiveCtx) : undefined} />
            )}
            {scheduleId && isLive && presetCtx && presetApply && (
              <LivePresetPanel ctx={presetCtx} apply={presetApply} canStart={isLive} collapsible onStartTimer={presetStart} connected={connected} />
            )}
            {!isLive && (
              <p className="text-[11px] text-gray-400">LIVE 시작 후 공지·타이머를 운영할 수 있어요.</p>
            )}
          </div>
        )}

        {/* 3. 이미지 / 텍스트 — 기존 notice editor 가 이미지 섹션 포함 → 동일 editor 재사용 */}
        {tab === 'image' && (
          <div className="space-y-2" data-testid="pc-ov-image">
            <p className="text-[11px] text-gray-500 leading-relaxed">
              공지 이미지 / 텍스트는 '방송 공지' 편집기에서 함께 관리합니다. (기존 editor REUSE)
            </p>
            <button type="button" onClick={openNoticeEditor} disabled={!isLive}
                    className="min-h-[36px] px-4 rounded-lg bg-black text-white dark:bg-white dark:text-black text-[12px] font-black disabled:opacity-40"
                    data-testid="pc-ov-image-open">
              이미지 / 텍스트 편집
            </button>
          </div>
        )}

        {/* 4. QR */}
        {tab === 'qr' && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="pc-ov-qr">
            <p className="text-[11px] text-gray-500 flex-1 min-w-0">
              현재 소개 상품 상세로 이동하는 QR 을 방송 화면에 10초간 표시합니다.
            </p>
            <button type="button"
                    onClick={() => wrap('qr_show', () => ops.showQr(active?.product_id, 10), 'QR 표시 (10초)')}
                    disabled={!!disabledMsg || !!busy}
                    className="min-h-[36px] px-4 rounded-lg bg-black text-white dark:bg-white dark:text-black text-[12px] font-black disabled:opacity-40"
                    data-testid="pc-ov-qr-show">
              QR 표시
            </button>
            <button type="button"
                    onClick={() => wrap('qr_hide', () => ops.hideQr(active?.product_id), 'QR 내림')}
                    disabled={!!disabledMsg || !!busy || !qrOn}
                    className="min-h-[36px] px-4 rounded-lg border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 text-[12px] font-black disabled:opacity-40"
                    data-testid="pc-ov-qr-hide">
              QR 내리기
            </button>
            {qrOn && (
              <span className="text-[10px] font-black text-emerald-600" data-testid="pc-ov-qr-active">
                ● 표시 중
              </span>
            )}
          </div>
        )}

        {/* 5. 화면 프리셋 — PC·모바일 공통 OverlayPresetPicker (동일 적용 로직) */}
        {tab === 'preset' && (
          <OverlayPresetPicker scheduleId={scheduleId} liveCtx={liveCtx} isLive={isLive}
                               draftPreset={draftPreset} setDraftPreset={setDraftPreset}
                               isTestMode={isTestMode} setLiveCtx={setLiveCtx} variant="pc" onEditLayout={onEditLayout} />
        )}
      </div>
    </section>
  );
};

// STEP STUDIO-MOBILE-TEST-PRESET-ACCESS-1 · PC 프리셋 탭 내용을 공통 컴포넌트로 추출 (모바일 설정 sheet 재사용).
//   · LIVE 전: draft 저장 · LIVE 중: 기존 ops.applyPreset · 테스트 모드 LIVE: 로컬 ctx 만 변경 (WRITE 0).
const OverlayPresetPicker = ({ scheduleId, liveCtx, isLive, draftPreset, setDraftPreset, isTestMode = false, setLiveCtx = null, variant = 'pc', onEditLayout = null, hideSelfToggle = false }) => {
  const ops = useLiveOperations(scheduleId);
  const [busy, setBusy] = useState(false);
  const mobile = variant === 'mobile';
  const tid = mobile ? 'm-ov' : 'pc-ov';
  const active = liveCtx?.active_product || null;
  const previewProduct = active || (Array.isArray(liveCtx?.live_products) ? liveCtx.live_products[0] : null) || null;
  const display = liveCtx?.active_product_display || {};
  const overlayHidden = display?.mode === 'hidden';
  // Effective selection: LIVE 중이면 서버 SoT, 아니면 local draft (없으면 null).
  const selectedPreset = isLive && !overlayHidden ? (display?.preset || 'default') : draftPreset;
  const onPresetClick = async (presetKey) => {
    if (!isLive || !scheduleId || !active?.product_id) {
      setDraftPreset(presetKey);
      toast.info(isTestMode ? '프리셋 저장 완료 (이 브라우저) · 테스트 LIVE 미리보기에 자동 적용돼요.' : '프리셋 저장 완료 · 미리보기에 바로 반영돼요.');
      return;
    }
    if (isTestMode) {
      setDraftPreset(presetKey);
      setLiveCtx?.((c) => ({ ...(c || {}), active_product_display: { ...((c || {}).active_product_display || {}), preset: presetKey, mode: 'visible' } }));
      toast.success('프리셋 적용 완료 (테스트 · 로컬)');
      return;
    }
    setBusy(true);
    try { await ops.applyPreset(active.product_id, presetKey); toast.success('프리셋 적용 완료'); }
    catch (e) { toast.error(e?.response?.data?.detail || '적용 실패'); }
    finally { setBusy(false); }
  };
  return (
          <div className="space-y-3" data-testid={`${tid}-presets-wrap`}>
            {/* STEP LIVE-SELF-OVERLAY-UI-1 · §1 자사라이브 Overlay ON/OFF 토글.
                기존 /seller/settings REUSE · PC·모바일 공유 · LIVE 중 변경 가능. */}
            {!hideSelfToggle && <SelfLiveOverlayToggle />}
            {/* STEP LIVE-STUDIO-PC-UI-5 · 상단 안내 배너 (시안 매칭 · info/blue tone).
                자동 적용 contract 미존재 (GAP) → 수동 적용 안내. */}
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#EEF4FF] border border-[#BBD4FF] text-[#1E429F]"
                 data-testid={`${tid}-preset-info-banner`}>
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              <p className="text-[11px] font-black leading-snug">
                방송 전에 프리셋을 설정하고 저장할 수 있어요. 테스트 미리보기에는 자동 적용되며 실제 송출 적용은 별도 연결 예정입니다.
              </p>
            </div>
            {!previewProduct && (
              <p className="text-[11px] font-bold text-gray-500 bg-[#F7F8FA] rounded-lg px-3 py-2"
                 data-testid={`${tid}-presets-empty-hint`}>
                미리보기는 방송 상품이 1개 이상 있을 때 표시돼요. 상품을 먼저 등록해 주세요.
              </p>
            )}
            <div className={mobile ? 'flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1 -mx-1 px-1' : 'grid grid-cols-5 gap-3'}
                 data-testid={`${tid}-presets`}>
              {OVERLAY_PRESETS.map((p) => {
                const isActive = selectedPreset === p.k;
                // STEP OVERLAY-PRESET-UI-FIX-1 · 공통 LivePresetOverlay REUSE (size="sm").
                //   · Studio 미리보기 카드 5종이 소비자 Viewer / 전면 overlay 와 동일 렌더
                //     정의 사용 — 중복 구현 금지 · 상품 데이터만 그대로 전달.
                //   · 상품 변경 시 previewProduct 가 바뀌면 5 카드 전부 자동 갱신.
                //   · 선택 상태(ring/border)는 button 레벨에서 유지 (프리셋 저장 기능 보존).
                return (
                  <button key={p.k} type="button"
                          onClick={() => onPresetClick(p.k)}
                          disabled={!!busy}
                          className={`relative text-left rounded-xl border-2 overflow-hidden transition disabled:opacity-40 ${mobile ? 'w-[128px] shrink-0 snap-start' : ''} ${
                            isActive
                              ? 'border-[#FF3B30] shadow-sm ring-2 ring-[#FF3B30]/20'
                              : 'border-[#E5E7EB] hover:border-[#FF3B30]/50'
                          }`}
                          aria-pressed={isActive}
                          data-testid={`${tid}-preset-card-${p.k}`}
                          data-preset-selected={isActive ? '1' : '0'}>
                    <div className="aspect-video bg-gradient-to-br from-[#F7F8FA] to-[#EEF0F3] relative overflow-hidden">
                      {previewProduct?.image ? (
                        <SafeImg src={previewProduct.image} kind="product" alt=""
                                 className="absolute inset-0 w-full h-full object-cover opacity-70" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-[9px] font-black text-gray-400">
                          상품 이미지 없음
                        </div>
                      )}
                      {previewProduct && (
                        <LivePresetOverlay preset={p.k} product={previewProduct}
                                           position="bottom" size="sm" />
                      )}
                    </div>
                    <div className="px-2 py-1.5 bg-white text-center">
                      <p className={`text-[12px] font-black ${isActive ? 'text-[#FF3B30]' : 'text-black dark:text-white'}`}>
                        {p.label}
                      </p>
                      <p className="text-[9px] font-bold text-gray-500 mt-0.5 truncate" data-testid={`${tid}-preset-hint-${p.k}`}>
                        {p.hint}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            {typeof onEditLayout === 'function' && (
              <button type="button" onClick={onEditLayout}
                      className="w-full min-h-[40px] px-3 rounded-xl border-2 border-dashed border-[#FF3B30]/60 text-[#FF3B30] text-xs font-black inline-flex items-center justify-center gap-1.5"
                      data-testid={`${tid}-layout-edit`}>
                <Move size={13} /> Overlay 배치 편집 (위치·크기 · 가로형/세로형)
              </button>
            )}
            {/* STEP LIVE-STUDIO-PC-UI-4 · draft 선택 상태 안내 (가짜 저장 금지). */}
            {draftPreset && !isLive && (
              <p className="text-[10px] text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-1.5 inline-flex items-center gap-1"
                 data-testid={`${tid}-preset-draft-hint`}>
                <AlertTriangle size={10} />
                '{OVERLAY_PRESETS.find((p) => p.k === draftPreset)?.label}' 저장됨 · 미리보기·테스트 LIVE 자동 적용 (실제 송출 적용은 별도 연결 예정)
              </p>
            )}
          </div>
  );
};

//   · Items mapped to existing tabs where available (방송하기/상품관리/댓글-주문/방송통계=방송상태).
//   · 미구현(화면구성/공지타이머/설정) 은 가짜 완료 표시 금지 — disabled 상태로 노출.
//   · Bottom: 방송 가이드 card (static · informational only).
const PC_SIDE_ITEMS = [
  { key: 'live',       label: '방송하기',       icon: Radio,          tab: 'live'       },
  { key: 'products',   label: '상품관리',       icon: Package,        tab: 'products'   },
  { key: 'overlay',    label: '화면구성',       icon: Layers,         tab: null,         sub: '오버레이' },
  { key: 'notice',     label: '공지 / 타이머',  icon: Megaphone,      tab: null         },
  { key: 'comments',   label: '댓글 / 주문관리', icon: MessageCircle,  tab: 'comments'   },
  { key: 'stats',      label: '방송통계',       icon: BarChart3,      tab: 'status'     },
  { key: 'settings',   label: '설정',           icon: Settings,       tab: null         },
];

const SIDE_ITEMS = [
  { key: 'live', label: '방송', icon: Radio },
  { key: 'products', label: '상품', icon: Package },
  { key: 'comments', label: '댓글·주문', icon: MessageCircle },
  { key: 'status', label: '방송상태', icon: Wifi },
];

// STEP LIVE-STUDIO-PC-UI-2 · PC Studio 좌측 메뉴.  시안 매칭 (아이콘 + 라벨 세로 리스트
// + 하단 '방송 가이드' 카드).  미구현 메뉴는 눌러도 아무 변화 없는 placeholder 가 되지
// 않도록 disabled 처리 (가짜 완료 금지).
const ConsoleSidebar = ({ activeTab, onTab, onOpenNotice }) => (
  <aside className="hidden lg:flex flex-col w-[180px] shrink-0 border-r border-[#E5E7EB] dark:border-white/10 bg-white dark:bg-[#0F0F0F]"
          data-testid="live-console-sidebar">
    <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="live-console-sidebar-nav">
      {PC_SIDE_ITEMS.map((s) => {
        const isActive = s.tab && activeTab === s.tab;
        const disabled = !s.tab && !(s.key === 'notice' && typeof onOpenNotice === 'function');
        const handle = () => {
          if (s.tab) { onTab(s.tab); return; }
          if (s.key === 'notice' && typeof onOpenNotice === 'function') { onOpenNotice(); return; }
        };
        return (
          <button key={s.key} type="button" onClick={handle} disabled={disabled}
                  data-testid={`live-console-sidebar-${s.key}`}
                  aria-pressed={isActive}
                  aria-disabled={disabled}
                  className={`w-full inline-flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-[12.5px] font-black text-left transition ${
                    isActive
                      ? 'bg-[#FF3B30] text-white shadow-sm'
                      : disabled
                        ? 'text-gray-300 dark:text-white/20 cursor-not-allowed'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-[#F7F8FA] dark:hover:bg-white/10'
                  }`}>
            <s.icon size={15} className="shrink-0" />
            <span className="truncate">{s.label}{s.sub && (<span className="ml-1 text-[10px] font-bold opacity-70">({s.sub})</span>)}</span>
          </button>
        );
      })}
    </nav>
    <div className="mx-3 mb-3 p-3 rounded-xl bg-gradient-to-br from-[#FFF4F1] to-[#FFEAE6] border border-[#FFD6CE]"
         data-testid="live-console-sidebar-guide">
      <div className="inline-flex items-center gap-1 mb-1">
        <Sparkles size={13} className="text-[#FF3B30]" />
        <p className="text-[11px] font-black text-[#17181C]">방송 가이드</p>
      </div>
      <p className="text-[10px] leading-relaxed text-gray-600">
        처음이신가요?<br />단계별로 안내해드려요.
      </p>
    </div>
    <Link to="/seller/dashboard"
          className="mx-3 mb-3 px-3 py-2 rounded-lg text-[11px] font-bold text-gray-500 dark:text-gray-400 hover:bg-[#F7F8FA] dark:hover:bg-white/10 inline-flex items-center gap-1.5 justify-center"
          data-testid="live-console-back-to-center">
      <ArrowLeft size={12} /> 판매자센터로
    </Link>
  </aside>
);

// STEP LIVE-STUDIO-PC-UI-2 · PC Studio 상단 전체폭 TopBar (hidden lg:flex).
//   · LIVEMOA 스튜디오 + LIVE pill (실 상태만 · 가짜 LIVE 금지).
//   · 중앙: 외부 플랫폼 연결 상태 pill (channels SoT · 실제 연결된 플랫폼만 "연결됨").
//   · 우측: 설정 (placeholder · 미구현) · 방송 나가기 (판매자센터로 복귀).
const PcTopBar = ({ channels, isLive, phase, onLeave, streamingSessionId = null }) => {
  const yt = channels?.youtube?.connected;
  const ig = channels?.instagram?.connected;
  const tt = channels?.tiktok?.connected && channels?.tiktok?.production_ready;
  const PlatformPill = ({ label, connected, color }) => (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-black border transition ${
      connected
        ? 'bg-white dark:bg-[#141414] border-[#E5E7EB] dark:border-white/10 text-black dark:text-white shadow-sm'
        : 'bg-[#F7F8FA] dark:bg-white/5 border-dashed border-[#E5E7EB] dark:border-white/10 text-gray-400'
    }`}
          data-testid={`pc-top-platform-${label.toLowerCase()}`}
          data-connected={connected ? '1' : '0'}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: connected ? color : '#D1D5DB' }} />
      <span>{label}</span>
      <span className={`text-[10px] font-bold ${connected ? 'text-emerald-600' : 'text-gray-400'}`}>
        {connected ? '연결됨' : '미연결'}
      </span>
    </span>
  );
  return (
    <header className="hidden lg:flex items-center gap-3 h-14 shrink-0 px-5 bg-white dark:bg-[#0F0F0F] border-b border-[#E5E7EB] dark:border-white/10"
            data-testid="pc-top-bar">
      <Link to="/seller/dashboard" className="inline-flex items-center gap-2 shrink-0"
            data-testid="pc-top-logo">
        <span className="text-[18px] font-black tracking-tight text-[#FF3B30]">LIVE</span>
        <span className="text-[18px] font-black tracking-tight text-black dark:text-white">MOA</span>
        <span className="ml-2 px-2 py-0.5 rounded-md text-[11px] font-black text-gray-600 dark:text-gray-300 bg-[#F7F8FA] dark:bg-white/10">스튜디오</span>
        {isLive ? (
          <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black"
                data-testid="pc-top-live-badge">
            <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> LIVE
          </span>
        ) : phase === 'ended' ? (
          <span className="ml-1 px-2 py-0.5 rounded-full bg-gray-400 text-white text-[10px] font-black">종료</span>
        ) : (
          <span className="ml-1 px-2 py-0.5 rounded-full bg-[#F7F8FA] dark:bg-white/10 text-gray-500 dark:text-gray-400 text-[10px] font-black">준비 중</span>
        )}
      </Link>
      <div className="flex-1 flex items-center justify-center gap-2 min-w-0" data-testid="pc-top-platforms">
        <PlatformPill label="YouTube"   connected={!!yt} color="#FF0000" />
        <PlatformPill label="Instagram" connected={!!ig} color="#E1306C" />
        <PlatformPill label="TikTok"    connected={!!tt} color="#000000" />
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {/* STEP MINI-CONSOLE-STUDIO-UX-1 · PC header 우측 상단 compact MiniOrder Badge.
            · streamingSessionId 없으면 return null (잘못된 링크 생성 금지).
            · 영상 가림 최소 — 작은 pill 3개만 inline.
            · STEP STUDIO-PCTOPBAR-RUNTIME-FIX-1 · session 변수는 상위 Inner 의 state
              이므로 PcTopBar scope 밖. streamingSessionId prop 으로 전달받아 사용. */}
        <MiniOrderStudioBadge streamingSessionId={streamingSessionId} />
        <button type="button"
                disabled
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E7EB] dark:border-white/10 text-xs font-black text-gray-400 dark:text-white/40 cursor-not-allowed"
                data-testid="pc-top-settings"
                aria-disabled>
          <Settings size={13} /> 설정
        </button>
        <button type="button" onClick={onLeave}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F7F8FA] hover:bg-[#EEF0F3] dark:bg-white/10 dark:hover:bg-white/20 text-gray-700 dark:text-white text-xs font-black transition"
                data-testid="pc-top-leave">
          <LogOut size={13} /> 방송 나가기
        </button>
      </div>
    </header>
  );
};

// ── STEP 8-E · Broadcast Selector (CTA Resolver + Identity Lock 진입점) ──
// 판매자가 [방송하기] CTA (?sid 없음) 로 진입했을 때, 어떤 방송을 준비할지
// 명시적으로 선택하는 화면. 임의로 seller-wide 최신 세션을 잡지 않고 아래
// 우선순위대로 resolve:
//   P1) 실 LIVE (status=라이브 · streaming_session_id 존재) 방송 존재 → 그 방송
//   P2) 현재 시간 grace(-30/+30분) 내 예정방송 정확히 1개 → 그 방송
//   P3) 후보 2+ 또는 없음 → 판매자가 명시적으로 선택 (임의 선택 금지)
//
// STEP STUDIO-BROADCAST-SELECTOR-FIX-1 · 조회 범위 확대
//   · 기존: /seller/broadcasts/today → 오늘 KST 안에 예정된 방송만 반환.
//     → 내일/미래 예정 방송이 '방송하기' 선택화면에서 사라지는 혼란 발생.
//   · 수정: 기존 /seller/broadcasts (전체 조회 · linked_product_count 포함)
//     REUSE 하고 client-side 로 '선택 가능' (라이브 + 오늘·미래 예정) 필터.
//   · /today endpoint 는 변경하지 않음 (다른 사용처 다수 · LOCK).
//   · 신규 API/DB field 0 · 선택만으로는 송출 호출 0.
const _kstNow = () => new Date();  // client 시간 그대로 사용 · grace 는 30분 대칭 window
const _CLOSED_STATUS = new Set(['종료', 'ended', '취소', 'cancelled', 'canceled']);
const _isLiveStatus = (s) =>
  s === '라이브' || s === 'live' || s === 'LIVE' || s === 'ongoing' || s === '진행중';
const _isLiveNow = (b) => _isLiveStatus(b?.status) && !!b?.streaming_session_id;
const _isClosed = (b) => {
  if (!b?.status) return false;
  const low = String(b.status).toLowerCase();
  return _CLOSED_STATUS.has(b.status) || _CLOSED_STATUS.has(low);
};
// Asia/Seoul 자정 (UTC 로 환산한 ms).  오늘 KST 00:00 이상의 scheduled_at 은
// '오늘 또는 미래' 로 간주 — 늦게 시작하는 오늘 예정 방송도 포함.
const _startOfTodayKstMs = () => {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 3600 * 1000);
  kst.setUTCHours(0, 0, 0, 0);
  return kst.getTime() - 9 * 3600 * 1000;
};
const _filterSelectable = (list) => {
  const todayMs = _startOfTodayKstMs();
  return (Array.isArray(list) ? list : []).filter((b) => {
    if (!b || b.is_deleted) return false;
    if (_isClosed(b)) return false;
    if (_isLiveNow(b)) return true;
    const t = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
    return t > 0 && t >= todayMs;
  }).sort((a, b) => {
    // LIVE 우선 → scheduled_at ASC
    const la = _isLiveNow(a), lb = _isLiveNow(b);
    if (la && !lb) return -1;
    if (!la && lb) return 1;
    const ta = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0;
    const tb = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
    return ta - tb;
  });
};
const _pickResolution = (list) => {
  // returns { kind: 'live'|'auto'|'select', chosen?: schedule, candidates?: [] }
  const safe = Array.isArray(list) ? list : [];
  const nowMs = _kstNow().getTime();
  const graceMs = 30 * 60 * 1000;
  // P1 · 실 LIVE
  const liveOne = safe.find((s) => _isLiveNow(s));
  if (liveOne) return { kind: 'live', chosen: liveOne };
  // P2 · 시간 grace 내 예정 정확히 1개
  const inWindow = safe.filter((s) => {
    if (s.status && s.status !== '예정') return false;
    const t = s.scheduled_at ? new Date(s.scheduled_at).getTime() : 0;
    return t && Math.abs(t - nowMs) <= graceMs;
  });
  if (inWindow.length === 1) return { kind: 'auto', chosen: inWindow[0] };
  // P3 · 명시 선택
  return { kind: 'select', candidates: safe };
};

const BroadcastSelector = ({ onSelect, direct = false }) => {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // STEP STUDIO-BROADCAST-SELECTOR-FIX-1 · /today → /seller/broadcasts (REUSE)
        //   · /seller/broadcasts 는 (판매자 소유 전부 · linked_product_count 포함)
        //   · client-side filter 로 '라이브 + 오늘·미래 예정' 만 노출.
        //   · 종료 · 취소 · 삭제 는 _filterSelectable 에서 제외.
        const { data } = await api.get('/seller/broadcasts');
        if (!alive) return;
        const raw = Array.isArray(data) ? data : (data?.items || []);
        const arr = _filterSelectable(raw);
        setList(arr);
        // Auto-resolve P1 · P2 without user click
        const r = _pickResolution(arr);
        if (r.kind === 'live' || r.kind === 'auto') {
          onSelect(r.chosen.schedule_id, r.kind);
        }
      } catch { if (alive) setList([]); }
    })();
    return () => { alive = false; };
  }, [onSelect]);

  if (list === null) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-white dark:bg-[#0A0A0A]"
            data-testid="broadcast-selector-loading">
        <p className="text-sm text-gray-500 inline-flex items-center gap-2">
          <RefreshCw size={14} className="animate-spin" /> 현재 방송을 확인하고 있어요…
        </p>
      </div>
    );
  }

  // STEP LIVE-CONSOLE-ENTRY-1 · '라이브' 메뉴 직접 진입 (?direct=1) 전용 헤더.
  // "어떤 방송을 준비할까요?" 로 자동 redirect 하지 않고 LIVE Console entry shell 로서
  // 표시한다. 기존 "방송하기" (direct=false) 흐름은 100% 유지.
  const title = direct ? 'LIVE Console' : '어떤 방송을 준비할까요?';
  const subtitle = direct
    ? '방송을 등록하거나 선택해 LIVE Console 로 진입하세요.'
    : '현재 시간대에 시작할 수 있는 방송이 여러 개거나 아직 시간이 아니에요. 진행할 방송을 명시적으로 선택해 주세요.';

  return (
    <div className="h-[100dvh] overflow-y-auto bg-white dark:bg-[#0A0A0A]"
          data-testid={direct ? "live-console-direct-shell" : "broadcast-selector"}>
      <div className="max-w-lg mx-auto px-4 py-8">
        <button type="button"
                onClick={() => navigate(direct ? '/seller/dashboard' : '/seller/dashboard?section=broadcasts')}
                className="text-xs text-gray-500 hover:text-[#FF3B30] inline-flex items-center gap-1 mb-4"
                data-testid="broadcast-selector-back">
          <ArrowLeft size={12} /> {direct ? '판매자센터로' : '판매자센터 방송관리로'}
        </button>
        <h1 className="text-2xl font-black text-black dark:text-white mb-1 inline-flex items-center gap-2">
          {direct && <Radio size={20} className="text-[#FF3B30]" />}
          {title}
        </h1>
        <p className="text-xs text-gray-500 mb-6">{subtitle}</p>

        {/* STUDIO-MOBILE-DIRECT-TEST-ENTRY-1 · 방송 예약 없이 기존 TEST 모드(?testmode=1 · 판매자별 공유 세션) 진입 */}
        {process.env.REACT_APP_STUDIO_TEST_ENTRY === '1' && (
          <button type="button" onClick={() => navigate('/seller/live-console?testmode=1')}
                  className="w-full mb-4 min-h-[44px] px-3 rounded-full border border-amber-400 bg-amber-50 text-amber-700 text-xs font-black inline-flex items-center justify-center gap-1.5"
                  data-testid="broadcast-selector-test-entry">
            <FlaskConical size={14} /> 방송 테스트 (송출 없음)
          </button>
        )}

        {list.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-black/10 dark:border-white/10 p-8 text-center"
                data-testid="broadcast-selector-empty">
            <Radio size={32} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-black text-black dark:text-white mb-1">현재 준비된 방송이 없습니다.</p>
            <p className="text-xs text-gray-500 mb-4">
              방송을 등록한 후 LIVE 를 시작할 수 있어요.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
              {/* STEP LIVE-NAV-P1 · 판매자센터 HOME 이 아닌 방송관리 섹션으로 이동 (기존 SellerDashboardV2 section=broadcasts REUSE) */}
              <button type="button" onClick={() => navigate('/seller/dashboard?section=broadcasts')}
                      className="px-4 py-2 rounded-full bg-[#FF3B30] text-white text-sm font-bold"
                      data-testid="broadcast-selector-goto-broadcasts">
                방송 등록하기
              </button>
              {/* STEP LIVE-STUDIO-CONSOLE-SPLIT-1 · destination 을 PC Studio 가 아닌
                  채널 연결 전용 route 로 변경.  Camera/Mic/상품/Overlay 가 열리지 않도록. */}
              <button type="button" onClick={() => navigate('/seller/channels')}
                      className="px-4 py-2 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold text-gray-700 dark:text-gray-200"
                      data-testid="broadcast-selector-goto-live-control">
                플랫폼 연결상태 확인
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {list.map((s) => {
            const isLive = _isLiveNow(s);
            // STEP STUDIO-BROADCAST-SELECTOR-FIX-1 · 상품 수 = broadcast_products 집계
            // (linked_product_count) 를 1순위. legacy connected_products[] 는 fallback.
            // /seller/broadcasts 응답이 linked_product_count 를 반드시 포함하므로 선택
            // 카드와 방송관리의 '상품 N개' 가 동일한 소스를 공유 (미니주문과 혼동 X).
            const productCount = (typeof s.linked_product_count === 'number'
              ? s.linked_product_count
              : (Array.isArray(s.connected_products) ? s.connected_products.length : 0));
            const t = s.scheduled_at ? new Date(s.scheduled_at) : null;
            // STEP STUDIO-BROADCAST-SELECTOR-FIX-1 · Asia/Seoul 기준 표시.
            //   · 오늘 방송 → HH:mm
            //   · 미래 방송 → M월 d일 (화) HH:mm (KST)
            //   · toLocaleString timeZone 옵션으로 명시적 KST 환산.
            const todayMs = _startOfTodayKstMs();
            const tomorrowMs = todayMs + 24 * 3600 * 1000;
            const isTodayBc = t && t.getTime() >= todayMs && t.getTime() < tomorrowMs;
            const kstOpts = { timeZone: 'Asia/Seoul' };
            const timeText = t
              ? (isTodayBc
                  ? t.toLocaleTimeString('ko-KR', { ...kstOpts, hour: '2-digit', minute: '2-digit', hour12: false })
                  : t.toLocaleString('ko-KR', { ...kstOpts, month: 'numeric', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false }))
              : '—';
            return (
              <button key={s.schedule_id} type="button"
                      onClick={() => { setBusy(s.schedule_id); onSelect(s.schedule_id, 'manual'); }}
                      disabled={busy === s.schedule_id}
                      className={`w-full text-left rounded-2xl border p-3 flex items-center gap-3 hover:border-[#FF3B30] transition ${
                        isLive ? 'border-[#FF3B30] bg-[#FF3B30]/[0.03]' : 'border-black/10 dark:border-white/10'
                      } ${busy === s.schedule_id ? 'opacity-60' : ''}`}
                      data-testid={`broadcast-selector-item-${s.schedule_id}`}>
                <div className="w-14 h-14 rounded-lg bg-black/[0.06] dark:bg-white/10 flex items-center justify-center shrink-0">
                  {isLive ? <Radio size={20} className="text-[#FF3B30]" /> : <Clock size={20} className="text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {isLive && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black">
                        <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> LIVE
                      </span>
                    )}
                    <span className="text-[11px] font-black text-gray-500">{timeText}</span>
                  </div>
                  <p className="text-sm font-black truncate text-black dark:text-white">{s.title || '무제 방송'}</p>
                  <p className="text-[11px] text-gray-500">상품 {productCount}개 · schedule_id: {s.schedule_id.slice(0, 22)}…</p>
                </div>
                <span className="text-[11px] font-black text-[#FF3B30] inline-flex items-center gap-0.5 shrink-0"
                       data-testid={`broadcast-selector-prepare-${s.schedule_id}`}>
                  {isLive ? '재접속' : '준비하기'} <ArrowRight size={12} />
                </span>
              </button>
            );
          })}
        </div>

        <p className="text-[10px] text-gray-400 text-center mt-6 leading-relaxed">
          한 판매자 계정은 동시에 최대 1개의 실 방송만 송출할 수 있어요.<br/>
          진행 중인 방송이 있으면 재접속하고, 없으면 예정방송 중 하나를 선택해 주세요.
        </p>
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────
// ── STEP 8-I-2 · Seller LIVE Transparent Comment Overlay ──────────────────
// STEP 8-I-2A · REGRESSION FIX
// - 신규 polling 제거. `messages` prop 을 상위 SellerLiveConsoleInner 의
//   sharedMessages state 에서 그대로 REUSE (신규 API/poll/collection 0건).
// - 정렬 fix: API 는 DESC 반환 → 상위 store 는 DESC 유지 · 여기서 `[0..2]` 로
//   최신 3개를 잡고, UI 표시는 오래된→최신 순으로 reverse 하여 아래가 최신.
// - CommentsOrdersTab 폴링과 절대 중복되지 않음 (동일 array 공유).
const SellerLiveCommentOverlay = ({ messages, onTap }) => {
  // STEP 8-I-4E · fresh(실시간 최초 도착) activity 판별용 ref · component mount 시점부터
  // 새로 들어온 activity 만 sparkle · history reload 는 정적.
  const seenActivityIdsRef = useRef(new Set());
  const visible = useMemo(() => {
    // messages 는 CommentsOrdersTab 과 동일 순서 저장 · API 는 ASC (oldest→newest).
    // sort by created_at ASC 후 slice(-3) → 최신 3 (chat 자연 순서 · 아래=최신).
    const arr = [...(messages || [])];
    arr.sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return ta - tb; // ASC (oldest first)
    });
    return arr.slice(-3);
  }, [messages]);
  if (!visible || visible.length === 0) return null;
  return (
    <button type="button" onClick={onTap}
            className="pointer-events-auto flex flex-col gap-1.5 text-left w-full"
            aria-label="댓글 전체 관리 열기"
            data-testid="live-console-comment-overlay">
      {visible.map((m) => {
        // STEP 8-I-4 · Social Proof activity (cart_add · order_created · comment_order_confirmed)
        // 은 하나의 라인으로 아이콘+문구만 노출 (닉네임/판매자 badge 없이 조용히).
        if (m._activity) {
          // STEP 8-I-4E · fresh(실시간 최초 도착) 여부는 message_id 가 mountRef 이후에
          // 들어왔는지로 판단 · seenActivityIdsRef 로 재실행 방지.
          const isFresh = !seenActivityIdsRef.current.has(m.message_id);
          if (isFresh) seenActivityIdsRef.current.add(m.message_id);
          const emojiCls = isFresh ? 'lm-emoji-sparkle' : '';
          return (
            <div key={m.message_id}
                  className={`max-w-[85%] px-3 py-1.5 rounded-2xl bg-black/40 backdrop-blur-sm text-white text-[13px] font-black leading-snug flex items-center gap-2 ${isFresh ? 'animate-in fade-in slide-in-from-bottom-2 duration-200' : ''}`}
                  data-testid={`live-console-activity-${m.message_id}`}>
              <span className={`text-[26px] leading-none ${emojiCls}`}>🎉</span>
              <span>{m.text.replace(/^🎉\s*/, '')}</span>
            </div>
          );
        }
        const src = (m.source || 'livemoa').toLowerCase();
        const badge = src.startsWith('youtube') ? { l: 'YT',  cls: 'bg-red-600' }
                    : src.startsWith('tiktok')  ? { l: 'TT',  cls: 'bg-black' }
                    : src.startsWith('instagram') ? { l: 'IG', cls: 'bg-pink-500' }
                    : { l: 'LM', cls: 'bg-white/25' };
        // STEP 8-I-3 · Seller-view 는 backend 에서 author_display 로 unmask 제공 · fallback = masked
        const authorName = m.author_display || m.author_masked || '익명';
        return (
          <div key={m.message_id}
                className="max-w-[85%] px-2.5 py-1.5 rounded-2xl bg-black/50 backdrop-blur-sm text-white text-[12px] leading-snug flex items-start gap-1.5 animate-in fade-in slide-in-from-bottom-2 duration-200"
                data-testid={`live-console-comment-overlay-msg-${m.message_id}`}>
            <span className={`shrink-0 px-1 py-0.5 rounded text-[9px] font-black tracking-wide ${badge.cls} text-white`}>{badge.l}</span>
            <span className="font-black text-white/95 shrink-0">{authorName}</span>
            <span className="text-white/90 break-words">{m.text}</span>
          </div>
        );
      })}
      <div className="text-center text-[10px] text-white/70 font-bold pt-0.5"
            data-testid="live-console-comment-overlay-hint">
        탭하여 댓글 숨기기 · 우측 버튼으로 복원
      </div>
    </button>
  );
};

// STEP 8-I-2 · Live Comment Overlay 는 SellerLiveConsole 아래에 정의됨.
// STUDIO-SELLER-NOTICE-PREVIEW-FIX-1 · 접힘(기본) 📢 공지 ↔ 펼침(문구) · 레일(w-12 + gap) 왼쪽 고정 · 폭 제한.
const SellerNoticePreview = ({ text, open, setOpen, below = false }) => {
  return (
    <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
            className="lg:hidden absolute z-30 pointer-events-auto text-left rounded-2xl bg-white/90 dark:bg-black/60 backdrop-blur-md border border-[#FFD6CE] shadow-lg px-2.5 py-1.5"
            style={{ top: `calc(env(safe-area-inset-top) + ${below ? '8.5rem' : '4rem'})`, right: 'calc(env(safe-area-inset-right) + 4rem)',
                     maxWidth: open ? 'min(15rem, calc(100vw - 16rem))' : undefined }}
            data-testid="live-console-seller-notice-preview" data-open={open ? '1' : '0'}>
      <span className="text-[11px] font-black text-[#17181C] dark:text-white whitespace-nowrap">📢 공지</span>
      {open && (
        <span className="block mt-1 text-[11px] text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words max-h-[30vh] overflow-y-auto"
              data-testid="live-console-seller-notice-preview-text">{text}</span>
      )}
    </button>
  );
};

// LIVE-DESCRIPTION-ATTACH-HIDE-FIX-1 · 타이머 패널 공지 감추기 (프리셋과 동일 상태 재사용).
const TimerNoticeHideBtn = () => {
  const [hidden, setHidden] = useStudioNoticeHidden();
  return (
    <button type="button" onClick={() => setHidden(!hidden)} aria-pressed={hidden}
            className={`min-h-[40px] px-3 rounded-full border-2 text-xs font-black ${hidden ? 'border-gray-900 bg-gray-900 text-white' : 'border-black/15 dark:border-white/20 text-black dark:text-white'}`}
            data-testid="live-console-timer-sheet-notice-hide">
      {hidden ? '판매자 화면 보이기' : '판매자 화면 감추기'}
    </button>
  );
};


const SellerLiveConsole = () => {
  const navigate = useNavigate();
  const [qs, setQs] = useSearchParams();
  const preferredSid = qs.get('sid');
  // STEP LIVE-CONSOLE-ENTRY-1 · '라이브' 메뉴 직접 진입 flag — Dashboard 사이드바가
  // ?direct=1 을 붙여 호출. 방송 없음 상태에서도 LIVE Console entry shell 을 유지.
  const direct = qs.get('direct') === '1';
  // STEP STUDIO-MOBILE-TEST-ENTRY-1 · 2026-10-12 · 개발용 테스트 진입 모드.
  //   · REACT_APP_STUDIO_TEST_ENTRY=1 환경변수 로만 활성화 (프로덕션 노출 X).
  //   · ?testmode=1 또는 sid=__TEST__ 로 진입 시 scheduleId sentinel '__TEST__' 할당.
  //   · 실제 방송 생성·시작·송출·주문 호출은 Inner 내부에서 전부 차단 (gate 적용).
  //   · 기존 Camera/Mic/UI 레이아웃은 그대로 재사용 → 모바일 UX 확인 전용.
  const testModeFlag = qs.get('testmode') === '1' || preferredSid === '__TEST__';
  const effectiveSid = testModeFlag ? '__TEST__' : preferredSid;

  // STEP 8-E · CTA Resolver — ?sid 없이 진입 시 방송 선택 화면 먼저 노출.
  // 이 return 은 hooks 앞에 있어도 안전 (모든 hooks 이 이 return 이후에 정의됨).
  if (!effectiveSid) {
    return (
      <BroadcastSelector
        direct={direct}
        onSelect={(schedId, _kind) => setQs({ sid: schedId }, { replace: true })}
      />
    );
  }

  return <SellerLiveConsoleInner key={effectiveSid} preferredSid={effectiveSid} setQs={setQs} />;
};

// STEP 8-E · Identity Lock — Inner 컴포넌트에 `key={preferredSid}` 부여하여
// 판매자가 다른 방송으로 switch 하면 컴포넌트 전체가 unmount → remount.
// comments/candidates/pinned/activeProduct/liveContext 등 모든 runtime state 및
// polling closure 가 새 session_id 기준으로 완전히 재초기화됨.
const SellerLiveConsoleInner = ({ preferredSid, setQs }) => {
  const navigate = useNavigate();
  // STEP STUDIO-MOBILE-TEST-ENTRY-1 · 테스트 진입 sentinel.
  //   · preferredSid === '__TEST__' 이면 모든 **write** API 호출을 차단 (toast 로 안내).
  //   · READ API 는 그대로 호출됨 → 404 는 기존 catch 로 안전히 처리 (empty UI).
  //   · Camera/Mic preview 는 허용 (버튼 눌러 수동으로만 시작 · auto-publish 금지).
  const isTestMode = preferredSid === '__TEST__';
  useShortToast(isTestMode);
  // CONSOLIDATION-1 · TEST 에서 '판매자 화면 감추기' 제거 → 이전 숨김 상태 해제.
  const [_nHidden, _setNHidden] = useStudioNoticeHidden();
  const [noticePreviewOpen, setNoticePreviewOpen] = useState(false);
  useEffect(() => { if (isTestMode && _nHidden) _setNHidden(false); }, [isTestMode, _nHidden, _setNHidden]);
  // STUDIO-BLUETOOTH-REMOTE-INPUT-DIAG-1 · testmode 전용 리모컨 입력 확인 (?remotediag=1 로 바로 열기).
  const [remoteDiagOpen, setRemoteDiagOpen] = useState(
    () => isTestMode && new URLSearchParams(window.location.search).get('remotediag') === '1');
  // STUDIO-BLUETOOTH-PRODUCT-CONTROL-1 · 리모컨 상품 조작 (testmode 전용 · 기본 OFF).
  const [remoteOn, setRemoteOn] = useState(false);
  const [remoteCursorPid, setRemoteCursorPid] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  // STEP STUDIO-STAB-2 · refs mirror current UI state · read from popstate
  // handler without re-registering listener on every re-render.
  const phaseRef = useRef('preview');
  const moreOpenRef = useRef(false);
  const drawerOpenRef = useRef(false);
  const ytEgressOpenRef = useRef(false);
  const ytCommentOpenRef = useRef(false);
  const filterOpenRef = useRef(false);
  const noticeEditOpenRef = useRef(false);
  // STEP STUDIO-MOBILE-RAIL-ROUTING-FIX-1 · 타이머 sheet mirror ref (popstate guard).
  const timerOpenRef = useRef(false);
  // STEP STUDIO-MOBILE-CONTROLS-UI-1 · settings sheet mirror ref (popstate guard).
  const settingsOpenRef = useRef(false);
  const endLiveRef = useRef(null);
  // STEP LIVE-PUBLISHER-GUARD-1A · §3 · publisher-claim heartbeat timer handle.
  //   · publishToLiveKit 성공 시 start · endLive 또는 Room Disconnected 시 clear.
  const publisherClaimTimerRef = useRef(null);
  const roomRef = useRef(null);
  const timerRef = useRef(null);
  // STEP STUDIO-MOBILE-CAMERA-SWITCH-FIX-1 · 전/후면 전환 연타 race guard.
  // flipCamera 가 실행 중일 때 추가 클릭을 무시해 동시 setCameraEnabled / startPreview
  // 호출로 track lifecycle 이 꼬이는 현상을 방지.
  const flippingRef = useRef(false);

  const [cfg, setCfg] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [mics, setMics] = useState([]);
  const [cameraId, setCameraId] = useState('');
  const [micId, setMicId] = useState('');
  const [facing, setFacing] = useState('user');  // 'user' | 'environment' — mobile 전/후면
  const [previewOn, setPreviewOn] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [permError, setPermError] = useState(null);

  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState('preview'); // 'preview' · 'countdown' · 'live' · 'ended'
  const [countdown, setCountdown] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [publishState, setPublishState] = useState('idle');
  const [busy, setBusy] = useState(false);
  const [previewStarting, setPreviewStarting] = useState(false);   // STEP 8-LIVE-CONSOLE-MOBILE-CAMERA-FIX · 즉시 로딩 피드백

  const [activeTab, setActiveTab] = useState('products');
  const [drawerOpen, setDrawerOpen] = useState(false); // mobile bottom sheet
  // STEP STUDIO-MOBILE-UI-2 · 댓글 overlay 표시/숨김 toggle (mobile).
  //   · rail 댓글 버튼: 댓글 표시 상태 토글 (drawer 를 열지 않음 — 영상 확대 유지).
  //   · overlay 영역 tap: 숨김.  댓글 수집 (CommentsOrdersTab · sharedMessages) 은
  //     계속 polling — 숨김은 UI 뿐.
  const [commentsHidden, setCommentsHidden] = useState(false);
  const [seller, setSeller] = useState(null);

  // STEP 8-I · Mobile Basic Camera Filter (PREVIEW ONLY · Seller video CSS filter).
  // 신규 DB collection 0건 · 방송 중 React state 만 유지 · 방송 종료 시 자동 소멸.
  // Buyer Viewer / YouTube Egress 미적용 → UI 에 명시적 disclaimer 표시.
  const [filterMode, setFilterMode] = useState('original');
  const [filterIntensity, setFilterIntensity] = useState(60);
  const [filterOpen, setFilterOpen] = useState(false);
  // STEP STUDIO-MOBILE-CONTROLS-UI-1 · 2026-10-12 · 전용 Settings bottom sheet.
  //   이전: 하단 [설정] 버튼이 setMoreOpen(true) 로 MorePanel 과 중복 연결.
  //   수정: setSettingsOpen(true) 로 독립 sheet 를 열어 촬영 환경 전용 (카메라 전환 ·
  //         카메라 필터 · 상품 Overlay 바로가기) 만 노출.  MorePanel 은 방송 운영 전용.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [overlaySettingsOpen, setOverlaySettingsOpen] = useState(false);
  // STEP STUDIO-MOBILE-PANEL-VISIBILITY-1 · 2026-10-12 · 조작부 숨김 토글 + 고정 패널.
  //   · controlsVisible: 우측 rail + 하단 미디어 조작부 노출 토글.  기본 ON.
  //   · pinMode: 'none' | 'comments' | 'product'.  동시에 하나만 (상호 배제).
  //   · commentsPinSize: 'sm' | 'md' | 'lg' — 대략 4·6·10 개.
  //   모두 클라이언트 state 로 처리 (신규 DB/API 0).
  const [controlsVisible, setControlsVisible] = useState(true);
  // STUDIO-MOBILE-CONTROL-BARS-UX-1 · 하단바 / 오른쪽 바 독립 접기 (저장 X · 진입 시 항상 펼침).
  const [bottomBarOpen, setBottomBarOpen] = useState(true);
  const [railOpen, setRailOpen] = useState(true);
  const [pinMode, setPinMode] = useState('none');
  const [commentsPinSize, setCommentsPinSize] = useState('md');
  const [moreOpen, setMoreOpen] = useState(false);
  // STEP STUDIO-MOBILE-RAIL-ROUTING-FIX-1 · 2026-10-12 · 타이머 전용 bottom sheet state.
  //   이전 bug: rail 타이머 버튼이 openTab('status') 를 호출하여 StatusTab(방송상태)
  //   패널이 열렸음.  dedicate state 를 두어 OverlayTimerControl 만 렌더.
  const [timerOpen, setTimerOpen] = useState(false);
  const [previewKind, setPreviewKind] = useState(null);
  // STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 자동 캡처 흐름 state.
  //   quickCountdown: null | 3 | 2 | 1 → overlay 숫자.  캡처 성공 후 modal 열림.
  //   quickCaptureFile: 캡처된 JPEG File → QuickAddModal.initialImageFile 로 전달.
  const [quickCountdown, setQuickCountdown] = useState(null);
  const [quickCaptureFile, setQuickCaptureFile] = useState(null);
  // STUDIO-BLUETOOTH-QUICK-REGISTER-1 · 상품 등록 담당자 (방송별 · 방송 중 전환 가능) 'self' | 'assistant'.
  const _regModeKey = `livemoa.quickRegMode.${preferredSid || 'none'}`;
  const [quickRegMode, setQuickRegModeState] = useState(() => { try { return localStorage.getItem(_regModeKey) === 'assistant' ? 'assistant' : 'self'; } catch { return 'self'; } });
  const setQuickRegMode = (m) => { setQuickRegModeState(m); try { localStorage.setItem(_regModeKey, m); } catch { /* 메모리만 */ } };
  const shutterBusyRef = useRef(false);
  // STUDIO-BR-TX3-SHUTTER-CAPTURE-1 · 모바일 캡처 버튼 위치(셀터 가상 터치 좌표) · TEST 전용.
  const shutterBtn = useShutterButtonRect();
  const [shutterCalOpen, setShutterCalOpen] = useState(false);
  const videoFilterCss = useMemo(() => computeFilterCss(filterMode, filterIntensity), [filterMode, filterIntensity]);

  // STEP STUDIO-MOBILE-FULLSCREEN-FIX-1C · 2026-10-11
  //   mobile Studio 가 마운트되어 있는 동안 html/body 스크롤·overscroll 을 완전히 잠근다.
  //   root 가 `fixed inset-0` 로 전환되었으므로 body 는 in-flow content 가 없어도
  //   브라우저에 따라 historic scroll offset 또는 rubber-band 가 남아 영상을 흔든다.
  //   LiveKit/Room/Track lifecycle, EgressTemplate, video DOM 모두 미접촉 — 단지 CSS 만.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;
    const prevBodyOverscroll = document.body.style.overscrollBehavior;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
      document.body.style.overscrollBehavior = prevBodyOverscroll;
    };
  }, []);

  // STEP LIVE-STUDIO-PC-UI-1 · PC Studio 미리보기 비율 토글 (16:9 가로 · 9:16 세로).
  //   · UI-only — LiveKit publish / LiveKit Egress / Camera MediaStreamTrack
  //     constraints / overlay engine 모두 변경 금지.  실제 송출은 기존 세로 유지.
  //   · 모바일 UI 영향 0 (video className 은 lg: 응답형으로만 추가).
  //   · 9:16 선택 시 GAP badge (`미리보기 전용 · 실제 송출 비율 미변경`) 노출.
  //   · 16:9 선택 시 overlay preview iframe (portrait content) 은 PC 에서만 숨김
  //     (모바일/9:16 preview 는 그대로 노출).
  const [pcAspect, setPcAspect] = useState('16:9');

  // STEP 8-I-2A · Lifted chat polling · SellerLiveConsoleInner 가 하나의 진짜 SoT.
  // - `/public/streaming/sessions/{sid}/chat` 폴링을 여기로 이관하여
  //   (1) SellerLiveCommentOverlay 는 sharedMessages prop 을 그대로 사용 (자체 poll 0건)
  //   (2) CommentsOrdersTab 도 sharedMessages 를 우선 사용 (내부 poll bypass) → 중복 폴링 0건
  // - store 순서: API 응답이 DESC (newest first) 이므로 그대로 DESC 유지.
  //   Overlay: slice(0,3) 후 UI 상 reverse 로 아래=최신 표시.
  //   CommentsOrdersTab: 자체 map 은 store 순서 그대로 사용 (기존 렌더 동일).
  const [sharedMessages, setSharedMessages] = useState([]);
  // STEP 8-I-5A · Group C FINAL UX POLISH · liveCtx lift (viewer_count · active_product · live_notice).
  // ProductsTab 자체 polling 은 그대로 유지 · 신규 4s polling 1개만 추가.
  const [liveCtx, setLiveCtx] = useState(null);
  // LIVE-TEST-STUDIO-CONSOLE-PARITY-1 · TEST 공유 세션 (입장 즉시 상품 10개 · PC 보조 콘솔과 동기화 · LIVE 상태 무관).
  const testShare = useTestLiveShare({ enabled: isTestMode, ctx: liveCtx, setCtx: setLiveCtx, seed: _buildTestLiveCtx });
  useEffect(() => {
    if (!isTestMode || !session?.__test || !liveCtx) return;
    try {
      localStorage.setItem(_TEST_NT_KEY, JSON.stringify({ live_notice: liveCtx.live_notice || null, timer: liveCtx.timer || null }));
    } catch { /* quota */ }
  }, [isTestMode, session?.__test, liveCtx?.live_notice, liveCtx?.timer]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const sid = session?.session_id;
    if (!sid) { if (!isTestMode) setLiveCtx(null); return; }
    // STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 테스트 모드는 초기값을 liveCtx 에
    //   setLiveCtx(_buildTestLiveCtx()) 로 넣고 ProductsTab 이 externalCtx 로 공유.
    //   실제 /live-context 폴링은 skip (session 이 존재하지 않아 404 noise 유발).
    if (session?.__test) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/streaming/sessions/${sid}/live-context`);
        if (alive) setLiveCtx(data);
      } catch { /* soft */ }
    };
    load();
    const id = setInterval(load, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [session?.session_id]);
  const _chatLastTsRef = useRef(null);
  const _chatSeenIdsRef = useRef(new Set());
  useEffect(() => {
    // session 이 바뀌면 완전 초기화 (Identity Lock)
    setSharedMessages([]);
    _chatLastTsRef.current = null;
    _chatSeenIdsRef.current = new Set();
    const sid = session?.session_id;
    if (!sid) return;
    let alive = true;
    const load = async () => {
      try {
        // STEP 8-I-3 · seller_view=1 → Seller UI 는 원본 nickname 노출 (backend 권한 검증됨)
        const params = _chatLastTsRef.current
          ? { since: _chatLastTsRef.current, seller_view: 1 }
          : { limit: 50, seller_view: 1 };
        const { data } = await api.get(`/public/streaming/sessions/${sid}/chat`, { params });
        if (!alive) return;
        const incoming = (data?.messages || []).filter((m) => !_chatSeenIdsRef.current.has(m.message_id));
        incoming.forEach((m) => _chatSeenIdsRef.current.add(m.message_id));
        if (incoming.length) {
          // STEP 8-I-4A · Activity row (author_role='activity') 는 _activity flag 로 매핑 → 렌더 분기.
          const normalized = incoming.map((m) =>
            (m.author_role === 'activity' || m.message_type === 'activity')
              ? { ...m, _activity: true }
              : m
          );
          // API 는 ASC (oldest→newest) 반환 · 기존 CommentsOrdersTab 저장 순서 그대로 유지.
          setSharedMessages((prev) => [...prev, ...normalized].slice(-200));
          // since cursor: incoming 은 ASC · last (newest) 의 created_at 을 사용해야 다음 poll 이 정말 새 것만.
          const newest = incoming[incoming.length - 1];
          if (newest?.created_at) _chatLastTsRef.current = newest.created_at;
        }
      } catch { /* soft */ }
    };
    load();
    const id = setInterval(load, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [session?.session_id]);

  // STEP 8-LIVE-CONSOLE-WIRING · 실 데이터 · 재개발 없음 · 판매자 레벨 조회
  const [channels, setChannels] = useState(null);        // GET /seller/live/channels
  const [scheduleId, setScheduleId] = useState(null);    // for comment-candidates scope

  // STEP 8-LIVE-SESSION-STATE-AND-BUYER-ROUTE-FIX
  // 서버 streaming_sessions 상태를 SoT 로 반영 · Mobile/PC 크로스 디바이스 동기화.
  // - preferredSid (schedule_id) 를 body 로 넘겨 idempotent POST → 서버가 링크된 active
  //   streaming_session 을 반환 · 이미 있으면 그것을, 없으면 새로 생성 (ready state).
  // - session state 를 6초 주기로 refresh · streaming_status='live' 인데 local publishState
  //   이 idle 이면 "다른 기기에서 송출 중" 을 Status Tab 에 반영.
  // STEP 8-I-4C · resolvedScheduleId — preferredSid(URL) 이 유효 schedule 형태면 우선,
  //   아니면 늦게 세팅되는 fallback `scheduleId` state 를 사용. Ref 로 최신값을 유지해
  //   `refreshServerSession` 이 stale closure 문제 없이 항상 최신 값을 payload 로 보냄.
  // - scheduleId 가 뒤늦게 확정되면 useEffect 로 `refreshServerSession()` 을 한 번 즉시
  //   재실행하여 backend session.schedule_id 를 원하는 방송으로 late-link 시킨다.
  // - 무한 loop 방지: `lastLinkedScheduleIdRef` 로 이미 link 한 값은 반복 요청 X.
  const _preferredIsSchedule = !!(preferredSid && preferredSid.startsWith('schedule_'));
  const resolvedScheduleIdRef = useRef(_preferredIsSchedule ? preferredSid : null);
  const lastLinkedScheduleIdRef = useRef(null);
  useEffect(() => {
    resolvedScheduleIdRef.current = _preferredIsSchedule
      ? preferredSid
      : (scheduleId && String(scheduleId).startsWith('schedule_')) ? scheduleId : null;
  }, [_preferredIsSchedule, preferredSid, scheduleId]);

  const refreshServerSession = useCallback(async () => {
    try {
      const rs = resolvedScheduleIdRef.current;
      const body = rs ? { schedule_id: rs } : {};
      const { data } = await api.post('/seller/streaming/sessions', body);
      setSession((prev) => ({ ...(prev || {}), ...data }));
      if (rs) lastLinkedScheduleIdRef.current = rs;
    } catch { /* soft */ }
  }, []);
  useEffect(() => {
    // STEP STUDIO-TEST-LIVE-AND-CONSOLE-FIX-1 · §A — 테스트 모드에서는 실 세션 생성 차단.
    //   `refreshServerSession` 은 POST `/seller/streaming/sessions` 호출로
    //   실제 DB streaming_session 을 **ensure** 하는 WRITE API. 테스트 모드에서는
    //   아예 호출하지 않아 실 세션/주문 pollution 을 방지한다.
    if (isTestMode) return undefined;
    refreshServerSession();
    const t = setInterval(refreshServerSession, 6000);
    return () => clearInterval(t);
  }, [refreshServerSession, isTestMode]);
  // STEP 8-I-4C · scheduleId 가 뒤늦게 (fallback 경로로) 확정되었고 아직 backend
  // session 이 그 값으로 링크되지 않았으면 즉시 한 번 재-link.
  useEffect(() => {
    if (isTestMode) return;
    const rs = resolvedScheduleIdRef.current;
    if (!rs) return;
    if (lastLinkedScheduleIdRef.current === rs) return;
    refreshServerSession();
  }, [scheduleId, refreshServerSession, isTestMode]);
  // STEP 8-LIVE-MULTISTREAM-B/C · Egress destinations
  const [egressStatus, setEgressStatus] = useState(null);
  const [egressCapability, setEgressCapability] = useState(null);
  const [ytEgressOpen, setYtEgressOpen] = useState(false);
  const [ytUrl, setYtUrl] = useState('rtmps://a.rtmps.youtube.com:443/live2');
  const [ytKey, setYtKey] = useState('');
  // STEP MP-EGRESS-PREP-1 · Instagram manual Stream URL + Stream Key 입력.
  // 판매자가 IG Live Producer 에서 직접 발급받은 URL/Key 를 그대로 붙임.
  // Egress path 는 YouTube 와 완전히 동일 · platform 만 "instagram".
  // 이번 STEP 에서는 실제 send 를 자동으로 하지 않음 · 판매자가 [연결] 눌러야 실행.
  const [igEgressOpen, setIgEgressOpen] = useState(false);
  const [igUrl, setIgUrl] = useState('rtmps://live-api-s.facebook.com:443/rtmp/');
  const [igKey, setIgKey] = useState('');
  const [igBusy, setIgBusy] = useState(false);
  // STEP 8-H-2 · Comment Ingest 를 위한 YouTube LIVE 시청 URL 필드.
  // Egress (video push) 와 Comment Poller (댓글 수신) 는 완전히 분리된 두 endpoint.
  // 이 필드가 있으면 [연결] 시 `/youtube/connect` 도 병렬 호출하여 poller 를 시작.
  const [ytWatchUrl, setYtWatchUrl] = useState('');
  const [ytBusy, setYtBusy] = useState(false);
  // STEP YT-AUTO-12 · UI-facing YouTube auto-broadcast phase (Google 상태
  // 머신 그대로 노출 X · toast 텍스트 + 필요 시 버튼 라벨 힌트 용).
  //   idle | preparing | starting | live | ending | ended
  const [ytPhase, setYtPhase] = useState('idle');
  // STEP 8-H-3 · 이미 Egress 송출 중일 때도 댓글 연결을 독립적으로 실행할 수 있는
  // 별도 Modal · Egress 를 절대 끊지 않음 · `/youtube/connect` 만 호출.
  const [ytCommentOpen, setYtCommentOpen] = useState(false);
  const [ytCommentUrl, setYtCommentUrl] = useState('');
  const [ytCommentBusy, setYtCommentBusy] = useState(false);
  const [ytStatus, setYtStatus] = useState(null); // GET /youtube/status 캐시 · 표시용

  const refreshChannels = useCallback(async () => {
    try {
      const { data } = await api.get('/seller/live/channels');
      setChannels(data);
    } catch { /* soft · 미인증/네트워크 오류 무시 */ }
  }, []);
  useEffect(() => {
    refreshChannels();
    const t = setInterval(refreshChannels, 8000);
    return () => clearInterval(t);
  }, [refreshChannels]);

  // Egress capability probe (1회)
  useEffect(() => {
    api.get('/seller/streaming/egress/capability')
      .then(({ data }) => setEgressCapability(data))
      .catch(() => setEgressCapability({ available: false, reason: 'unavailable' }));
  }, []);

  // Egress status polling — 방송 중일 때만
  const refreshEgress = useCallback(async () => {
    if (!session?.session_id) { setEgressStatus(null); return; }
    try {
      const { data } = await api.get(`/seller/streaming/sessions/${session.session_id}/egress/status`);
      setEgressStatus(data);
    } catch { /* soft */ }
  }, [session?.session_id]);
  useEffect(() => {
    refreshEgress();
    const t = setInterval(refreshEgress, 6000);
    return () => clearInterval(t);
  }, [refreshEgress]);

  const submitYtEgress = async () => {
    if (!session?.session_id || ytBusy) return;
    if (!ytUrl.startsWith('rtmp')) { toast.error('RTMPS URL 을 확인해 주세요.'); return; }
    if (!ytKey.trim() || ytKey.trim().length < 6) { toast.error('스트림 키를 입력해 주세요.'); return; }
    setYtBusy(true);
    try {
      const { data } = await api.post(
        `/seller/streaming/sessions/${session.session_id}/egress/destinations`,
        { platform: 'youtube', rtmp_url: ytUrl.trim(), stream_key: ytKey.trim() },
      );
      if (data?.status === 'error') {
        toast.error(`YouTube 송출 오류 · ${data.last_error || '연결 실패'}`);
      } else {
        toast.success('YouTube 송출을 시작했어요.');
      }
      // STEP 8-H-2 · YouTube 댓글 수신을 위해 별도 endpoint `/youtube/connect` 호출.
      // Egress (video push) 와 Comment Poller (댓글 수집) 는 완전히 다른 파이프라인 ·
      // 판매자가 시청 URL 을 입력한 경우에만 시도 · 실패해도 Egress 성공은 유지.
      const watch = ytWatchUrl.trim();
      if (watch && scheduleId) {
        try {
          const { data: yc } = await api.post(
            `/seller/broadcasts/${scheduleId}/youtube/connect`,
            { video_url: watch },
          );
          if (yc?.status === 'connected') {
            toast.success('YouTube 댓글 수집을 시작했어요.');
          } else if (yc?.status === 'error') {
            toast.error(`YouTube 댓글 연결 오류 · ${yc.error_message || 'unknown'}`);
          }
        } catch (e) {
          toast.error(`YouTube 댓글 연결 실패 · ${e?.response?.data?.detail || '시청 URL 확인 필요'}`);
        }
      }
      // 즉시 form field clear · in-memory 도 삭제 (보안)
      setYtKey(''); setYtWatchUrl(''); setYtEgressOpen(false);
      refreshEgress();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'YouTube 송출 연결 실패');
    } finally { setYtBusy(false); }
  };

  // STEP YT-AUTO-10/12 · YouTube 자동 연결 + auto go-live.
  // 판매자는 버튼 1번으로 auto-prepare → ready → testing → active/good 대기 →
  // live 전환까지 자동 진행. Google 내부 상태 머신은 노출하지 않음.
  const submitYtAutoConnect = async () => {
    if (!session?.session_id || ytBusy) return;
    setYtBusy(true);
    setYtPhase('preparing');
    // STEP YT-VERTICAL-1 · 카메라 orientation 감지 → Egress 세로/가로 preset 결정.
    // 우선 실 publisher video track 의 실제 해상도를 사용 (mobile portrait 카메라는
    // height > width). track 접근 실패 시 window matchMedia 로 fallback.
    let orientation = 'landscape';
    try {
      const room = roomRef.current || null;
      const track = room?.localParticipant?.videoTracks
        ? Array.from(room.localParticipant.videoTracks.values())[0]?.track
        : null;
      const settings = track?.mediaStreamTrack?.getSettings?.();
      if (settings?.width && settings?.height) {
        orientation = settings.height > settings.width ? 'portrait' : 'landscape';
      } else if (typeof window !== 'undefined' && window.matchMedia) {
        orientation = window.matchMedia('(orientation: portrait)').matches
          ? 'portrait' : 'landscape';
      } else if (typeof window !== 'undefined') {
        orientation = window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
      }
    } catch (_e) {
      orientation = 'landscape';
    }
    try {
      const { data } = await api.post(
        `/seller/live/youtube/auto-prepare`,
        { session_id: session.session_id, orientation },
      );
      if (!data?.ok) {
        const st = data?.state || 'unknown';
        const detail = (data?.detail || '') + '';
        if (st === 'oauth_not_connected' || st === 'oauth_refresh_failed') {
          toast.error('YouTube 계정이 연결되어 있지 않아요. SNS 연동에서 재연결해 주세요.');
        } else if (st === 'inconsistent_state') {
          toast.error('YouTube 세션 상태가 비정상이에요. 관리자에게 문의해 주세요.');
        } else if (st === 'egress_wiring_failed') {
          // STEP STUDIO-LIFECYCLE-STABILIZATION · destination_already_active
          // 라도 이번 reconciliation 이후에는 실제로 살아있는 경우에만 발생.
          if (detail.includes('destination_already_active')) {
            toast.info('YouTube 송출이 이미 진행 중입니다.');
            // fall through to poll refresh for accurate status.
            refreshEgress();
            setYtPhase('live');
            setYtEgressOpen(false);
            return;
          }
          toast.error(`YouTube 자동 연결 실패 · ${detail || st}`);
        } else if (st === 'egress_mapping_not_ready') {
          toast.error(`YouTube 자동 연결 실패 · ${detail || st}`);
        } else if (st === 'youtube_api_error') {
          toast.error(`YouTube API 호출 실패 · ${detail || ''}`);
        } else if (st === 'persistence_failure_after_remote_create') {
          toast.error('YouTube 리소스가 생성되었지만 저장에 실패했어요. 관리자에게 문의해 주세요.');
        } else {
          toast.error(detail || `YouTube 자동 연결 실패 · ${st}`);
        }
        setYtPhase('idle');
        return;
      }
      toast.info('YouTube 방송 시작 중…');
      setYtPhase('starting');
      // STEP YT-AUTO-12 · chain auto → testing → poll → live · state-aware.
      const { data: goRes } = await api.post(
        `/seller/live/youtube/go-live`,
        { session_id: session.session_id },
      );
      if (!goRes?.ok) {
        const st = goRes?.state || 'unknown';
        if (st === 'ingest_not_ready') {
          toast.error('YouTube ingest 준비 지연 · 잠시 후 다시 시도해 주세요.');
        } else if (st === 'invalid_state') {
          toast.error(`YouTube 방송 상태가 올바르지 않아요 · ${goRes.detail || st}`);
        } else if (st === 'youtube_api_error') {
          toast.error(`YouTube API 호출 실패 · ${goRes.reason || ''}`);
        } else if (st === 'not_prepared') {
          toast.error('YouTube 자동 연결이 아직 준비되지 않았어요.');
        } else {
          toast.error(goRes?.detail || `YouTube 방송 시작 실패 · ${st}`);
        }
        setYtPhase('idle');
        return;
      }
      toast.success('YouTube 방송 중');
      setYtPhase('live');
      setYtEgressOpen(false);
      refreshEgress();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'YouTube 자동 연결 실패');
      setYtPhase('idle');
    } finally { setYtBusy(false); }
  };

  // STEP MP-EGRESS-PREP-1 · Instagram destination handlers.
  // 로직은 YouTube 와 동일 (platform: 'instagram') · 신규 endpoint 0.
  const submitIgEgress = async () => {
    if (!session?.session_id || igBusy) return;
    if (!igUrl.startsWith('rtmp')) { toast.error('RTMPS URL 을 확인해 주세요.'); return; }
    if (!igKey.trim() || igKey.trim().length < 6) { toast.error('스트림 키를 입력해 주세요.'); return; }
    setIgBusy(true);
    try {
      const { data } = await api.post(
        `/seller/streaming/sessions/${session.session_id}/egress/destinations`,
        { platform: 'instagram', rtmp_url: igUrl.trim(), stream_key: igKey.trim() },
      );
      if (data?.status === 'error') {
        toast.error(`Instagram 송출 오류 · ${data.last_error || '연결 실패'}`);
      } else {
        toast.success('Instagram 송출을 시작했어요.');
      }
      setIgKey(''); setIgEgressOpen(false);
      refreshEgress();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Instagram 송출 연결 실패');
    } finally { setIgBusy(false); }
  };
  const stopIgEgress = async () => {
    if (!session?.session_id) return;
    if (!window.confirm('Instagram 송출을 중지할까요? LIVEMOA 원본 방송은 계속돼요.')) return;
    try {
      await api.delete(`/seller/streaming/sessions/${session.session_id}/egress/destinations/instagram`);
      toast.info('Instagram 송출을 중지했어요.');
      refreshEgress();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '중지 실패');
    }
  };

  const stopYtEgress = async () => {
    if (!session?.session_id) return;
    if (!window.confirm('YouTube 송출을 중지할까요? LIVEMOA 원본 방송은 계속돼요.')) return;
    try {
      await api.delete(`/seller/streaming/sessions/${session.session_id}/egress/destinations/youtube`);
      toast.info('YouTube 송출을 중지했어요.');
      refreshEgress();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '중지 실패');
    }
  };

  // STEP 8-H-3 · Comment Poller 상태 폴링 · scheduleId 있을 때 6초 주기.
  //   존재 여부: ytStatus.session?.status === 'connected' → 댓글 수집 활성
  //   Egress 상태(egressStatus)와 완전 독립적으로 표시.
  useEffect(() => {
    if (!scheduleId) { setYtStatus(null); return; }
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/seller/broadcasts/${scheduleId}/youtube/status`);
        if (alive) setYtStatus(data);
      } catch { /* soft · 스케줄 미링크 or 미인증 무시 */ }
    };
    load();
    const t = setInterval(load, 6000);
    return () => { alive = false; clearInterval(t); };
  }, [scheduleId]);

  // STEP 8-H-3 · Comment 전용 연결 · Egress endpoint 절대 재호출 안 함.
  //   기존 `/youtube/connect` 만 재사용 · backend 는 이미 idempotent (prior session
  //   stop → 새 poller task 시작 · line 9082~9124).
  const submitYtCommentConnect = async () => {
    if (!scheduleId || ytCommentBusy) return;
    const watch = ytCommentUrl.trim();
    if (!watch) { toast.error('YouTube LIVE 시청 URL 을 입력해 주세요.'); return; }
    setYtCommentBusy(true);
    try {
      const { data } = await api.post(
        `/seller/broadcasts/${scheduleId}/youtube/connect`,
        { video_url: watch },
      );
      if (data?.status === 'connected') {
        toast.success('YouTube 댓글 수집을 시작했어요.');
        setYtCommentUrl(''); setYtCommentOpen(false);
        // 즉시 상태 갱신
        try {
          const { data: st } = await api.get(`/seller/broadcasts/${scheduleId}/youtube/status`);
          setYtStatus(st);
        } catch {}
      } else if (data?.status === 'error') {
        toast.error(`YouTube 댓글 연결 오류 · ${data.error_message || 'unknown'}`);
      } else {
        toast.warning(`상태: ${data?.status || 'unknown'}`);
      }
    } catch (e) {
      // STEP 8-H-4: surface the exact backend detail. If no HTTP response
      // arrived (network / CORS / 502), give a distinct message so we don't
      // conflate it with a 4xx from `/youtube/connect`.
      // eslint-disable-next-line no-console
      console.error('[yt-comment-connect] error', {
        status: e?.response?.status,
        detail: e?.response?.data?.detail,
        message: e?.message,
      });
      if (e?.response?.data?.detail) {
        toast.error(e.response.data.detail);
      } else if (e?.response?.status) {
        toast.error(`YouTube 댓글 연결 실패 · HTTP ${e.response.status}`);
      } else {
        toast.error(`YouTube 댓글 연결 실패 · 네트워크 오류 (${e?.message || 'unknown'})`);
      }
    } finally { setYtCommentBusy(false); }
  };

  // Resolve schedule_id for candidate scoping.
  // 1) URL ?sid= 가 schedule_id 형태이면 그대로 사용
  // 2) 아니면 /seller/broadcasts/today 에서 live > 예정 순으로 첫 항목 선택
  useEffect(() => {
    let alive = true;
    (async () => {
      // STEP STUDIO-TEST-LIVE-AND-CONSOLE-FIX-1 · §A — 테스트 모드에서는 실제
      // scheduleId 를 가져오지 않는다. sentinel 'schedule_test_mode' 로 설정하여
      // scheduleId 가 필요한 UI 패널(ProductsTab 등)이 render 되지만 모든 write
      // 는 아래 isTestMode gate 로 차단된다. (`/seller/broadcasts/today` 호출 자체도
      // 불필요한 네트워크/감사로그를 유발하므로 skip.)
      if (isTestMode) {
        if (alive) setScheduleId('schedule_test_mode');
        return;
      }
      if (preferredSid && preferredSid.startsWith('schedule_')) {
        if (alive) setScheduleId(preferredSid);
        return;
      }
      try {
        const { data } = await api.get('/seller/broadcasts/today');
        if (!alive) return;
        const list = Array.isArray(data) ? data : [];
        const liveOne = list.find((b) => b.status === '라이브' || b.status === 'live');
        const pick = liveOne || list[0] || null;
        if (pick?.schedule_id) setScheduleId(pick.schedule_id);
      } catch { /* soft */ }
    })();
    return () => { alive = false; };
  }, [preferredSid, isTestMode]);

  // ── config + seller identity ───────────────────────────────────────
  useEffect(() => {
    let alive = true;
    api.get('/seller/streaming/config').then(({ data }) => { if (alive) setCfg(data); }).catch(() => {});
    api.get('/seller/me').then(({ data }) => { if (alive) setSeller(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // ── device enumerate + start preview ───────────────────────────────
  // STEP 8-LIVE-CONSOLE-FIRST-PERMISSION-FIX
  // 신규 기기에서 최초 방문 시 브라우저 permission prompt 가 뜨지 않던 P0 버그 수정.
  // STEP 8-LIVE-CONSOLE-BROWSER-GUARD
  // 인앱 브라우저(WebView · FB/Insta/Kakao/Naver/Line 등) 에서 getUserMedia 가 hang
  // 하거나 silent fail 하면 "정상 브라우저 열기" 안내. UA 단독으로는 차단하지 X.
  const enumerate = useCallback(async () => {
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setCameras(list.filter((d) => d.kind === 'videoinput'));
      setMics(list.filter((d) => d.kind === 'audioinput'));
    } catch (e) { /* ignore */ }
  }, []);

  const stopTracks = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch {} }); } catch {}
    streamRef.current = null;
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch {} }
    setPreviewOn(false);
  }, []);

  // permission denied 상태 안내용 helper — permissions API 가 있으면 사용
  const _checkPermissionState = async () => {
    try {
      if (!navigator.permissions || !navigator.permissions.query) return null;
      const [cam, mic] = await Promise.all([
        navigator.permissions.query({ name: 'camera' }).catch(() => null),
        navigator.permissions.query({ name: 'microphone' }).catch(() => null),
      ]);
      return { camera: cam?.state || null, microphone: mic?.state || null };
    } catch { return null; }
  };

  // 인앱 브라우저/WebView 힌트 감지 (UA 시그니처). 반환값이 true 여도 단독 차단하지 않음 —
  // 실제 getUserMedia 실패/hang 시에만 안내 문구를 다르게 준다.
  const _detectInAppHint = () => {
    try {
      const ua = (navigator.userAgent || '').toLowerCase();
      // iOS Safari 는 정상 · 인앱은 대개 특정 문자열 포함
      const iosInApp = /iphone|ipad/.test(ua)
        && !/safari\//.test(ua)   // 진짜 Safari 는 "Safari/" 를 포함, WKWebView 인앱은 대개 없음
        && !/crios/.test(ua) && !/fxios/.test(ua) && !/edgios/.test(ua);
      // Android 대표 인앱 시그니처
      const inAppSigs = [
        'fban', 'fbav', 'fb_iab',           // Facebook
        'instagram',                          // Instagram
        'kakaotalk',                          // KakaoTalk
        'line/',                              // LINE
        'naver(inapp',                        // 네이버 앱
        'daumapps',                           // 다음
        'wv)',                                // Android WebView marker
        'twitter', 'tiktok', 'threads',
        'snapchat', 'pinterest',
        'micromessenger',                     // WeChat
      ];
      const androidInApp = inAppSigs.some((s) => ua.includes(s));
      const isAndroid = /android/.test(ua);
      const isIOS = /iphone|ipad|ipod/.test(ua);
      return {
        likelyInApp: iosInApp || androidInApp,
        isAndroid, isIOS,
      };
    } catch { return { likelyInApp: false, isAndroid: false, isIOS: false }; }
  };

  const _inAppMessage = (hint) => {
    // 정상 브라우저 권장 · UA 로 차단은 하지 않음
    const suggest = hint.isIOS
      ? 'iPhone 은 Safari 에서 열어주세요.'
      : hint.isAndroid
      ? 'Android 는 Chrome 또는 Samsung Internet 에서 열어주세요.'
      : 'Chrome, Safari, Samsung Internet 등 정상 브라우저에서 열어주세요.';
    return `이 브라우저에서는 카메라 사용이 제한될 수 있어요. ${suggest} (현재 페이지 주소를 복사해서 정상 브라우저에 붙여넣기)`;
  };

  const startPreview = async (opts = {}) => {
    // ═══ PHASE 1 · USER-GESTURE-CRITICAL PATH ══════════════════════════
    // getUserMedia 이전에 절대 event loop 을 yield 하지 않는다.
    // React setState 조차 최소화 · getUserMedia 를 최우선 fire.

    // 1a) mediaDevices 지원 여부 (sync guard, state change 없음)
    if (typeof navigator === 'undefined' || !navigator.mediaDevices
        || typeof navigator.mediaDevices.getUserMedia !== 'function') {
      const hint = _detectInAppHint();
      setPermError(hint.likelyInApp
        ? _inAppMessage(hint)
        : '이 브라우저에서 카메라를 사용할 수 없어요. HTTPS · 최신 Chrome/Safari 에서 다시 열어주세요.');
      return;
    }

    // 1b) constraints 계산 (sync)
    const targetCam = opts.cameraId !== undefined ? opts.cameraId : cameraId;
    const targetMic = opts.micId !== undefined ? opts.micId : micId;
    const targetFacing = opts.facing || facing;

    // 최초 진입 (streamRef 비어있음) 시에는 OLD LiveStreamCard 와 동일하게 `video: true` 로 요청.
    // → Samsung Internet · Android WebView · 오래된 Chrome 에서도 permission prompt 확실히 발생.
    // 두 번째 이후 (전/후면 전환 등) 만 facingMode / deviceId 제약 사용.
    const isFirstRequest = !streamRef.current && !targetCam && !opts.facing;
    // STEP STUDIO-MOBILE-CAMERA-SWITCH-FIX-1 · opts.facing 가 명시적으로 전달된 경우
    // (= flipCamera 요청) 에는 `exact` 로 하드웨어 전환을 강제. 기존 `ideal` 은
    // Android Chrome/Samsung Internet 이 힌트로 무시하고 같은 카메라를 반환해
    // CSS 미러링만 바뀌는 현상의 root cause 였다. 기타 refresh (mic 변경 등) 는
    // 기존 `ideal` 동작을 유지해 회귀 위험을 최소화한다.
    const isExplicitFacingSwitch = !!opts.facing;
    let videoConstraint;
    if (targetCam) {
      videoConstraint = { deviceId: { exact: targetCam } };
    } else if (isFirstRequest) {
      videoConstraint = true;
    } else if (isExplicitFacingSwitch) {
      videoConstraint = { facingMode: { exact: targetFacing } };
    } else {
      videoConstraint = { facingMode: { ideal: targetFacing } };
    }
    const audioConstraint = targetMic ? { deviceId: { exact: targetMic } } : true;

    // 1c) 이전 트랙 정리 (sync 만)
    try { streamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch {} }); } catch {}

    // 1d) 🔴 **가장 중요** · getUserMedia 를 setState 보다 먼저 fire.
    //     여기서 반환되는 promise 는 아직 await 안 함 (user gesture 소비하지 않음).
    let gumPromise;
    try {
      gumPromise = navigator.mediaDevices.getUserMedia({
        video: videoConstraint,
        audio: audioConstraint,
      });
    } catch (syncErr) {
      // 매우 드문 sync throw (예: 제약이 명백히 invalid)
      setPermError(`카메라 시작 실패 · ${syncErr?.name || 'Error'}`);
      return;
    }

    // ═══ PHASE 2 · getUserMedia 이후 안전 영역 ═════════════════════════
    setPermError(null);
    setPreviewStarting(true);

    // 인앱 브라우저 hang 감지용 timeout race (12초). 정상 브라우저에서는 사용자가 prompt 를
    // 여는데 시간이 걸릴 수 있으므로 여유롭게 설정. 초과 시 UA 힌트 기반 안내.
    const HANG_MS = 12000;
    let stream = null;
    let hangDetected = false;
    try {
      stream = await Promise.race([
        gumPromise,
        new Promise((_, rej) => setTimeout(() => {
          hangDetected = true;
          rej(new Error('__gum_hang__'));
        }, HANG_MS)),
      ]);
    } catch (e1) {
      const nm = e1?.name || '';
      // 마이크 관련 실패만 video-only 폴백 (NotAllowedError · hang 은 폴백 X)
      if (!hangDetected && (nm === 'NotFoundError' || nm === 'OverconstrainedError' || nm === 'NotReadableError')) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraint, audio: false });
          toast.info('마이크 없이 카메라만 시작했어요. LIVE 시작 전 마이크를 확인해주세요.');
        } catch (e2) {
          await _reportGumError(e2, false);
          setPreviewStarting(false);
          return;
        }
      } else {
        await _reportGumError(e1, hangDetected);
        setPreviewStarting(false);
        return;
      }
    }

    // Success path
    streamRef.current = stream;
    if (videoRef.current) {
      try { videoRef.current.srcObject = stream; } catch {}
      try { await videoRef.current.play(); } catch { /* muted+autoplay 로 자동재생 */ }
    }
    setPreviewOn(true);
    setMicMuted(false);
    setPreviewStarting(false);
    enumerate();
  };

  const _reportGumError = async (e, hangDetected) => {
    const name = e?.name || 'Error';
    const hint = _detectInAppHint();

    // hang 또는 정체불명 error 이면서 UA 힌트가 인앱이면 → 인앱 브라우저 안내 우선
    if ((hangDetected || name === 'AbortError' || name === 'Error' || name === '') && hint.likelyInApp) {
      const msg = _inAppMessage(hint);
      setPermError(msg);
      toast.error('브라우저 호환 문제');
      return;
    }

    // NotAllowedError · denied vs prompt-dismissed 구분 시도
    let denied = false;
    if (name === 'NotAllowedError') {
      const p = await _checkPermissionState();
      denied = p?.camera === 'denied' || p?.microphone === 'denied';
      // NotAllowedError 인데 permissions API 로도 확인 안 되고 UA 가 인앱이면 인앱 안내를 병기
      if (!denied && hint.likelyInApp) {
        setPermError(_inAppMessage(hint));
        toast.error('브라우저 호환 문제');
        return;
      }
    }
    const map = {
      NotAllowedError: denied
        ? '카메라 권한이 브라우저 설정에서 차단되어 있어요. 주소창 왼쪽 자물쇠 아이콘 → 사이트 설정 → 카메라·마이크를 「허용」으로 바꾼 뒤 페이지를 새로고침해주세요.'
        : '카메라/마이크 권한을 허용해주세요. [카메라 켜기] 를 다시 눌러 브라우저의 권한 요청창에서 「허용」을 선택해주세요.',
      NotFoundError: '연결된 카메라/마이크가 없어요. 다른 앱이 사용 중인지 확인해주세요.',
      NotReadableError: '카메라를 다른 앱이 사용 중이에요. 다른 카메라 앱을 종료 후 다시 시도해주세요.',
      OverconstrainedError: '요청한 카메라 설정을 지원하지 않아요. 후면/전면을 바꿔 다시 시도해주세요.',
      SecurityError: '보안 컨텍스트가 아니에요. HTTPS 로 다시 접속해주세요.',
      TypeError: '이 브라우저에서 카메라를 사용할 수 없어요. HTTPS · 최신 Chrome/Safari 에서 다시 열어주세요.',
    };
    let friendly;
    if (hangDetected) {
      friendly = hint.likelyInApp ? _inAppMessage(hint)
        : '카메라 응답이 지연되고 있어요. 다른 카메라 앱을 종료하고 다시 시도해주세요. (지속되면 브라우저를 재시작해주세요)';
    } else {
      friendly = map[name] || `카메라 시작 실패 · ${name}`;
    }
    setPermError(friendly);
    toast.error(friendly);
  };

  // STEP STUDIO-MOBILE-CAMERA-SWITCH-FIX-1 · 실 하드웨어 카메라 장치 전환.
  //   RCA · 이전 구현은 (a) `setFacing(nextFacing)` 를 하드웨어 전환 전에 호출해
  //         실제 전환 실패시에도 CSS `scaleX(-1)` 미러링만 토글되었고, (b) preview 경로의
  //         startPreview 가 `facingMode: {ideal}` 을 써서 Android 가 같은 카메라를 반환해도
  //         성공으로 간주되어 실질적으로 미러링만 바뀌었다.  또한 (c) 실제 전환 결과를
  //         track.getSettings().facingMode / label 로 검증하지 않아 silent 실패를 덮었다.
  //   Fix  · 1) flippingRef 로 연타 race 차단, 2) startPreview `exact` 강제 (상단 patch),
  //         3) 전환 후 track settings 검증 → mismatch 시 deviceId 재시도 → 그래도 실패하면
  //            `facing` state 를 **원복** (CSS 만 바뀌는 fake success 금지),
  //         4) 단일 카메라 기기면 안내 toast 후 return,
  //         5) phase==='live' + roomRef null (테스트 LIVE) 은 preview 경로로 fallthrough.
  const flipCamera = async () => {
    if (flippingRef.current) return;
    flippingRef.current = true;
    const prevFacing = facing;
    const nextFacing = prevFacing === 'user' ? 'environment' : 'user';
    const _pickDeviceByFacing = async (want) => {
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        const cams = list.filter((d) => d.kind === 'videoinput' && d.deviceId);
        const rx = want === 'user'
          ? /front|user|selfie|전면|앞|셀피/i
          : /back|rear|environment|후면|뒤|외부/i;
        const hit = cams.find((d) => rx.test(d.label || ''));
        if (hit) return hit.deviceId;
        if (cams.length >= 2) {
          const currentTrack = roomRef.current?.localParticipant?.getTrackPublication(Track.Source.Camera)?.track;
          const currentSettings = currentTrack?.mediaStreamTrack?.getSettings?.() || {};
          const currId = currentSettings.deviceId
            || streamRef.current?.getVideoTracks?.()[0]?.getSettings?.()?.deviceId;
          const other = cams.find((d) => d.deviceId && d.deviceId !== currId);
          return other?.deviceId || cams[1].deviceId;
        }
        return null;
      } catch { return null; }
    };
    // 실제 track 이 요청한 facing 으로 전환됐는지 검증.  true=OK, false=반대 카메라,
    // null=label/settings 로 확정 불가 (낙관적 성공 처리).
    const _verifyFacing = (track, want) => {
      try {
        if (!track) return null;
        if (track.readyState && track.readyState === 'ended') return false;
        const settings = track.getSettings?.() || {};
        if (settings.facingMode === want) return true;
        if (settings.facingMode && settings.facingMode !== want) return false;
        const label = (track.label || '').toLowerCase();
        const rxWant = want === 'user'
          ? /front|user|selfie|전면|앞|셀피/i
          : /back|rear|environment|후면|뒤|외부/i;
        const rxOther = want === 'user'
          ? /back|rear|environment|후면|뒤|외부/i
          : /front|user|selfie|전면|앞|셀피/i;
        if (rxWant.test(label)) return true;
        if (rxOther.test(label)) return false;
        return null;
      } catch { return null; }
    };
    try {
      // 단일 카메라 기기 가드 — PC·일부 보안 기기 등.
      let camList = cameras;
      try {
        const enumList = await navigator.mediaDevices.enumerateDevices();
        camList = enumList.filter((d) => d.kind === 'videoinput');
      } catch {}
      if (!camList || camList.length < 2) {
        toast.info('전환할 다른 카메라가 없어요.');
        return;
      }

      const r = roomRef.current;
      const inLiveWithRoom = phase === 'live' && !!r;

      // ── 실 LIVE 경로 · LiveKit LocalParticipant track replace ─────────
      if (inLiveWithRoom) {
        await r.localParticipant.setCameraEnabled(false);
        await new Promise((res) => setTimeout(res, 300));
        try {
          await r.localParticipant.setCameraEnabled(true, { facingMode: { exact: nextFacing } });
        } catch (facingErr) {
          if (facingErr?.name === 'OverconstrainedError' || facingErr?.name === 'NotFoundError') {
            const devId = await _pickDeviceByFacing(nextFacing);
            if (!devId) throw facingErr;
            await r.localParticipant.setCameraEnabled(true, { deviceId: { exact: devId } });
          } else {
            throw facingErr;
          }
        }
        // 실 track 검증 — 잘못된 카메라면 deviceId label heuristic 로 재전환.
        let camPub = r.localParticipant.getTrackPublication(Track.Source.Camera);
        let mst = (camPub?.videoTrack || camPub?.track)?.mediaStreamTrack;
        let verdict = _verifyFacing(mst, nextFacing);
        if (verdict === false) {
          const devId = await _pickDeviceByFacing(nextFacing);
          if (devId) {
            await r.localParticipant.setCameraEnabled(false);
            await new Promise((res) => setTimeout(res, 200));
            await r.localParticipant.setCameraEnabled(true, { deviceId: { exact: devId } });
            camPub = r.localParticipant.getTrackPublication(Track.Source.Camera);
            mst = (camPub?.videoTrack || camPub?.track)?.mediaStreamTrack;
            verdict = _verifyFacing(mst, nextFacing);
          }
        }
        _attachLocalCamera(r);
        if (verdict === false) {
          toast.error('카메라 전환 실패 · 기기가 요청한 카메라를 지원하지 않아요.');
          return; // facing state 는 원복 (set 하지 않음)
        }
        setFacing(nextFacing);
        setCameraId('');
        return;
      }

      // ── Preview / 테스트 LIVE 경로 · getUserMedia replace via streamRef ─
      // 테스트 LIVE (phase==='live' + roomRef null) 도 여기로 fallthrough 하여
      // 실제 갤럭시 하드웨어 전환을 수행한다.  LiveKit publish 는 없어 외부 송출 LOCK 유지.
      if (previewOn || phase === 'live') {
        await startPreview({ facing: nextFacing, cameraId: '' });
        let vt = streamRef.current?.getVideoTracks?.()[0];
        if (!vt || vt.readyState !== 'live') {
          // startPreview 내부에서 이미 _reportGumError toast 완료.
          // ideal/exact 모두 실패했을 가능성이 있어 deviceId heuristic 로 재시도.
          const devId = await _pickDeviceByFacing(nextFacing);
          if (devId) {
            await startPreview({ cameraId: devId });
            vt = streamRef.current?.getVideoTracks?.()[0];
          }
          if (!vt || vt.readyState !== 'live') return;
        }
        let verdict = _verifyFacing(vt, nextFacing);
        if (verdict === false) {
          const devId = await _pickDeviceByFacing(nextFacing);
          if (devId) {
            await startPreview({ cameraId: devId });
            vt = streamRef.current?.getVideoTracks?.()[0];
            verdict = _verifyFacing(vt, nextFacing);
          }
          if (verdict === false) {
            toast.error('카메라 전환 실패 · 기기가 요청한 카메라를 지원하지 않아요.');
            return; // facing state 는 원복
          }
        }
        setFacing(nextFacing);
        setCameraId('');
      }
    } catch (e) {
      toast.error(`카메라 전환 실패 · ${e?.name || 'Error'}`);
    } finally {
      flippingRef.current = false;
    }
  };

  const toggleMic = () => {
    // Live 중이면 LocalParticipant 트랙, 아니면 preview 스트림.
    const r = roomRef.current;
    if (r) {
      const nextMuted = !micMuted;
      r.localParticipant.setMicrophoneEnabled(!nextMuted).catch(() => {});
      setMicMuted(nextMuted);
      return;
    }
    const s = streamRef.current;
    if (!s) return;
    const track = s.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMicMuted(!track.enabled);
  };

  // ── cleanup ──────────────────────────────────────────────────
  // STEP 8-F · Seller Console local monitor helper — LocalTrackPublished/Reconnected
  // 이벤트로도 재 attach 하여 초기 sync attach 가 놓친 track 을 복구한다.
  const _attachLocalCamera = useCallback((room) => {
    try {
      const lp = room?.localParticipant;
      if (!lp || !videoRef.current) return false;
      const camPub = lp.getTrackPublication(Track.Source.Camera);
      const camTrack = camPub?.videoTrack || camPub?.track;
      if (camTrack) {
        try { videoRef.current.srcObject = null; } catch {}
        camTrack.attach(videoRef.current);
        try { videoRef.current.muted = true; videoRef.current.play?.().catch(() => {}); } catch {}
        return true;
      }
    } catch {}
    return false;
  }, []);

  const disconnectRoom = useCallback(async () => {
    try {
      const r = roomRef.current;
      if (r) {
        // STEP 8-F · detach local camera track from video element before disconnect.
        try {
          const camPub = r.localParticipant?.getTrackPublication(Track.Source.Camera);
          const camTrack = camPub?.videoTrack || camPub?.track;
          if (camTrack && videoRef.current) { try { camTrack.detach(videoRef.current); } catch {} }
        } catch {}
        try { await r.disconnect(); } catch {}
      }
    } catch {}
    roomRef.current = null;
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch {} }
    setPublishState('idle');
  }, []);

  useEffect(() => {
    // STEP STUDIO-STAB-1 · beforeunload during LIVE shows browser confirm
    // dialog instead of silently killing publish + tracks.  The browser will
    // block the unload until the user confirms.  We deliberately do NOT call
    // any async /end API here (beforeunload cannot guarantee async delivery),
    // and we do NOT stop tracks / disconnect until the user has actually
    // committed to leaving.  Explicit [방송 종료] remains the single canonical
    // teardown path.
    const onBefore = (ev) => {
      if (phaseRef.current === 'live') {
        try { ev.preventDefault(); } catch {}
        try { ev.returnValue = ''; } catch {}
        return '';
      }
      // Non-live · safe to release camera resources synchronously.
      stopTracks(); disconnectRoom();
      return undefined;
    };
    window.addEventListener('beforeunload', onBefore);
    return () => {
      window.removeEventListener('beforeunload', onBefore);
      // STEP STUDIO-STAB-1 · Route unmount MUST NOT terminate a LIVE session.
      // React strict-mode double-mount, sheet routing, and Android back-nav
      // all trigger this cleanup.  Previous behaviour called stopTracks +
      // disconnectRoom unconditionally, which left DB streaming_status=live
      // stale and killed LiveKit publish mid-broadcast (the observed root
      // cause of the LiveKit Egress `complete + stream_results=[]` failure).
      // We only release camera/room when the seller is NOT actively live.
      if (phaseRef.current !== 'live') {
        stopTracks(); disconnectRoom();
      }
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [stopTracks, disconnectRoom]);

  // STEP STUDIO-LIFECYCLE-STABILIZATION · Android back / browser history guard.
  // Explicit contract:
  //   - Sheet/modal open  → 뒤로가기 → sheet 만 close · sentinel 재푸시 · LIVE 유지.
  //   - Sheet/modal 없음  → 뒤로가기 → sentinel 만 재푸시 · **/end 절대 호출 X**.
  // 사용자가 실제로 LIVE 를 종료하고 싶으면 반드시 명시적 [방송 종료] 버튼을 눌러야 한다.
  // 이전 patch 에서 사용하던 `window.confirm` → `endLiveRef.current()` 경로는
  // 모바일에서 auto-accept / 오터치로 방송이 종료되는 사고를 유발했다.  제거.
  useEffect(() => {
    if (phase !== 'live') return undefined;
    const SENTINEL = { _lv_studio_live: true };
    try { window.history.pushState(SENTINEL, ''); } catch {}
    const onPop = () => {
      // If any sheet/modal is open, close ONE and stay on the page.
      const anyOpen = moreOpenRef.current || drawerOpenRef.current
        || ytEgressOpenRef.current || ytCommentOpenRef.current
        || filterOpenRef.current || noticeEditOpenRef.current
        || timerOpenRef.current
        || settingsOpenRef.current;
      if (anyOpen) {
        try {
          if (noticeEditOpenRef.current) setNoticeEditOpen(false);
          else if (filterOpenRef.current) setFilterOpen(false);
          else if (ytEgressOpenRef.current) setYtEgressOpen(false);
          else if (ytCommentOpenRef.current) setYtCommentOpen(false);
          else if (timerOpenRef.current) setTimerOpen(false);
          else if (settingsOpenRef.current) setSettingsOpen(false);
          else if (drawerOpenRef.current) setDrawerOpen(false);
          else if (moreOpenRef.current) setMoreOpen(false);
        } catch {}
      }
      // Regardless of overlay state · re-push sentinel · NEVER end broadcast.
      // Broadcast-end is the [방송 종료] 버튼의 canonical 책임.
      try { window.history.pushState(SENTINEL, ''); } catch {}
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [phase]);

  // ── publish to LiveKit (reuses same API endpoints as LiveStreamCard) ──
  // STEP 8-LIVE-CONSOLE-PUBLISH-VIDEO-SOURCE-FIX
  // 모바일 브라우저는 동일 카메라를 두 번 open 하지 못한다.
  // Preview 가 카메라를 점유한 상태로 setCameraEnabled 를 호출하면
  //   "Could not start video source" (NotReadableError) 발생.
  // → publish 직전에 preview MediaStream 을 stop/release 하고,
  //   LiveKit 이 새로 acquire 하도록 constraint 는 심플하게 (OLD LiveStreamCard 와 동일).
  // STEP LIVE-PUBLISHER-GUARD-1 · 기기 고유 claim_id (localStorage 기반 · 세션당 1개).
  //   · 동일 기기의 새로고침/탭 복귀는 같은 claim_id 로 재입장 → 자연스럽게 reconnect.
  //   · 다른 기기는 다른 claim_id → 서버가 409 duplicate_publisher 로 거부.
  //   · UUID 는 crypto.randomUUID 또는 폴백 (brower support 넓음).
  const _getPublisherClaimId = () => {
    const key = 'livemoa_publisher_claim_id';
    try {
      let v = window.localStorage.getItem(key);
      if (!v) {
        v = (window.crypto && window.crypto.randomUUID)
          ? window.crypto.randomUUID()
          : `pc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        window.localStorage.setItem(key, v);
      }
      return v;
    } catch { return `pc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; }
  };
  const publishToLiveKit = async (sid) => {
    if (isTestMode) { toast.info('테스트 모드 · LiveKit publish 호출 차단'); return; }
    setPublishState('connecting');
    let tok;
    try {
      const resp = await api.post(`/seller/streaming/sessions/${sid}/token`, null,
        { headers: { 'X-Publisher-Claim-Id': _getPublisherClaimId() } });
      tok = resp.data;
    } catch (e) {
      // STEP LIVE-PUBLISHER-GUARD-1 · 409 duplicate_publisher 안내 (강제 교체 금지).
      const _detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && (_detail?.code === 'duplicate_publisher')) {
        setPublishState('idle');
        toast.error(_detail.message
          || '다른 기기에서 방송 중입니다. 기존 방송을 종료하거나 연결 상태를 확인한 후 다시 시도해 주세요.');
      }
      throw e;
    }
    const room = new Room({ adaptiveStream: true, dynacast: true, publishDefaults: { simulcast: true } });
    roomRef.current = room;
    room.on(RoomEvent.Reconnecting, () => setPublishState('reconnecting'));
    room.on(RoomEvent.Reconnected, () => {
      setPublishState('publishing');
      // STEP 8-F · reconnect 후 Seller Console 자기 영상 복구.
      _attachLocalCamera(room);
    });
    room.on(RoomEvent.Disconnected, () => {
      setPublishState('idle');
      // STEP LIVE-PUBLISHER-GUARD-1A · Room 완전 끊김 → heartbeat 중단.
      try { if (publisherClaimTimerRef.current) clearInterval(publisherClaimTimerRef.current); } catch {}
      publisherClaimTimerRef.current = null;
    });
    room.on(RoomEvent.ConnectionStateChanged, (st) => {
      if (st === ConnectionState.Disconnected) setPublishState('idle');
    });
    // STEP 8-F · LocalTrackPublished 이벤트로 attach 를 idempotent 하게 보장.
    // setCameraEnabled 직후 track 이 아직 완전히 wired 안 됐을 수 있으므로
    // 이 이벤트를 SoT 로 사용 · 초기 sync attach 는 fast-path 로 유지.
    room.on(RoomEvent.LocalTrackPublished, (pub) => {
      try {
        if (pub?.source === Track.Source.Camera) {
          _attachLocalCamera(room);
        }
      } catch {}
    });
    // STEP 8-I-4 · Social Proof activity 를 Seller 도 실시간으로 본다.
    // Buyer 화면과 동일 Data Channel (topic="social_proof") 을 subscribe →
    // sharedMessages 에 `_activity: true` synthetic 메시지로 push.
    // 신규 poll/API/DB 0건 · 오직 Data Channel + React state.
    room.on(RoomEvent.DataReceived, (payload, _p, _k, topic) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (topic !== 'social_proof' && msg?.type !== 'social_proof') return;
        const pid = msg.proof_id;
        if (!pid || _chatSeenIdsRef.current.has(pid)) return;
        _chatSeenIdsRef.current.add(pid);
        const activity = {
          message_id: pid,
          _activity: true,
          event: msg.event || 'order_created',
          text: msg.text || '',
          created_at: new Date(msg.ts || Date.now()).toISOString(),
          author_role: 'activity',
          source: 'livemoa',
        };
        setSharedMessages((prev) => [...prev, activity].slice(-200));
      } catch { /* soft */ }
    });
    await room.connect(tok.url, tok.token);

    // 🔴 CRITICAL · publish 이전에 preview 카메라/마이크 track 을 완전히 release.
    // 모바일 (Android Chrome/Samsung Internet, iOS Safari 15+) 은 동일 물리 카메라의
    // 이중 open 을 거부하고 NotReadableError("Could not start video source") 반환.
    try { streamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch {} }); } catch {}
    streamRef.current = null;
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch {} }

    // LiveKit 이 새로 카메라를 acquire. constraint 는 사용자가 명시 선택한 deviceId 만 전달 ·
    // 그 외에는 undefined (OLD LiveStreamCard 와 동일 · 브라우저 default = 최대 호환).
    const camOpts = cameraId ? { deviceId: { exact: cameraId } } : undefined;
    const micOpts = micId ? { deviceId: { exact: micId } } : undefined;
    await room.localParticipant.setCameraEnabled(true, camOpts);
    await room.localParticipant.setMicrophoneEnabled(!micMuted, micOpts);
    // STEP 8-F · fast-path attach (안전망은 LocalTrackPublished 리스너 위에서 처리).
    _attachLocalCamera(room);
    setPublishState('publishing');
    // STEP LIVE-PUBLISHER-GUARD-1A · §3 · publisher-claim heartbeat 시작.
    //   · setInterval 주기는 서버가 응답한 heartbeat_seconds (lease/3 · 최소 10초).
    //   · heartbeat 실패 (409 claim_lost / 404) 시 타이머 정리 — 다른 기기에게 점유권 양보.
    //   · 404 (이미 종료) 는 자연스러운 종료 흐름으로 간주.
    //   · claim 헤더는 /token 과 동일한 localStorage UUID REUSE.
    try { if (publisherClaimTimerRef.current) clearInterval(publisherClaimTimerRef.current); } catch {}
    const _hbInterval = Math.max(5, (tok?.publisher_claim_heartbeat_seconds || 10)) * 1000;
    publisherClaimTimerRef.current = setInterval(async () => {
      try {
        await api.patch(`/seller/streaming/sessions/${sid}/publisher-claim`, null,
          { headers: { 'X-Publisher-Claim-Id': _getPublisherClaimId() } });
      } catch (e) {
        const _st = e?.response?.status;
        if (_st === 409 || _st === 404) {
          try { clearInterval(publisherClaimTimerRef.current); } catch {}
          publisherClaimTimerRef.current = null;
          if (_st === 409) toast.error('다른 기기가 publisher 를 점유했습니다. 방송 상태를 확인해주세요.');
        }
      }
    }, _hbInterval);
  };

  // ── start broadcast (3·2·1 → publish) ────────────────────────
  const goLive = async () => {
    // STEP STUDIO-TEST-LIVE-AND-CONSOLE-FIX-1 · §A — 테스트 모드 가상 LIVE.
    //   실제 LiveKit session · publish · egress 호출 없이 UI 만 live 로 전환하여
    //   판매자가 방송 중 콘솔(상품/Overlay/댓글/타이머/더보기)을 매번 방송 예약
    //   없이 확인할 수 있도록 한다. 모든 WRITE API 는 호출하지 않는다.
    if (isTestMode) {
      if (busy) return;
      if (!previewOn) { toast.error('먼저 카메라를 켜주세요.'); return; }
      setBusy(true);
      try {
        setPhase('countdown');
        for (let n = 3; n >= 1; n--) {
          setCountdown(n);
          await new Promise((r) => setTimeout(r, 900));
        }
        // 가상 세션 — session_id 가 '__TEST_SESSION__' sentinel 이므로 어떤
        // backend 조회도 404 로 soft-fail (기존 catch 구문이 흡수).
        setSession({
          session_id: '__TEST_SESSION__',
          streaming_status: 'live',
          __test: true,
        });
        // STEP STUDIO-MOBILE-PRODUCT-PANEL-1 · 테스트 상품 10개를 liveCtx 로 공급.
        //   · 동일 ProductsTab 컴포넌트 재사용을 위해 실 응답과 동일 shape 유지.
        setLiveCtx((c) => (c?.live_products ? c : _buildTestLiveCtx()));
        setElapsed(0);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
        setPhase('live');
        toast.info('🧪 테스트 LIVE · 가상 화면 · 실제 송출/주문 없음');
      } finally { setBusy(false); }
      return;
    }
    if (busy) return;
    if (!previewOn) { toast.error('먼저 카메라를 켜주세요.'); return; }
    setBusy(true);
    try {
      // Countdown 3 · 2 · 1
      setPhase('countdown');
      for (let n = 3; n >= 1; n--) {
        setCountdown(n);
        await new Promise((r) => setTimeout(r, 900));
      }
      // create + start · reuse existing streaming API
      // STEP 8-LIVE-PUBLISH-TO-BUYER-VIEWER-SYNC
      // preferredSid 는 URL ?sid= 로 넘어오는 값 · 실제로는 pre-registered `schedule_id` 임.
      // STEP 8-LIVE-SESSION-STATE-AND-BUYER-ROUTE-FIX · 다른 기기에서 이미 publishing 중이면
      // 재-publish 하지 않고 안내 (LiveKit 이중 publisher 방지 · Buyer 무영향).
      let sid = null;
      const linkScheduleId = (preferredSid && preferredSid.startsWith('schedule_')) ? preferredSid : null;
      if (session?.session_id && session?.streaming_status !== 'ended') {
        sid = session.session_id;
      } else {
        const { data: created } = await api.post('/seller/streaming/sessions',
          linkScheduleId ? { schedule_id: linkScheduleId } : {});
        sid = created.session_id;
        setSession(created);
      }
      // LIVE-RECORDING-ASPECT-RATIO-9X16-1 · 모바일 9:16 기본 · PC 는 기존 16:9/9:16 토글.
      const startOrientation = (isLgView && pcAspect === '16:9') ? 'landscape' : 'portrait';
      const { data: live } = await api.post(`/seller/streaming/sessions/${sid}/start`, { orientation: startOrientation });
      setSession(live);
      setElapsed(0);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);

      if (cfg?.mode === 'livekit' && cfg?.livekit_configured) {
        try {
          await publishToLiveKit(live.session_id);
          toast.success('LIVEMOA LIVE 송출 시작');
        } catch (e) {
          setPublishState('error');
          setPhase('preview');
          toast.error(`연결 실패 · ${e?.message?.slice(0, 60) || 'unknown'}`);
          return;
        }
      } else {
        toast.info('Mock 모드 · 실 송출 없이 프리뷰만 유지됩니다.');
      }
      setPhase('live');
    } catch (e) {
      setPhase('preview');
      toast.error('방송 시작 실패');
    } finally { setBusy(false); }
  };

  const endLive = async () => {
    if (isTestMode) {
      // STEP STUDIO-TEST-LIVE-AND-CONSOLE-FIX-1 · §A-4 — 테스트 LIVE 종료.
      //   실제 /end API 는 호출하지 않고 UI state + camera/mic + timer 만 정리.
      try { stopTracks(); } catch {}
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setSession(null);
      // LIVE-TEST-STUDIO-CONSOLE-PARITY-1 · 테스트 상품은 공유 세션에 유지 (이 화면을 나가며 로컬만 정리).
      setLiveCtx(null);
      setElapsed(0);
      setPhase('preview');
      navigate('/seller/dashboard');
      return;
    }
    if (busy || !session?.session_id) return;
    if (!window.confirm('방송을 종료할까요?')) return;
    setBusy(true);
    try {
      await disconnectRoom();
      // STEP STUDIO-STAB-3 · Also fully release camera/mic MediaStreamTracks
      // so the OS/browser marks the device as free.  Previous behaviour left
      // preview stream refs alive → next broadcast start showed "Could not
      // start video source" until app restart.
      try { stopTracks(); } catch {}
      const { data } = await api.post(`/seller/streaming/sessions/${session.session_id}/end`);
      setSession(data);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setPhase('ended');
      // LIVE-SESSION-LIFECYCLE-P0-FIX-1 · 남은 세션이 있으면 정상 종료로 표시하지 않음.
      if (data?.lifecycle?.summary === 'partial' || (data?.lifecycle?.remaining_open || []).length) {
        toast.error('방송 세션 일부가 종료되지 않았어요 · 관리자 긴급 종료가 필요할 수 있어요.', { duration: 8000 });
      } else {
        toast.info(`방송 종료 · ${_fmt(data?.duration_seconds || 0)}`);
      }
    } catch (e) { toast.error('방송 종료 실패'); }
    finally { setBusy(false); }
  };
  // STEP STUDIO-STAB-TDZ-HOTFIX · mirror useEffect 는 참조 state 가 모두 선언된
  // 이후 위치로 이동함.  실제 블록은 noticeChipOpen 선언 아래에 있음 (line ~2495).
  // 여기서는 endLive 의 identity 변화를 ref 로만 브릿지 (다른 state 참조 없음 · TDZ 안전).
  useEffect(() => { endLiveRef.current = endLive; });

  const isLive = phase === 'live' && session?.streaming_status === 'live';
  const isOwner = true;   // 이 페이지는 SellerOnlyRoute → 항상 owner

  // STEP LIVE-STUDIO-PRESET-PREVIEW-REAL-1 · PRESET-TDZ-HOTFIX
  //   · 참조 state (scheduleId · liveCtx · isLive) 가 모두 선언된 뒤 위치로 이동
  //     (이전 2365 위치는 TDZ 오류로 Studio 화면이 중단되는 P0 버그 유발).
  //   · 서버 persist: `PUT /seller/broadcasts/{sid}` + `preset_draft` 필드 REUSE
  //     (신규 collection·endpoint 0 · live_schedules 1 field 만 additive).
  //   · localStorage 는 오프라인/즉시 반영용 캐시로만 사용 — SoT 는 서버.
  //   · PcPresetOverlay 로 video 위 로컬 overlay (iframe 미사용 · 흰 화면 재발 X).
  //   · LIVE 시작 + active product 준비 시 1회 자동 적용 (canonical ops.applyPreset REUSE).
  const _presetDraftKey = scheduleId ? `livemoa_preset_draft::${scheduleId}` : null;
  // STEP STUDIO-MOBILE-UI-3 · draftPreset default = 'mini' (A안).
  //   · 이전 null → 서버가 canonicalize_preset(None) 로 'default' 선택 → video 전체 가림.
  //   · 'mini' 로 바꾸면 PresetOverlay 가 작은 상품 pill 로 render → 영상 가림 최소.
  //   · localStorage 에 사용자가 선택한 값이 있으면 그 값을 복원 (사용자 선택 존중).
  const [_draftPresetState, _setDraftPresetRaw] = useState(() => {
    try {
      const saved = localStorage.getItem('livemoa.studio.draftPreset');
      if (saved && ['default','full','price','options','mini'].includes(saved)) return saved;
    } catch { /* ignore */ }
    return 'mini';
  });
  const [autoApplied, setAutoApplied] = useState(false);
  // STEP STUDIO-OVERLAY-DRAG-RESIZE-1 · Overlay 자유 배치 (방송별·판매자 기본 · 테스트 모드는 로컬만).
  const overlayLayout = useOverlayLayout({ scheduleId, isTestMode, liveCtx });
  // COMBINED-PRESET-1 · 프리셋 불러오기: 이 방송(preset_draft) → 판매자 기본(layout.preset) → 미니 프리셋.
  const [hasBroadcastPreset, setHasBroadcastPreset] = useState(false);
  const draftPreset = hasBroadcastPreset ? _draftPresetState : (overlayLayout.layout?.preset || 'mini');
  const [isLgView, setIsLgView] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const h = () => setIsLgView(mq.matches);
    mq.addEventListener?.('change', h);
    return () => mq.removeEventListener?.('change', h);
  }, []);
  const overlayOrient = isLgView && pcAspect === '16:9' ? '16:9' : '9:16';
  // PC-STUDIO-SYNC-RESPONSIVE-FIX-1 · PC 영상 박스를 사용 가능 높이·폭에 맞춰 선택 비율(16:9 / 9:16)로 맞춤 (왜곡·잘림 방지 · 스트림 미접촉).
  const videoBoxRef = useRef(null);
  const [fitBox, setFitBox] = useState(null);
  useEffect(() => {
    const el = videoBoxRef.current;
    if (!el || !isLgView || typeof ResizeObserver === 'undefined') { setFitBox(null); return undefined; }
    const r = pcAspect === '16:9' ? 16 / 9 : 9 / 16;
    const calc = () => {
      const W = el.clientWidth; const H = el.clientHeight;
      if (!W || !H) return;
      const w = Math.min(W, H * r);
      setFitBox((p) => (p && p.w === Math.floor(w) && p.h === Math.floor(w / r) ? p : { w: Math.floor(w), h: Math.floor(w / r) }));
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isLgView, pcAspect]);
  const showCombo = useOverlayShowCombo({ scheduleId, liveCtx, setLiveCtx, isLive, isTestMode });
  // MOBILE-FINAL-FIX-1 · 지난 상품 슬라이더 (기존 로직 그대로 Console 로 lift · 판매자 미리보기 전용).
  const [pastSliderMode, setPastSliderMode] = useState('off');       // 'off' | 'once' | 'loop'
  const [pastSliderEffect, setPastSliderEffect] = useState('slide'); // 'slide' | 'fade'
  const [pastSliderInterval, setPastSliderInterval] = useState(8);   // 5 | 8 | 10 (sec)
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  // CONSOLIDATION-1 · 외부 송출 Overlay 판매자 미리보기 (세션 전용 · 저장 X · 송출 설정 무변경).
  const [extPreview, setExtPreview] = useState(false);
  const pastSlider = { pastSliderMode, setPastSliderMode, pastSliderEffect, setPastSliderEffect, pastSliderInterval,
    setPastSliderInterval, previewPlaying, setPreviewPlaying, previewIndex, setPreviewIndex };
  const pastList = useMemo(() => pastListOf(liveCtx), [liveCtx]);
  const stepSlide = () => setPreviewIndex((i) => {
    const next = i + 1;
    if (next >= pastList.length) {
      if (pastSliderMode === 'loop') return 0;
      setPreviewPlaying(false);
      return i;
    }
    return next;
  });
  const stepSlideRef = useRef(stepSlide);
  stepSlideRef.current = stepSlide;
  // STUDIO-PC-QUICKADD-SLIDE-SYNC-1 · TEST 공유 세션으로 모바일·PC Studio·PC 보조 콘솔 슬라이드 상태 공유.
  const { leader: slideLeader } = usePastSlideSync({
    enabled: isTestMode, ready: testShare.status !== 'idle', ctx: liveCtx, setCtx: setLiveCtx,
    local: { mode: pastSliderMode, playing: previewPlaying, index: previewIndex, interval: pastSliderInterval, effect: pastSliderEffect },
    apply: (s) => { setPastSliderMode(s.mode); setPreviewPlaying(s.playing); setPreviewIndex(s.index); setPastSliderInterval(s.interval); setPastSliderEffect(s.effect); },
    onTakeover: () => stepSlideRef.current(),
  });
  //   · once: 끝까지 가면 자동 stop.  loop: wrap-around.  (공유 중에는 마지막 조작 기기만 자동 넘김)
  useEffect(() => {
    if (!previewPlaying || !slideLeader) return undefined;
    if (pastList.length === 0) { setPreviewPlaying(false); return undefined; }
    if (pastList.length === 1) { return undefined; }
    const t = setInterval(() => stepSlideRef.current(), Math.max(1, pastSliderInterval) * 1000);
    return () => clearInterval(t);
  }, [previewPlaying, pastSliderMode, pastSliderInterval, pastList, slideLeader]);
  const pastPreview = previewPlaying && pastSliderMode !== 'off' && pastList.length
    ? { product: pastList[previewIndex % pastList.length], index: previewIndex % pastList.length,
        total: pastList.length, effect: pastSliderEffect }
    // LIVE-EXTERNAL-OVERLAY-PREVIEW-FINAL-1 · 송출 미리보기: 실제 지난 상품이 있으면 첫 항목 표시 (기존 슬라이더 설정 존중).
    : (extPreview && pastSliderMode !== 'off' && pastList.length
      ? { product: pastList[0], index: 0, total: pastList.length, effect: pastSliderEffect } : null);
  const extActive = (egressStatus?.destinations || []).some((d) => ['active', 'starting'].includes(d.status));
  const showCurrentCard = isLive && !!liveCtx?.active_product && !drawerOpen && !moreOpen && !filterOpen && !timerOpen
    && !settingsOpen && !overlaySettingsOpen && controlsVisible && !overlayLayout.editing && !isLgView;
  // STEP STUDIO-TEST-LIVE-AND-CONSOLE-FIX-1 · §A-4 — "나가기": 카메라/마이크/타이머 정리 후 복귀 (배너·칩 공용).
  const exitTestMode = () => {
    try { stopTracks(); } catch {}
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setSession(null);
    setLiveCtx(null);
    setElapsed(0);
    setPhase('preview');
    navigate('/seller/dashboard');
  };
  const startLayoutEdit = () => {
    if (isLive && !window.confirm('LIVE 중입니다. Overlay 배치를 편집할까요?\n\'배치 완료 · 저장\'을 누르면 방송 화면에 반영됩니다.')) return;
    setSettingsOpen(false);
    setOverlaySettingsOpen(false);
    overlayLayout.begin();
    setTimeout(() => {
      try { document.querySelector('[data-testid="studio-overlay-stage"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
    }, 60);
  };
  // Hydrate order: 1) 서버 (/seller/broadcasts/today 매칭) · 2) localStorage cache fallback.
  useEffect(() => {
    if (!_presetDraftKey || !scheduleId) {
      _setDraftPresetRaw(null); setAutoApplied(false); return;
    }
    let alive = true;
    // 즉시 localStorage cache 로 pre-fill (렌더 깜빡임 방지)
    try {
      const v = window.localStorage.getItem(_presetDraftKey);
      if (v && ['default','full','price','options','mini'].includes(v)) {
        _setDraftPresetRaw(v);
      } else {
        _setDraftPresetRaw(null);
      }
    } catch { _setDraftPresetRaw(null); }
    setAutoApplied(false);
    // 서버가 SoT · today 목록에서 preset_draft 를 복원.
    (async () => {
      try {
        const { data } = await api.get('/seller/broadcasts/today');
        if (!alive) return;
        const match = (Array.isArray(data) ? data : []).find((b) => b.schedule_id === scheduleId);
        const serverVal = match?.preset_draft || null;
        if (serverVal && ['default','full','price','options','mini'].includes(serverVal)) {
          _setDraftPresetRaw(serverVal);
          setHasBroadcastPreset(true);
          try { window.localStorage.setItem(_presetDraftKey, serverVal); } catch { /* ignore */ }
        } else if (serverVal === null || serverVal === undefined || serverVal === '') {
          // 서버가 명시적으로 비워져 있으면 cache 도 비움 (팀 전체 공유 전제).
          if (match && 'preset_draft' in match) {
            _setDraftPresetRaw(null);
            setHasBroadcastPreset(false);
            try { window.localStorage.removeItem(_presetDraftKey); } catch { /* ignore */ }
          }
        }
      } catch { /* soft · localStorage cache fallback 유지 */ }
    })();
    return () => { alive = false; };
  }, [_presetDraftKey, scheduleId]);
  const _saveBroadcastPreset = (v) => {
    _setDraftPresetRaw(v);
    setHasBroadcastPreset(!!v);
    if (!_presetDraftKey) return;
    try {
      if (v) window.localStorage.setItem(_presetDraftKey, v);
      else window.localStorage.removeItem(_presetDraftKey);
    } catch { /* ignore quota/private-mode */ }
    // 서버 persist (fire-and-forget · 실패해도 UI 는 유지 · 다음 재접속 시 서버값 우선).
    if (!scheduleId) return;
    // STEP STUDIO-TEST-LIVE-AND-CONSOLE-FIX-1 · §A — 테스트 모드에서는 서버 persist 금지.
    if (isTestMode) return;
    (async () => {
      try {
        await api.put(`/seller/broadcasts/${scheduleId}`, { preset_draft: v || null });
      } catch { /* soft · 서버 저장 실패해도 localStorage cache 는 유지 */ }
    })();
  };
  // 저장 범위: 판매자 기본 → layout.preset (테스트는 로컬) · 이 방송만 → 기존 preset_draft.
  const setDraftPreset = (v) => {
    if (overlayLayout.saveScope === 'broadcast' && !isTestMode) { _saveBroadcastPreset(v); return; }
    setHasBroadcastPreset(false);
    overlayLayout.setTop(overlayOrient, { preset: v });
  };
  const _presetAutoOps = useLiveOperations(scheduleId);
  // STEP LIVE-STUDIO-PRESET-UNIFIED-1 · P3 · 상품 변경 후에도 draftPreset 자동 재적용.
  //   · 이전 스냅샷 autoApplied 는 단일 boolean 이어서 상품이 바뀌어도 재발동 X.
  //   · ref 로 "마지막으로 preset 을 밀어넣은 (productId, preset) 조합" 을 기억 →
  //     조합이 바뀌면 재발동 · 서버의 preset 과 이미 일치하면 skip (중복 호출 금지).
  const _lastAppliedRef = useRef({ pid: null, preset: null });
  useEffect(() => {
    const activePid = liveCtx?.active_product?.product_id;
    const curPreset = liveCtx?.active_product_display?.preset;
    // 실제 LIVE 자동 적용은 기존대로 이 방송 preset_draft 만 사용 (송출 흐름 불변).
    const draftPreset = _draftPresetState;
    if (!isLive || !scheduleId || !activePid || !draftPreset) return;
    // 서버에 이미 반영되어 있으면 더 이상 호출하지 않음.
    if (curPreset === draftPreset) {
      _lastAppliedRef.current = { pid: activePid, preset: draftPreset };
      setAutoApplied(true);
      return;
    }
    // 동일 (pid, preset) 조합에 대해서는 이미 1회 호출했으면 재호출 금지
    //   (서버 polling 반영 지연 중 중복 호출 방지).
    if (_lastAppliedRef.current.pid === activePid
        && _lastAppliedRef.current.preset === draftPreset) return;
    _lastAppliedRef.current = { pid: activePid, preset: draftPreset };
    (async () => {
      try {
        await _presetAutoOps.applyPreset(activePid, draftPreset);
        setAutoApplied(true);
      } catch (e) { /* best-effort · 수동 재선택 가능 */ }
    })();
  }, [isLive, scheduleId, liveCtx?.active_product?.product_id, liveCtx?.active_product_display?.preset, _draftPresetState, _presetAutoOps]);

  const openTab = (key) => {
    setActiveTab(key);
    setDrawerOpen(true);
  };

  // ── layout ────────────────────────────────────────────────
  // STEP 8-I-5 (Group C) · Bright Commerce UI:
  //   · Root/Sidebar/Header/Tabs/BottomSheet 를 밝은 톤(#F7F8FA)으로 재배치.
  //   · Video 영역/Overlay 는 dark 유지 (LOCK). Logic/handler 100% REUSE.
  // STEP 8-I-5A · Group C FINAL UX POLISH:
  //   · Header: 💬/🎉 metric 제거 → 👁 viewer_count 만 (LiveKit 실 참가자 · seller 제외).
  //   · 상단 live_notice bar (📢) · Buyer/Seller 동일 데이터 REUSE.
  //   · Video 하단 "지금 소개중" chip (active_product REUSE).
  //   · Comment Overlay bottom offset ↑ (control bar 뒤로 잘림 방지).
  const viewerCount = liveCtx?.viewer_count;
  const activeLiveProduct = liveCtx?.active_product || null;
  // STUDIO-SELF-NOTICE-IMAGE-REMOVE-1 · TEST 자사라이브 공지는 문구 전용 (이미지 참조 보존 · 비노출).
  const _rawNotice = liveCtx?.live_notice || null;
  const liveNotice = _rawNotice?.image_url ? { ..._rawNotice, image_url: null, image_size: null } : _rawNotice;
  // REAL-CONTROL-INTEGRATION-1 · 실제 방송: 판매자별 타이머 문구·외부공지 + 방송 API 연결 (TEST 는 기존 공유 세션 그대로).
  const liveSid = session?.session_id || null;
  const livePresets = useLivePresets(!isTestMode && !!liveSid);
  const realCtx = realPresetCtx(livePresets.presets, liveCtx?.external_notice?.id, liveCtx?.timer);
  const setExtLocal = (r) => setLiveCtx((c) => (c ? { ...c, external_notice: r } : c));
  const realApply = makeRealApply({ ctx: realCtx, save: livePresets.save, sessionId: liveSid, onSelected: setExtLocal });
  const setNoticeLocal = (ln) => setLiveCtx((c) => (c ? { ...c, live_notice: ln } : c));
  const realLiveOps = useLiveOperations(isTestMode ? null : scheduleId);
  const remoteOps = isTestMode ? {
    timer: (a, x) => setLiveCtx((c) => (c ? presetOps.timer(c, a, x) : c)),
    notice: (n) => setNoticeLocal(n ? { text: n.text.trim(), notice_id: n.notice_id, updated_at: new Date().toISOString() } : null),
    ext: (id) => setLiveCtx((c) => (c ? { ...c, external_notice_selected: id } : c)),
  } : {
    timer: (a, x) => realTimer(scheduleId, a, x),
    notice: (n) => realNotice(liveSid, n).then((r) => { if (r !== undefined) setNoticeLocal(r); }),
    ext: (id) => realExternal(liveSid, id).then((r) => { if (r !== undefined) setExtLocal(r); }),
  };
  const presetCtx = isTestMode ? liveCtx : realCtx;
  const presetApply = isTestMode ? (fn) => setLiveCtx((c) => (c ? fn(c) : c)) : realApply;
  const presetStart = isTestMode ? undefined : (p) => realTimer(scheduleId, 'start', { duration_seconds: Math.round(p.minutes * 60), label: p.label });
  const showPresets = isTestMode || !!livePresets.presets;
  // NOTICE-PREVIEW-FIX-1 · 공지 내리면 미리보기 접힘 상태로 초기화 (재등록 시 기본 접힘).
  useEffect(() => { if (!liveNotice?.text) setNoticePreviewOpen(false); }, [liveNotice?.text]);

  // STEP 8-I-5A · notice 편집 상태 (모달)
  const [noticeEditOpen, setNoticeEditOpen] = useState(false);
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · Shared Quick Register modal state.
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  // STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 로컬 카메라 프레임 캡처 (preview video).
  //   · 기존 `videoRef` (LiveKit local track attached · publish 중에도 유지) 를 재사용.
  //   · Canvas drawImage → JPEG Blob → File.  Overlay/UI/댓글은 포함되지 않음 (video 요소만 캡처).
  //   · 카메라 재시작/중복 getUserMedia 없음.
  const _captureCameraFrame = useCallback(async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight || v.readyState < 2) {
      throw new Error('카메라 미리보기를 사용할 수 없어요. 카메라를 먼저 켜주세요.');
    }
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
    if (!blob) throw new Error('캡처 변환 실패');
    return new File([blob], `quick_${Date.now()}.jpg`, { type: 'image/jpeg' });
  }, []);

  // STEP STUDIO-MOBILE-QUICK-REGISTER-1 · rail 퀵등록 버튼 핸들러.
  //   · LIVE 중일 때만 유효 (previewOn 체크로 테스트 모드 포함 공통).
  //   · 3·2·1 카운트다운 → _captureCameraFrame → setQuickAddOpen(true).
  //   · 실패 시 toast + 기존 '사진 업로드/바로 촬영' 경로를 modal 안에서 사용 가능.
  const startQuickRegister = useCallback(async () => {
    // 버튼·셀터 동시 입력/연타/길게 누름 → 한 번만 (동일 tick 중복까지 ref 로 차단).
    if (quickCountdown !== null || shutterBusyRef.current || quickAddOpen) return;
    if (!previewOn) { toast.error('먼저 카메라를 켜주세요.'); return; }
    shutterBusyRef.current = true;
    try {
      for (let n = 3; n >= 1; n--) {
        setQuickCountdown(n);
        await new Promise((r) => setTimeout(r, 850));
      }
      setQuickCountdown(0);
      await new Promise((r) => setTimeout(r, 120));
      let file = null;
      try { file = await _captureCameraFrame(); }
      catch (e) {
        toast.error(e?.message || '캡처 실패 · 사진 업로드로 등록해 주세요.');
      }
      if (file) await deliverCapture(file);
      else { setQuickCaptureFile(null); setQuickAddOpen(true); }
    } finally { shutterBusyRef.current = false; setQuickCountdown(null); }
  }, [quickCountdown, quickAddOpen, previewOn, _captureCameraFrame, quickRegMode, scheduleId, isTestMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // STUDIO-BLUETOOTH-QUICK-REGISTER-1 · 캡처 전달: 직접 = 기존 빠른등록 창 · 보조자 = 기존 업로드 → 방송별 전달함.
  //   전달 실패·보조자 부재 시에도 같은 사진으로 '직접 등록' 가능(유실 없음). 소개 상품은 바꾸지 않음.
  const openDirect = (file) => { setQuickCaptureFile(file); setQuickAddOpen(true); };
  const deliverCapture = async (file) => {
    if (quickRegMode !== 'assistant') { openDirect(file); return; }
    const direct = { label: '직접 등록', onClick: () => openDirect(file) };
    // LIVE-TEST-STUDIO-CONSOLE-PARITY-1 · TEST 는 공유 세션 전달함(__TEST__)으로 실제 전달.
    const capSid = isTestMode ? TEST_SID : scheduleId;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'product');
      const up = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post(`/seller/live/${capSid}/captures`, {
        image_url: up.data.url, client_key: `cap:${capSid}:${Date.now()}`, source: isTestMode ? 'studio_test' : 'studio',
      });
      toast.success(isTestMode ? '상품 캡처 전달됨 · TEST 보조 콘솔에서 등록' : '상품 캡처 전달됨 · 보조자 콘솔에서 등록', { action: direct, duration: 6000 });
    } catch (e) {
      toast.error('전달 실패 · 직접 등록으로 열어요');
      openDirect(file);
    }
  };

  // STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 테스트 모드 가상 등록 — WRITE 0.
  //   · 실제 /quick-product 호출 대신 externalCtx(=liveCtx) 에 상품을 push.
  //   · 이미지 서버 업로드 안 함: blob preview URL 을 그대로 사용.
  const handleTestQuickRegister = useCallback(async (body) => {
    const nextSlot = ((liveCtx?.live_products || []).length) + 1;
    const newProd = {
      product_id: `test_product_quick_${Date.now()}`,
      slot_number: nextSlot,
      name: body.name,
      sale_price: body.sale_price,
      image: body.image || null,
      sale_status: '판매 중',
      is_introduced: !!body.announce_now,
      available_stock: body.stock != null ? body.stock : 999,
      stock_status: body.stock != null ? 'tracked' : 'infinite',
    };
    setLiveCtx((c) => {
      const list = (c?.live_products || []);
      return {
        ...(c || {}),
        live_products: [...list, newProd],
        active_product: body.announce_now ? newProd : (c?.active_product || null),
        active_product_display: body.announce_now
          ? { preset: 'default', position: 'bottom', mode: 'visible' }
          : (c?.active_product_display || null),
      };
    });
    return { product: newProd, slot_number: nextSlot, announced: !!body.announce_now };
  }, [liveCtx]);
  // STEP LIVE-STUDIO-PC-PRODUCT-SELECT-UI-1 · 공통 picker (전체 상품에서 등록) 열림 상태.
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · 공통 Notice editor hook (Studio 와 동일 hook).
  // state + /uploads + PUT/DELETE /seller/streaming/sessions/{sid}/notice 를
  // hook 이 담당. Console 은 modal 전용 side-effect (setLiveCtx + close) 만 유지.
  const _noticeEditor = useNoticeEditorAutoHydrate(
    session?.session_id || null,
    liveCtx?.live_notice || null,
    {
      onSaved: (n) => {
        setLiveCtx((prev) => ({ ...(prev || {}), live_notice: n }));
        setNoticeEditOpen(false);
      },
      localMode: isTestMode,
    },
  );
  const noticeDraft = _noticeEditor.draft;
  const setNoticeDraft = _noticeEditor.setDraft;
  const noticeBusy = _noticeEditor.busy;
  const noticeImageUrl = _noticeEditor.imageUrl;
  const setNoticeImageUrl = _noticeEditor.setImageUrl;
  const noticeImageSize = _noticeEditor.imageSize;
  const setNoticeImageSize = _noticeEditor.setImageSize;
  const noticeImageUploading = _noticeEditor.imageUploading;
  useEffect(() => { if (noticeImageUrl) setNoticeImageUrl(''); }, [noticeImageUrl, setNoticeImageUrl]);
  // STEP 8-I-5A Micro Fix · compact chip expand/collapse (Modal X · inline).
  const [noticeChipOpen, setNoticeChipOpen] = useState(false);
  // STEP STUDIO-STAB-TDZ-HOTFIX · 이전 STUDIO-STAB-2 패치가 이 mirror useEffect
  // 블록을 endLive 정의부 바로 아래(line ~2455)에 배치했었는데, `noticeEditOpen`
  // state 선언(line ~2485)이 그보다 아래에 있어 초기 render 에서 TDZ (temporal
  // dead zone) 로 `Cannot access 'noticeEditOpen' before initialization` 발생.
  // Fix: 이 블록을 참조하는 모든 state 선언 이후 (noticeChipOpen 아래) 로 이동.
  // 로직/의미 100% 동일 · 위치만 이동.
  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { moreOpenRef.current = moreOpen; }, [moreOpen]);
  useEffect(() => { drawerOpenRef.current = drawerOpen; }, [drawerOpen]);
  useEffect(() => { ytEgressOpenRef.current = ytEgressOpen; }, [ytEgressOpen]);
  useEffect(() => { ytCommentOpenRef.current = ytCommentOpen; }, [ytCommentOpen]);
  useEffect(() => { filterOpenRef.current = filterOpen; }, [filterOpen]);
  useEffect(() => { noticeEditOpenRef.current = noticeEditOpen; }, [noticeEditOpen]);
  useEffect(() => { timerOpenRef.current = timerOpen; }, [timerOpen]);
  useEffect(() => { settingsOpenRef.current = settingsOpen; }, [settingsOpen]);
  const openNoticeEditor = () => {
    // STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · open 시 server snapshot 으로 명시적 재hydrate.
    // (autoHydrate 가 이미 처리하지만 modal 열기 직전에 최신 server state 를 강제
    // 반영하여 로컬 draft 가 stale 하지 않도록 보장.)
    _noticeEditor.hydrate(liveNotice);
    setNoticeEditOpen(true);
  };
  // STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · upload / save / clear 는 useNoticeEditor hook 이
  // 담당 — 아래 alias 는 downstream JSX (modal) 가 변경 없이 그대로 사용하도록 고정.
  const uploadNoticeImage = (file) => _noticeEditor.uploadImage(file);
  const saveLiveNotice = () => _noticeEditor.save();
  const clearLiveNotice = () => _noticeEditor.clear();

  // STEP MOA-LIVE-VOICE-1B · 라모 AI 음성 엔진.
  //   · 기존 useLiveOperations REUSE — 신규 WRITE 경로 없음.
  //   · 테스트 LIVE 모드는 isTestMode 를 전달하여 디스패치가 로컬 ctx 만 변경.
  //   · 호출어 감지: STEP 1-C 에서 Vosk-browser provider attach (PC/Android 만).
  //     iOS 는 PTT 유지 (voskIsIOS true 면 attach skip · UI 안내).
  const _ramoOps = useLiveOperations(scheduleId);
  const ramoVoice = useRamoVoice({
    scheduleId,
    ops: _ramoOps,
    liveCtx,
    setLiveCtx,
    openNoticeEditor,
    clearLiveNotice,
    isTestMode,
    overlay: {
      layout: overlayLayout.layout, setSessionTop: overlayLayout.setSessionTop,
      qrDefault: showCombo.prefs?.qr_duration, draftPreset,
      past: { mode: pastSliderMode, count: pastList.length, playing: previewPlaying,
        start: () => { setPreviewIndex(0); setPreviewPlaying(true); }, stop: () => setPreviewPlaying(false) },
    },
  });

  // STEP MOA-LIVE-VOICE-AUTO-WHISPER-1 · 방송 마이크 트랙 읽기 전용 재사용 (LiveKit 무변경).
  const _getBroadcastMicStream = useCallback(() => {
    try {
      const pub = roomRef.current?.localParticipant?.getTrackPublication?.(Track.Source.Microphone);
      const mst = pub?.track?.mediaStreamTrack;
      if (!mst || mst.readyState !== 'live' || !mst.enabled || pub?.isMuted) return null;
      return new MediaStream([mst]);
    } catch { return null; }
  }, []);
  const autoVoice = useRamoAutoVoice({ voice: ramoVoice, isTestMode, getBroadcastStream: _getBroadcastMicStream });
  // STEP MOA-LIVE-VOICE-1C-FIX · 진행률 · 단계별 에러 · 재시도 · 중단 처리.
  const [wakeOn, setWakeOn] = useState(false);
  // state: idle | loading_sdk | downloading | initializing_model | mic | recognizer | ready | error | ios_hold
  const [wakeState, setWakeState] = useState('idle');
  const [wakeProgress, setWakeProgress] = useState(0);   // 0~100
  const [wakeError, setWakeError] = useState('');
  const [wakeErrorCode, setWakeErrorCode] = useState('');
  const [wakeAttempt, setWakeAttempt] = useState(0);     // 재시도 트리거
  const _voskProviderRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    if (!wakeOn || !ramoVoice.enabled) {
      try { _voskProviderRef.current?.dispose?.(); } catch {}
      _voskProviderRef.current = null;
      ramoVoice.detachWakeWord?.();
      if (!cancelled) { setWakeState('idle'); setWakeProgress(0); setWakeError(''); setWakeErrorCode(''); }
      return () => { cancelled = true; };
    }
    if (voskIsIOS()) {
      setWakeState('ios_hold');
      setWakeErrorCode('ios_hold');
      setWakeError('iOS 는 호환성 검증 전까지 PTT 를 사용해 주세요.');
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        setWakeState('loading_sdk'); setWakeProgress(0); setWakeError(''); setWakeErrorCode('');
        const provider = createVoskWakeWord({});
        _voskProviderRef.current = provider;
        await provider.init({
          onDetect: (meta) => { ramoVoice.onWakeDetected?.(provider, meta); },
          onProgress: (stage, percent) => {
            if (cancelled) return;
            setWakeState(stage || 'loading_sdk');
            if (typeof percent === 'number') setWakeProgress(Math.max(0, Math.min(100, percent)));
          },
        });
        // provider 를 hook 에도 등록 (STEP 1-B attach 플로우 유지).  init 재실행 방지.
        _voskProviderRef.current._attached = true;
        if (!cancelled) { setWakeState('ready'); setWakeProgress(100); }
      } catch (e) {
        if (cancelled) return;
        const code = e?.code || e?.message || 'wake_error';
        setWakeState(code === 'ios_hold' ? 'ios_hold' : 'error');
        setWakeErrorCode(code);
        setWakeError(
          code === 'ios_hold' ? 'iOS 는 호환성 검증 전까지 PTT 를 사용해 주세요.'
          : code === 'cdn_404' ? '모델 파일을 받을 수 없어요 (서버 404).'
          : code === 'timeout' ? '다운로드 시간 초과 (90초).  네트워크를 확인하고 다시 시도해 주세요.'
          : code === 'fetch_failed' ? '모델 다운로드 중 네트워크 오류가 발생했어요.'
          : code === 'decompress_failed' ? '모델 압축 해제 실패 (파일 손상 가능).'
          : code === 'recognizer_init_failed' ? '호출어 엔진 초기화 실패.'
          : code === 'audio_pipeline_failed' ? '오디오 파이프라인 생성 실패.'
          : code === 'mic_denied' ? '마이크 권한이 거부되었어요.'
          : code === 'vosk_load_failed' ? '호출어 엔진 로드 실패.'
          : '호출어 초기화 실패.'
        );
      }
    })();
    return () => {
      cancelled = true;
      try { _voskProviderRef.current?.dispose?.(); } catch {}
      _voskProviderRef.current = null;
      ramoVoice.detachWakeWord?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wakeOn, ramoVoice.enabled, wakeAttempt]);
  const retryWakeInit = () => { setWakeAttempt((n) => n + 1); };
  // STEP WAKEWORD-FLEX-1-RCA · 모바일 진단 (provider 카운터 polling · 동작 영향 0).
  const [wakeDiag, setWakeDiag] = useState(null);
  useEffect(() => {
    if (wakeState !== 'ready') { setWakeDiag(null); return undefined; }
    const t = setInterval(() => { setWakeDiag(_voskProviderRef.current?.getDiag?.() || null); }, 700);
    return () => clearInterval(t);
  }, [wakeState]);

  return (
    <div className="fixed inset-0 lg:relative lg:inset-auto lg:h-[100dvh] overflow-hidden overscroll-none bg-[#F7F8FA] dark:bg-[#0A0A0A] flex flex-col"
         data-testid="seller-live-console"
         style={{ touchAction: 'manipulation' }}>
      {/* STEP STUDIO-MOBILE-TEST-ENTRY-1 · 테스트 모드 상단 배너.
          실제 방송이 시작되지 않음을 명확히 알림 — 송출/주문 호출 차단 상태. */}
      {isTestMode && (
        <div className="hidden lg:flex absolute top-0 inset-x-0 z-50 bg-amber-500/90 text-black text-[11px] font-black px-3 py-1 items-center justify-between gap-2 pointer-events-none [&>button]:pointer-events-auto"
             style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.25rem)' }}
             data-testid="live-console-test-mode-banner">
          <span>🧪 테스트 모드 · 송출·주문·세션 생성 차단 · TEST 공유 세션 {testShare.account && <b data-testid="live-console-test-account-pc">[{testShare.account.name} · {testShare.account.id.slice(-6)}]</b>} <span data-testid="live-console-test-share-status">{testShare.status === 'error' ? '동기화 오류' : testShare.status === 'synced' ? '동기화 중' : '연결 중'}</span></span>
          <a href={`/seller/live-control?sid=${TEST_SID}`} target="_blank" rel="noopener noreferrer"
             className="ml-auto px-2 py-0.5 rounded-full bg-white/80 text-black text-[10px] font-black pointer-events-auto"
             data-testid="live-console-test-mode-open-console">PC 보조 콘솔 열기</a>
          <button type="button"
                  onClick={exitTestMode}
                  className="px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-black"
                  data-testid="live-console-test-mode-exit">
            나가기
          </button>
        </div>
      )}
      {/* STEP LIVE-STUDIO-PC-UI-2 · PC 전체폭 상단 바 (hidden lg:flex · 모바일 영향 0). */}
      <PcTopBar channels={channels}
                isLive={isLive}
                phase={phase}
                streamingSessionId={session?.session_id || null}
                onLeave={() => navigate('/seller/dashboard')} />
      <div className="flex-1 flex min-h-0">
        <ConsoleSidebar activeTab={activeTab}
                        onTab={openTab}
                        onOpenNotice={openNoticeEditor} />

      {/* Main area · video + controls */}
      <div className="flex-1 flex flex-col lg:flex-row min-w-0 lg:min-h-0">
        {/* Video column · STEP 8-I-5 · video 자체는 dark 유지 · header/controls 만 밝은 톤 */}
        {/* STEP 8-I-5A · relative → overlay/product-chip 를 control bar 위에 정확히 위치. */}
        <div className="flex-1 min-w-0 flex flex-col bg-[#F7F8FA] dark:bg-[#050505] relative lg:overflow-hidden lg:min-h-0">
          {/* STEP LIVE-STUDIO-PC-UI-2 · PC 전용 리치 메트릭 바 (hidden lg:flex · 모바일 영향 0).
              시안: ● LIVE | 00:23:15 | 👤 viewer | 💬 comment | ... | 방송 테스트 | 방송 종료.
              가짜 데이터 금지 — 실 state (elapsed, viewerCount, sharedMessages.length) 만 표시,
              없는 지표는 "—" placeholder.  '방송 테스트' 는 아직 미구현 → disabled chip.
              STEP LIVE-END-FIX-1 · §2 · 상단 메트릭 바에 lg:sticky top-0 z-30 을 적용해
              Preview·Overlay 패널이 아래로 스크롤 되어도 [방송 종료] 버튼이 항상 보이도록
              고정. 1920/1440/1366 가로폭 모두 metric overflow 는 pc-metrics-stats 쪽에서
              흡수하고 종료 버튼은 shrink-0. */}
          <div className="hidden lg:flex lg:sticky lg:top-0 z-30 items-center gap-3 px-5 py-3 bg-white dark:bg-[#0F0F0F] border-b border-[#E5E7EB] dark:border-white/10 shadow-sm"
               data-testid="pc-metrics-bar"
               data-live-end-sticky-container="true">
            <div className="flex items-center gap-2 shrink-0">
              {isLive ? (
                <>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF3B30] text-white text-[11px] font-black"
                        data-testid="pc-metrics-live-badge">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
                  </span>
                  <span className="text-[16px] font-black text-black dark:text-white font-mono tabular-nums"
                        data-testid="pc-metrics-elapsed">
                    {_fmt(elapsed)}
                  </span>
                </>
              ) : phase === 'ended' ? (
                <span className="px-2.5 py-1 rounded-full bg-gray-400 text-white text-[11px] font-black">종료</span>
              ) : (
                <span className="text-[12px] text-gray-500 font-bold">방송 준비 중</span>
              )}
            </div>
            <div className="flex-1 flex items-center gap-4 min-w-0 overflow-hidden" data-testid="pc-metrics-stats">
              <span className="inline-flex items-center gap-1 text-[13px] font-black text-black dark:text-white"
                    title="실시간 시청자 수">
                <Users size={14} className="text-gray-400" />
                <span data-testid="pc-metrics-viewers">{Number.isFinite(viewerCount) ? viewerCount.toLocaleString('ko-KR') : '—'}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-[13px] font-black text-black dark:text-white"
                    title="실시간 댓글 수">
                <MessageCircle size={14} className="text-gray-400" />
                <span data-testid="pc-metrics-comments">{(sharedMessages || []).length.toLocaleString('ko-KR')}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-[13px] font-black text-gray-400"
                    title="주문 지표는 추후 공개 (fabrication 금지)">
                <ShoppingBag size={14} />
                <span data-testid="pc-metrics-orders">—</span>
              </span>
              <span className="inline-flex items-center gap-1 text-[13px] font-black text-gray-400"
                    title="좋아요 지표는 추후 공개 (fabrication 금지)">
                <Heart size={14} />
                <span data-testid="pc-metrics-likes">—</span>
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* STEP LIVE-STUDIO-PC-UI-1 · PC 미리보기 비율 토글 (여기로 이동 · 시안 매칭) */}
              <div className="inline-flex items-center rounded-full border border-[#E5E7EB] dark:border-white/15 overflow-hidden bg-white dark:bg-[#0F0F0F] shadow-sm"
                   data-testid="pc-metrics-aspect-toggle"
                   data-pc-aspect={pcAspect}>
                {['16:9', '9:16'].map((ratio) => (
                  <button key={ratio} type="button" onClick={() => setPcAspect(ratio)}
                          aria-pressed={pcAspect === ratio}
                          className={`px-2.5 py-1 text-[10px] font-black transition ${
                            pcAspect === ratio
                              ? 'bg-[#FF3B30] text-white'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-[#F7F8FA] dark:hover:bg-white/10'
                          }`}
                          data-testid={`pc-metrics-aspect-${ratio.replace(':', 'x')}`}>
                    {ratio}
                  </button>
                ))}
              </div>
              <button type="button" disabled
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E7EB] text-[11px] font-black text-gray-400 cursor-not-allowed"
                      title="방송 테스트 (추후 지원)"
                      data-testid="pc-metrics-test">
                방송 테스트
              </button>
              {/* STEP LIVE-END-FIX-1 · §2 PC 상단 항상 표시 · ring-2 highlight ·
                  1366/1440/1920 가로폭 모두에서 shrink-0 로 metric overflow 발생해도
                  버튼이 밀리지 않음 — scroll-independent (상단 메트릭 바는 preview
                  스크롤과 별도 영역). 중복 클릭은 busy 로 차단. */}
              {isLive ? (
                <button type="button" onClick={endLive} disabled={busy}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-40 text-white text-[12px] font-black shadow-md ring-2 ring-[#FF3B30]/40 transition shrink-0"
                        data-testid="pc-metrics-end"
                        data-live-end-sticky="true"
                        title="방송 종료 (단축: 상단 항상 노출)">
                  <StopCircle size={13} /> 방송 종료
                </button>
              ) : phase === 'preview' && (
                <button type="button" onClick={goLive} disabled={busy || !previewOn}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-40 text-white text-[12px] font-black shadow-sm transition"
                        data-testid="pc-metrics-go-live">
                  <Radio size={13} /> LIVE 시작
                </button>
              )}
            </div>
          </div>

          {/* STEP STUDIO-MOBILE-FULLSCREEN-FIX-1C · Mobile header 완전 투명화.
              이전 FIX-1B 는 bg-white/55 backdrop-blur 로 영상 상단을 가로 전체로 가렸다.
              1C: 배경 띠 완전 제거 — 각 버튼/뱃지 자체 pill 로 가독성 유지. Desktop 영향 0. */}
          <div className="lg:hidden absolute top-0 inset-x-0 z-30 flex items-center justify-between gap-2 px-3 py-2 bg-transparent"
               data-testid="live-console-header"
               style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}>
            <div className="flex items-center gap-2 min-w-0">
              <button type="button" onClick={() => navigate('/seller/dashboard')}
                      className="lg:hidden p-1.5 rounded-full text-gray-600 hover:bg-[#F7F8FA]"
                      data-testid="live-console-back-mobile">
                <ArrowLeft size={16} />
              </button>
              {isLive ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-black shadow-sm"
                       data-testid="live-console-live-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE · {_fmt(elapsed)}
                </span>
              ) : phase === 'ended' ? (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gray-400 text-white text-[11px] font-black">
                  종료
                </span>
              ) : (
                <span className="text-[11px] text-gray-500 font-bold">방송 준비 중</span>
              )}
              {/* STUDIO-SELLER-UI-FINAL-CLEANUP-1 · 모바일 테스트 모드 소형 표시 (배너 대체 · 차단 로직 무변경). */}
              {isTestMode && (
                <span className="inline-flex items-center shrink-0 rounded-full bg-amber-400 text-black text-[10px] font-black shadow-sm overflow-hidden"
                      data-testid="live-console-test-mode-chip" title="테스트 모드 · 송출·주문·세션 생성 차단">
                  <span className="px-2 py-0.5" data-testid="live-console-test-account">TEST{testShare.account ? ` · ${testShare.account.name || ''}·${testShare.account.id.slice(-4)}` : ''}</span>
                  <a href={`/seller/live-control?sid=${TEST_SID}`} target="_blank" rel="noopener noreferrer"
                     className="px-2 py-0.5 min-h-[24px] inline-flex items-center bg-amber-300 border-l border-black/20"
                     title={`PC 보조 콘솔 (같은 TEST 공유 세션 · ${testShare.status === 'error' ? '동기화 오류' : '동기화'})`}
                     data-testid="live-console-test-mode-chip-console">콘솔</a>
                  <button type="button" onClick={exitTestMode}
                          className="px-2 py-0.5 min-h-[24px] bg-black/75 text-white"
                          data-testid="live-console-test-mode-chip-exit">나가기</button>
                </span>
              )}
              <p className="text-xs font-black truncate text-black dark:text-white hidden sm:block" data-testid="live-console-title">
                {seller?.brand_name || seller?.name || 'LIVEMOA LIVE'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* STEP LIVE-STUDIO-PC-UI-1 · PC-only 미리보기 비율 토글.
                  모바일 (sub-lg) 에서는 hidden · 기존 모바일 UX 변경 0.
                  9:16 선택 시 GAP badge 표출 (실 송출 미변경 안내). */}
              <div className="hidden lg:inline-flex items-center rounded-full border border-[#E5E7EB] dark:border-white/15 overflow-hidden bg-white dark:bg-[#0F0F0F] shadow-sm"
                   data-testid="live-console-pc-aspect-toggle"
                   data-pc-aspect={pcAspect}>
                <button type="button" onClick={() => setPcAspect('16:9')}
                        aria-pressed={pcAspect === '16:9'}
                        className={`px-2.5 py-1 text-[10px] font-black transition inline-flex items-center gap-1 ${
                          pcAspect === '16:9'
                            ? 'bg-[#FF3B30] text-white'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-[#F7F8FA] dark:hover:bg-white/10'
                        }`}
                        data-testid="live-console-pc-aspect-16x9"
                        title="가로 16:9 미리보기 (실 송출 미변경)">
                  16:9
                </button>
                <button type="button" onClick={() => setPcAspect('9:16')}
                        aria-pressed={pcAspect === '9:16'}
                        className={`px-2.5 py-1 text-[10px] font-black transition inline-flex items-center gap-1 ${
                          pcAspect === '9:16'
                            ? 'bg-[#FF3B30] text-white'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-[#F7F8FA] dark:hover:bg-white/10'
                        }`}
                        data-testid="live-console-pc-aspect-9x16"
                        title="세로 9:16 미리보기 (실 송출 미변경)">
                  9:16
                </button>
              </div>
              {pcAspect === '9:16' && (
                <div className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-700 dark:text-amber-300 text-[10px] font-bold"
                     data-testid="live-console-pc-aspect-gap-banner"
                     title="미리보기 비율만 변경 · 실제 LIVEMOA / YouTube 송출 비율은 기존 그대로 유지됩니다.">
                  <AlertTriangle size={10} />
                  미리보기 전용 · 실 송출 미변경
                </div>
              )}
              {isLive && (
                <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-black text-gray-700 dark:text-gray-200"
                     data-testid="live-console-header-metrics">
                  <Users size={13} className="text-gray-400" />
                  <span data-testid="live-console-metric-viewers">{viewerCount ?? '—'}</span>
                </div>
              )}
              {isLive && (
                <button type="button" onClick={endLive} disabled={busy}
                        className="px-3 py-1.5 rounded-full text-[11px] font-black bg-[#FF4F3D] hover:bg-[#D92F27] disabled:opacity-40 text-white shadow-sm inline-flex items-center gap-1"
                        data-testid="live-console-end">
                  <StopCircle size={12} /> 방송 종료
                </button>
              )}
            </div>
          </div>

          {/* STEP 8-I-5A · LIVE Notice — compact chip 형태 (Mobile UX Micro Fix).
              기본: 영상 상단 왼쪽에 작은 📢 pill (전체 가로폭 사용 X).
              Tap → 아래로 펼쳐 전체 내용 + [수정] · 다시 Tap → 접힘.
              Modal / 페이지 이동 X · Bottom Sheet X · 편집은 여전히 openNoticeEditor 로 진입. */}
          {isLive && !isTestMode && (
            <div className="absolute top-2 left-2 z-30 max-w-[75%]"
                 data-testid="live-console-notice-chip-wrap">
              {liveNotice?.text ? (
                <div className="pointer-events-auto">
                  <button type="button" onClick={() => setNoticeChipOpen((v) => !v)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFF4F1] hover:bg-[#FFEAE6] border border-[#FFD6CE] shadow-sm text-[11px] font-black text-[#17181C]"
                          aria-expanded={noticeChipOpen}
                          data-testid="live-console-notice-chip">
                    <span className="text-[12px]">📢</span>
                    <span>공지</span>
                  </button>
                  {noticeChipOpen && (
                    <div className="mt-1.5 max-w-[280px] rounded-xl bg-[#FFF4F1] border border-[#FFD6CE] shadow-lg p-2.5"
                         data-testid="live-console-notice-chip-expanded">
                      <p className="text-[11px] font-black text-[#17181C] leading-relaxed whitespace-pre-line">
                        {liveNotice.text}
                      </p>
                      <div className="mt-1.5 flex items-center justify-end gap-1.5">
                        <button type="button" onClick={openNoticeEditor}
                                className="px-2.5 py-1 rounded-full bg-[#FF4F3D] hover:bg-[#D92F27] text-white text-[10px] font-black"
                                data-testid="live-console-notice-chip-edit">
                          수정
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button type="button" onClick={openNoticeEditor}
                        className="pointer-events-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/40 hover:bg-black/55 backdrop-blur-sm text-white/90 text-[10px] font-black border border-white/15"
                        data-testid="live-console-notice-chip-empty">
                  <span className="text-[11px]">📢</span>
                  <span>공지 +</span>
                </button>
              )}
            </div>
          )}

          {/* STEP STUDIO-MOBILE-FULLSCREEN-FIX-1B · 모바일 duplicate metrics strip 제거.
              동일 데이터 (viewer count · title) 가 상단 overlay header 에 이미 노출 →
              중복 bar 가 영상 공간을 침범하여 흔들림/흰 띠의 요인이 됨. 숨김 처리. */}
          {false && isLive && (
            <div className="sm:hidden bg-white dark:bg-[#0F0F0F] border-b border-[#E5E7EB] dark:border-white/10 px-3 py-1.5 flex items-center gap-3 text-[11px] font-black text-gray-700 dark:text-gray-200"
                 data-testid="live-console-header-metrics-mobile">
              <span className="inline-flex items-center gap-1">
                <Users size={12} className="text-gray-400" /> {viewerCount ?? '—'}
              </span>
              <span className="ml-auto text-gray-400 font-medium truncate">
                {seller?.brand_name || seller?.name || 'LIVEMOA LIVE'}
              </span>
            </div>
          )}

          {/* Video area */}
          {/* STEP 8-I-1 · Android HOTFIX · min-h-[50vh] 제거.
              해당 min-h 가 flex-column 축소를 막아 header + video + prep-bar + mobile-tabs 총합이
              100dvh 를 초과할 때 prep-bar 가 viewport 아래로 밀리는 실기기 버그의 root cause.
              min-h-0 로 flex-1 이 자유롭게 축소 가능 → 하단 Control Bar 항상 visible viewport 내부.
              Desktop 은 이미 lg:min-h-0 라 무영향.
              STEP LIVE-STUDIO-PC-UI-3 · PC 에서는 Preview 가 전체 세로를 독점하지 않도록
              `lg:max-h-[52vh]` cap 추가 → 영상 비율은 <video> 가 자체 aspect class 로 유지하고
              남는 flex 공간은 하단 Overlay 운영 패널이 사용.
              STEP LIVE-STUDIO-PC-UI-6 · 사용자 요청으로 Preview 를 아래로 더 확장 — cap 을 64vh 로 상향.
              동시에 center column 에 lg:overflow-y-auto 를 적용해 총 높이가 viewport 초과 시
              하단 프리셋/LIVE 시작 버튼이 자연스럽게 세로 스크롤로 접근 가능. */}
          {/* STEP STUDIO-MOBILE-FULLSCREEN-FIX-1B · Video container → mobile absolute inset-0 + lg relative flex-1.
              이전: `flex-1 min-h-[320px]` 가 flex column 안에서 header + prep-bar 와 세 영역으로 나뉘어
                100dvh 재계산 시 영상 크기가 함께 변해 흔들림/띠 발생.
              변경: mobile 에서는 영상을 absolute inset-0 로 column 전체 배경으로 고정 (video DOM/ref 미접촉).
                PC (lg+) 는 lg:relative lg:inset-auto lg:flex-1 lg:min-h-[520px] lg:max-h-[64vh] 로 기존 flex 레이아웃 유지.
              video 요소는 w-full h-full object-cover 로 fullscreen fill — 세로 카메라 track 은 중앙 crop.
              aspect-ratio 가 flex height 를 흔들던 문제 해소. */}
          <div ref={videoBoxRef} className="absolute inset-0 bg-black flex items-center justify-center overflow-hidden lg:relative lg:inset-auto lg:flex-1 lg:min-h-[180px]">
            <video ref={videoRef} playsInline autoPlay muted
                    className="w-full h-full object-cover bg-black"
                    style={{
                      transform: facing === 'user' ? 'scaleX(-1)' : 'none',
                      filter: videoFilterCss,
                      ...(fitBox ? { width: fitBox.w, height: fitBox.h, flex: 'none' } : {}),
                    }}
                    data-testid="live-console-video"
                    data-pc-aspect={pcAspect} />
            {/* STEP LIVE-STUDIO-PRESET-PREVIEW-REAL-1 · 로컬 Overlay 레이어 (iframe 미사용 · 흰 화면 재발 X).
                방송 전/중 공통 · 썸네일과 동일한 preset 레이아웃 · 실 product 데이터만 사용.
                LIVE 중이면 서버 SoT (액티브 상품 + display.preset) 우선, 아니면 draftPreset + preview product fallback. */}
            {/* PC-STUDIO-SYNC-RESPONSIVE-FIX-1 · PC 카메라 미리보기는 상품·QR 그래픽 직접 표시 X (관리 영역 상태 표시로 대체).
                모바일·레이아웃 편집 중에는 기존대로 표시. 실제 송출 Overlay/Egress 미접촉. */}
            <div className={overlayLayout.editing ? 'contents' : 'contents lg:hidden'} data-testid="live-console-overlay-stage-wrap">
            <StudioOverlayStage liveCtx={liveCtx} isLive={isLive} draftPreset={draftPreset} isTestMode={isTestMode} combo={showCombo.combo}
                                showPrefs={showCombo.prefs} pastPreview={pastPreview}
                                suppressed={drawerOpen || moreOpen || settingsOpen || overlaySettingsOpen || timerOpen || filterOpen || noticeEditOpen
                                  || quickAddOpen || quickCountdown !== null || addPickerOpen || ytEgressOpen || igEgressOpen || ytCommentOpen}
                                productOverlay={!isLive || isLgView || extPreview} extPreview={extPreview}
                                orient={overlayOrient} pcAspect={pcAspect} ol={overlayLayout} />
            </div>
            {/* STEP OVERLAY-UX-FINAL-1 · Seller Preview overlay — EgressTemplate REUSE.
                방송 중 Seller 카메라 preview 위에 실제 Egress 가 합성하는 Overlay 를
                동일 state (polling /live-context) 로 미러.  신규 engine X · 신규 state X.
                `preview=1` 로 embed → EgressTemplate 가 (a) stage 배경 transparent,
                (b) <video> hidden, (c) lkUrl/lkToken 미제공 → LiveKit room 미연결,
                START_RECORDING 미송신.  오직 Overlay 레이어 (상품/재고/QR/공지/타이머)
                만 보임.  camera monitor 는 그대로 살아 있고 Seller 가 자기 방송에서
                실제로 송출되는 Overlay 를 YouTube 를 열지 않고도 확인 가능.  모바일
                카메라 터치/pinch 를 가리지 않도록 pointer-events: none. */}
            {/* STEP STUDIO-MOBILE-UI-3 · Egress preview iframe 완전 숨김.
                이전: 모바일에서 iframe 노출 → EgressTemplate 안 PresetOverlay 가
                  Studio 측 PresetOverlay (L3980) 와 **중복 상품 UI** 생성 + safe-area
                  dashed line (빨간 점선) 등 preview 전용 guide 가 실 방송 화면에 표시.
                1C 수정으로 iframe body 는 transparent 됐지만 content 중복은 그대로.
                PresetOverlay 가 이미 실 Egress 와 동일 UI 를 locally 렌더하므로 iframe
                은 미리보기 가치가 없어 완전 숨김. 실 Egress 송출 pipeline 은 미접촉. */}
            {false && isLive && session?.session_id && (
              <iframe
                src={`/egress-template/${encodeURIComponent(session.session_id)}?orientation=portrait&preview=1`}
                title="Seller Overlay Preview"
                className="absolute inset-0 w-full h-full border-0 pointer-events-none hidden"
                style={{ background: 'transparent' }}
                data-testid="live-console-overlay-preview"
                data-pc-aspect={pcAspect} />
            )}
            {/* STEP LIVE-STUDIO-PC-9x16-WHITE-PREVIEW-FIX · PC overlay 미리보기는 iframe
                흰 화면 이슈로 숨김.  실제 Overlay 는 Consumer / YouTube Egress 에서 정상 송출됨을
                안내 (LIVE 중일 때만 노출). */}
            {isLive && session?.session_id && (
              <div className="hidden lg:flex absolute right-3 bottom-3 z-20 items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 text-white/90 text-[10px] font-black backdrop-blur-sm pointer-events-none"
                   data-testid="live-console-overlay-16x9-hint">
                <AlertTriangle size={10} />
                상품 Overlay 미리보기는 소비자 LIVE / 모바일 Studio 에서 확인
              </div>
            )}
            {!previewOn && phase !== 'live' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 gap-3 px-6 text-center z-10"
                    data-testid="live-console-preview-empty">
                <VideoOff size={40} />
                <p className="text-sm font-bold">
                  {previewStarting ? '카메라 시작 중…' : '방송을 시작하기 전에 카메라를 켜주세요.'}
                </p>
                <button type="button" onClick={() => startPreview()}
                        disabled={previewStarting}
                        className="px-4 py-2 rounded-full bg-[#FF3B30] text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="live-console-start-camera">
                  {previewStarting ? (
                    <><RefreshCw size={14} className="animate-spin" /> 시작 중…</>
                  ) : (
                    <><Camera size={14} /> {permError ? '다시 시도' : '카메라 켜기'}</>
                  )}
                </button>
                {permError && (
                  <p className="text-[11px] text-red-400 leading-snug max-w-[280px] inline-flex items-start gap-1"
                      data-testid="live-console-perm-error">
                    <AlertTriangle size={12} className="shrink-0 mt-0.5" /> {permError}
                  </p>
                )}
              </div>
            )}

            {phase === 'countdown' && <CountdownOverlay n={countdown} />}
            {phase === 'ended' && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/70 text-white">
                <p className="text-lg font-black">방송이 종료되었습니다.</p>
                <button type="button" onClick={() => navigate('/seller/dashboard')}
                        className="mt-2 px-4 py-2 rounded-full bg-white text-black text-sm font-bold">
                  판매자센터로
                </button>
              </div>
            )}
            {/* STEP STUDIO-MOBILE-FULLSCREEN-FIX-1 · Mobile Right Vertical Rail (LIVE 중만).
                시안 §3 · 상품 · 댓글 · 타이머 · 더보기 네 개 floating pill.
                기존 drawerOpen + activeTab + moreOpen state 그대로 REUSE (신규 handler 0).
                영상 위 반투명 흰색 pill 로 얹어 — SellerLiveCommentOverlay 와 겹치지 않도록
                우측 안전영역에 배치.  Desktop 은 우측 패널로 접근하므로 lg:hidden. */}
            {/* STUDIO-SELLER-NOTICE-PREVIEW-FIX-1 · 판매자 확인용 공지 미리보기 — 우측 상품 버튼 바로 왼쪽 (TEST). */}
            {isLive && liveNotice?.text && !drawerOpen && !moreOpen && !filterOpen && !timerOpen && !settingsOpen && !overlaySettingsOpen && controlsVisible && (
              <SellerNoticePreview text={liveNotice.text} open={noticePreviewOpen} setOpen={setNoticePreviewOpen} below={remoteOn} />
            )}
            {isLive && !drawerOpen && !moreOpen && !filterOpen && !timerOpen && !settingsOpen && !overlaySettingsOpen && controlsVisible && !railOpen && (
              <RailCollapsedTab onOpen={() => setRailOpen(true)} />
            )}
            {isLive && !drawerOpen && !moreOpen && !filterOpen && !timerOpen && !settingsOpen && !overlaySettingsOpen && controlsVisible && railOpen && (
              <div className="lg:hidden absolute z-30 flex flex-col gap-1.5 pointer-events-auto overflow-y-auto"
                   data-testid="live-console-mobile-rail"
                   style={{ top: 'calc(env(safe-area-inset-top) + 4rem)', right: 'calc(env(safe-area-inset-right) + 0.5rem)',
                            maxHeight: 'calc(100% - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 12rem)' }}>
                {[
                  { key: 'products', label: '상품',  icon: Package,         onPress: () => openTab('products') },
                  { key: 'comments', label: '댓글',  icon: MessageCircle,   onPress: () => setCommentsHidden((v) => !v) },
                  // STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 전용 rail 버튼.
                  //   3·2·1 카운트다운 → 로컬 프레임 캡처 → QuickAddModal.
                  //   기존 QuickAddModal 100% REUSE (신규 API/engine 0).
                  { key: 'quick',    label: '퀵등록', icon: Zap,             onPress: startQuickRegister },
                  // STEP STUDIO-MOBILE-RAIL-ROUTING-FIX-1 · 타이머 전용 sheet 로 연결.
                  //   이전: openTab('status') → StatusTab(방송상태) 가 열리는 bug.
                  //   수정: setTimerOpen(true) → OverlayTimerControl 전용 bottom sheet.
                  { key: 'timer',    label: isTestMode ? '공지' : '타이머', icon: isTestMode ? Megaphone : Clock, onPress: () => setTimerOpen(true) },
                  // LIVEMOA-LIVE-TEST-PREVIEW-INTEGRATION-1 · 테스트 모드 전용 미리보기.
                  // LIVE-EXTERNAL-OVERLAY-PREVIEW-FINAL-1 · 별도 '송출뷰' 버튼 숨김 (LivePreviewModal egress 구현은 보존).
                  ...(isTestMode ? [
                    { key: 'viewer', label: '소비자', icon: Eye,         onPress: () => setPreviewKind('viewer') },
                  ] : []),
                  { key: 'more',     label: '더보기', icon: MoreHorizontal, onPress: () => setMoreOpen(true)   },
                ].map((it) => {
                  // STEP STUDIO-MOBILE-UI-2 · active 상태 구분:
                  //   · products/status → drawer open + 해당 tab (코랄)
                  //   · comments → 댓글 overlay 가 숨겨져 있지 않은 상태 (= 보이는 중)
                  //   · more → moreOpen
                  const activeRail = (it.key === 'comments')
                    ? !commentsHidden
                    : (it.key === 'more')
                    ? moreOpen
                    : (it.key === 'timer')
                    ? timerOpen
                    : (it.key === 'quick')
                    ? (quickCountdown !== null || quickAddOpen)
                    : (drawerOpen && activeTab === it.key);
                  return (
                    <button key={it.key} type="button" onClick={it.onPress}
                            aria-label={it.label}
                            className={`w-12 h-12 shrink-0 rounded-full backdrop-blur-md shadow-lg flex flex-col items-center justify-center gap-0.5 transition border ${
                              activeRail
                                ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                                : 'bg-white/85 dark:bg-black/55 text-gray-800 dark:text-white border-white/70 dark:border-white/15 hover:bg-white'
                            }`}
                            data-testid={`live-console-mobile-rail-${it.key}`}>
                      <it.icon size={17} />
                      <span className="text-[9px] font-black leading-none">{it.label}</span>
                    </button>
                  );
                })}
                <button type="button" onClick={() => setRailOpen(false)} aria-label="오른쪽 조작바 접기"
                        className="w-12 h-8 shrink-0 rounded-full bg-white/85 text-gray-800 backdrop-blur-md border border-black/10 shadow-lg inline-flex items-center justify-center"
                        data-testid="live-console-rail-collapse">
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
            {/* STEP 8-I-2 · Transparent Comment Overlay (mobile · LIVE 중에만).
                STEP 8-I-5A · 위치를 video-column level 로 이동 → overflow-hidden clip 방지 +
                Control Bar / Mobile Tabs Bar 뒤로 잘리는 문제 최종 해결.
                Tap → 기존 mobile Bottom Sheet 를 열어 CommentsOrdersTab 전체 관리 진입.
                Desktop 은 우측 패널로 이미 상시 노출되므로 lg:hidden. */}
            {/* STEP 8-D · 이전 floating right controls 는 하단 control bar 로 이동.
                Live 중에도 하단 bar 만 사용해서 영상 오른쪽을 가리지 않음. */}

            {/* STEP MOA-LIVE-ASSISTANT-UI-PREP-1 · 라모 AI 음성 도우미 캡슐.
                UI/상태 구조만 준비 · 실제 음성 감지 · AI 명령 실행 · DB WRITE 없음.
                위치: 좌상단 eye-icon(controls-toggle · left-2 top:safe+4rem · w-10) 오른쪽
                      (left:3.5rem) 같은 y-축에 배치.  우측 rail, 하단 조작부, bottom-left
                      pin 패널, 방송 종료 버튼과 모두 겹치지 않음.
                controlsVisible OFF 시 펼친 패널은 접히고 작은 캡슐만 유지.
                공용 컴포넌트 — PC(lg+) 에서도 동일 위치(좌상단)에 렌더됨.
                `?testmode=1` 에서도 동일하게 노출 (DB/API 호출 0 보장 · 컴포넌트 자체가 side-effect free). */}
            {(previewOn || isLive) && (
              <RamoAiCapsule
                variant="mobile"
                forceCollapsed={!controlsVisible}
                className="absolute z-30"
                style={{
                  left: '3.5rem',
                  top: 'calc(env(safe-area-inset-top) + 4rem)',
                }}
                testIdPrefix="live-console-ramo"
                voice={ramoVoice}
                wakeWord={{
                  on: wakeOn,
                  state: wakeState,
                  progress: wakeProgress,
                  error: wakeError,
                  errorCode: wakeErrorCode,
                  toggle: () => { if (!wakeOn) autoVoice.stop(); setWakeOn((v) => !v); },
                  retry: retryWakeInit,
                  isIOS: voskIsIOS(),
                  diag: wakeDiag,
                }}
                autoVoice={{
                  ...autoVoice,
                  toggle: () => { if (!autoVoice.on) setWakeOn(false); autoVoice.toggle(); },
                }}
              />
            )}
          </div>

          {/* STEP LIVE-STUDIO-PC-UI-3 · 중앙 Preview 바로 아래 Overlay 운영 패널 (PC 전용).
              hidden lg:flex → 모바일 영향 0.  기존 useLiveOperations canonical hook 재사용 ·
              신규 engine/API/collection 0 · 가짜 preset ID 사용 금지. */}
          <PcOverlayOpsPanel scheduleId={scheduleId}
                             liveCtx={liveCtx}
                             isLive={isLive}
                             openNoticeEditor={openNoticeEditor}
                             draftPreset={draftPreset}
                             setDraftPreset={setDraftPreset}
                             isTestMode={isTestMode}
                             setLiveCtx={setLiveCtx}
                             onEditLayout={startLayoutEdit}
                             status={{ test: isTestMode, product: liveCtx?.active_product?.name || liveCtx?.active_product?.product_name || null,
                                       qr: !!liveCtx?.active_product_qr?.visible, notice: liveNotice?.text || null, timer: liveCtx?.timer || null,
                                       ext: (presetCtx?.external_notices || []).find((n) => n.id === presetCtx?.external_notice_selected) || null }}
                             presetCtx={showPresets ? { ...presetCtx, timer: liveCtx?.timer || null } : null} presetApply={presetApply}
                             presetStart={presetStart} connected={!isTestMode}
                             onNoticeSelect={(n) => { remoteOps.notice(n); if (isTestMode) toast.success(n ? '공지를 표시했어요.' : '공지를 내렸어요.'); }} />

          {/* STEP 8-I-5A · Video-column level absolute overlay stack (mobile-only, LIVE only).
              Video area 는 overflow-hidden 이므로 overlay 를 밖으로 빼내 control bar 위에 배치.
              STEP 8-I-5A Micro Fix · Control Bar 와 여백 +16px (8.25rem → 9.25rem)
              → 실기기에서 마지막 댓글이 컨트롤 앞에 확실히 보이도록. */}
          {/* STEP STUDIO-MOBILE-UI-2 · 댓글 overlay toggle (rail 댓글 버튼 또는 overlay tap).
              commentsHidden 일 때 overlay 전체 render skip — 영상 가림 0.  댓글 수집은 계속 작동.
              overlay tap → setCommentsHidden(true) 로 숨기기 (drawer 열지 않음 · 영상 확대 유지). */}
          {showCurrentCard && (
            <StudioCurrentProductCard product={liveCtx.active_product} onOpen={() => openTab('products')}
                                      collapsed={!bottomBarOpen}
                                      extPreview={extPreview}
                                      onToggleExtPreview={extActive || isTestMode ? () => setExtPreview((v) => !v) : null} />
          )}
          {isLive && !commentsHidden && (
            <div className="lg:hidden absolute left-0 right-0 z-30 pointer-events-none flex flex-col gap-2 px-3"
                 style={{ bottom: `calc(env(safe-area-inset-bottom) + ${showCurrentCard && bottomBarOpen ? 10 : 9.25}rem)` }}
                 data-testid="live-console-video-bottom-stack">
              <SellerLiveCommentOverlay messages={sharedMessages}
                                        onTap={() => setCommentsHidden(true)} />
              {/* STEP STUDIO-MOBILE-UI-3 · '소개중 상품 chip' 제거.
                  이전: active-product-chip 이 PresetOverlay (L3980) 와 **중복 노출** →
                    영상에 상품 overlay (PresetOverlay) + 별도 '소개중' 검은 바 두 번 표시.
                  이제 PresetOverlay (mini 기본) 가 유일한 상품 노출 — 중복 제거.
                  activeLiveProduct state / liveCtx.active_product 미접촉 — chip markup 만 제거. */}
            </div>
          )}

          {/* STEP 8-D · Bottom control bar · always visible above safe-area.
              Preview: 카메라/마이크 select + [전환] [마이크] [LIVE 시작하기]
              Live:    [전환] [마이크] [필터] [더보기]   (LIVE 시작 대신 상단 헤더 [방송 종료])
              STEP 8-I-1 · Android HOTFIX · padding-bottom 을 env(safe-area-inset-bottom) 만 의존하지 않고
              최소 1rem 상수 fallback 을 보장 (Android Chrome/Samsung Internet 은 safe-area 가 0px 반환).
              추가로 mobile 에서 sticky bottom 처럼 항상 접근 가능하도록 `sticky` + `z-20` 부여. */}
          {/* STEP STUDIO-MOBILE-FULLSCREEN-FIX-1C · Prep/Control bar 완전 투명화.
              이전 1B 는 bg-white/55 backdrop-blur border-t 로 영상 하단을 가로 전체로 가렸다.
              1C: 모바일에서 bar 배경 띠 완전 제거 — 각 버튼은 자체 pill 로 밝은 톤 가독성 유지.
              Desktop (lg+) 는 기존 bg-white sticky lg:relative 그대로. */}
          {phase !== 'ended' && (
            <div className="absolute bottom-0 inset-x-0 z-30 bg-transparent p-3 max-[389px]:px-2 space-y-2 pb-[calc(env(safe-area-inset-bottom)+1rem)] lg:relative lg:bottom-auto lg:inset-x-auto lg:bg-white lg:dark:bg-[#141414] lg:border-t lg:border-[#E5E7EB] lg:dark:border-white/10 lg:shrink-0 lg:sticky lg:z-20 lg:static"
                  data-testid="live-console-prep-bar">
              {previewOn && phase === 'preview' && (
                <div className="grid grid-cols-1 gap-2" data-testid="live-console-device-row">
                  {/* STEP 8-I-2 · physical camera device chooser 제거.
                      Android Samsung Internet 이 camera2 0 / camera2 2 / camera2 3 같은 노출 명을
                      그대로 보여주어 판매자 UX 를 해침. 전/후면 전환은 오직 [전환] 버튼으로만.
                      Mic select 는 유지 (일반적으로 1~2개 · label 도 명확). */}
                  <label className="text-[10px] text-gray-500 space-y-0.5">
                    마이크
                    <select value={micId}
                            onChange={(e) => { setMicId(e.target.value); startPreview({ micId: e.target.value }); }}
                            className="w-full px-2 py-1.5 rounded-lg border border-black/10 dark:border-white/10 text-xs bg-transparent"
                            data-testid="live-console-mic-select">
                      <option value="">기본 마이크</option>
                      {mics.map((m, i) => <option key={m.deviceId || i} value={m.deviceId}>{m.label || `마이크 ${i + 1}`}</option>)}
                    </select>
                  </label>
                </div>
              )}

              {/* Media control row · always visible when preview or live · STEP 8-I-5 · bright pills
                  STEP STUDIO-MOBILE-PANEL-VISIBILITY-1 · controlsVisible 가 OFF 면 하단 조작부 숨김.
                  LIVE 중에만 숨김 토글 적용 — 프리뷰(카메라 OFF) 시에는 항상 노출. */}
              {/* STUDIO-MOBILE-BOTTOM-BAR-SIDE-FIX-1 · 접힌 상태: 오른쪽 아래 [<] 펼치기만. */}
              {isLive && controlsVisible && !bottomBarOpen && (
                <div className="lg:hidden flex justify-end" style={{ paddingRight: 'env(safe-area-inset-right)' }}>
                  <BottomBarToggle open={false} setOpen={setBottomBarOpen} />
                </div>
              )}
              {(previewOn || isLive) && (!isLive || controlsVisible) && (
                <div className={`${isLive && !bottomBarOpen ? 'hidden lg:flex' : 'flex'} items-center gap-1.5 max-[389px]:gap-1 lg:gap-2`} data-testid="live-console-media-controls">
                  <button type="button" onClick={flipCamera}
                          className="shrink-0 min-h-[44px] px-2.5 max-[389px]:px-2 lg:px-3 py-2 rounded-full bg-[#F7F8FA] hover:bg-[#EEF0F3] border border-[#E5E7EB] dark:bg-white/10 dark:border-transparent dark:hover:bg-white/20 text-gray-800 dark:text-white text-xs font-black inline-flex items-center gap-1.5 transition"
                          aria-label="전/후면 전환" data-testid="live-console-flip">
                    <RefreshCw size={14} /> 전환
                  </button>
                  <button type="button" onClick={toggleMic}
                          className={`shrink-0 min-h-[44px] px-2.5 max-[389px]:px-2 lg:px-3 py-2 rounded-full text-xs font-black inline-flex items-center gap-1.5 max-[389px]:gap-1 transition ${
                            micMuted
                              ? 'bg-red-500 hover:bg-red-600 text-white shadow-sm'
                              : 'bg-[#F7F8FA] hover:bg-[#EEF0F3] border border-[#E5E7EB] dark:bg-white/10 dark:border-transparent dark:hover:bg-white/20 text-gray-800 dark:text-white'
                          }`}
                          aria-label={micMuted ? '마이크 켜기' : '마이크 끄기'} data-testid="live-console-mic-toggle">
                    {micMuted ? <MicOff size={14} /> : <Mic size={14} />}
                    {micMuted ? '마이크 꺼짐' : '마이크'}
                  </button>
                  {/* STEP 8-I · 모바일 전용 [필터] · [더보기] 진입점 (LIVE 중에만) ─
                      Desktop 은 우측 패널로 접근하므로 lg:hidden. */}
                  {isLive && (
                    <>
                      <button type="button" onClick={() => setFilterOpen(true)}
                              className={`lg:hidden shrink-0 min-h-[44px] px-2.5 max-[389px]:px-2 py-2 rounded-full text-xs font-black inline-flex items-center gap-1.5 max-[389px]:gap-1 transition ${
                                filterMode !== 'original'
                                  ? 'bg-[#FF3B30]/10 text-[#FF3B30] ring-1 ring-[#FF3B30]/40'
                                  : 'bg-[#F7F8FA] hover:bg-[#EEF0F3] border border-[#E5E7EB] dark:bg-white/10 dark:border-transparent dark:hover:bg-white/20 text-gray-800 dark:text-white'
                              }`}
                              aria-label="카메라 필터"
                              data-testid="live-console-filter-open">
                        <Sparkles size={14} />
                        {filterMode === 'original' ? '필터' : (FILTER_PRESETS.find((p) => p.key === filterMode)?.label || '필터')}
                      </button>
                      <button type="button" onClick={() => setSettingsOpen(true)}
                              className="lg:hidden shrink-0 ml-auto min-h-[44px] px-2.5 max-[389px]:px-2 py-2 rounded-full bg-[#F7F8FA] hover:bg-[#EEF0F3] border border-[#E5E7EB] dark:bg-white/10 dark:border-transparent dark:hover:bg-white/20 text-gray-800 dark:text-white text-xs font-black inline-flex items-center gap-1.5 transition"
                              aria-label="촬영 환경 설정"
                              data-testid="live-console-settings-open">
                        <Settings size={14} /> 설정
                      </button>
                      {controlsVisible && <BottomBarToggle open setOpen={setBottomBarOpen} />}
                    </>
                  )}
                </div>
              )}

              {phase === 'preview' && (
                <div className="lg:hidden flex items-center gap-2" data-testid="live-console-preview-tools">
                  {process.env.REACT_APP_STUDIO_TEST_ENTRY === '1' && !isTestMode && (
                    <button type="button" onClick={() => navigate('/seller/live-console?testmode=1')}
                            className="flex-1 min-h-[40px] px-3 rounded-full border border-amber-400 bg-amber-50 text-amber-700 text-xs font-black inline-flex items-center justify-center gap-1.5"
                            data-testid="live-console-mobile-test-entry">
                      <FlaskConical size={14} /> 방송 테스트 (송출 없음)
                    </button>
                  )}
                  <button type="button" onClick={() => setSettingsOpen(true)}
                          className="flex-1 min-h-[40px] px-3 rounded-full bg-[#F7F8FA] border border-[#E5E7EB] dark:bg-white/10 dark:border-transparent text-gray-800 dark:text-white text-xs font-black inline-flex items-center justify-center gap-1.5"
                          data-testid="live-console-mobile-preset-open">
                    <Layers size={14} /> 화면 프리셋
                  </button>
                </div>
              )}
              {/* LIVE 시작 버튼 · preview 상태에서만 · safe-area 위 항상 노출 */}
              {phase === 'preview' && (
                <button type="button" onClick={goLive} disabled={busy || !previewOn}
                        className="w-full px-4 py-3 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-40 text-white text-base font-black inline-flex items-center justify-center gap-2"
                        data-testid="live-console-go-live">
                  <Radio size={16} /> LIVE 시작하기
                </button>
              )}
              {cfg && phase === 'preview' && (
                <p className="text-[10px] text-gray-400 text-center leading-relaxed">
                  {cfg.livekit_configured ? '기존 LiveKit publish 재사용 · 시청자 화면 자동 노출' : 'Mock 모드 · 실 송출 미연결'}
                </p>
              )}
            </div>
          )}

          {/* STEP STUDIO-MOBILE-FULLSCREEN-FIX-1 · 중복 하단 탭바 제거.
              우측 세로 레일 (live-console-mobile-rail · LIVE 중) 로 역할 이관.
              기존 drawerOpen / activeTab / openTab 핸들러는 그대로 REUSE — 신규 state 0.
              Desktop 우측 패널(L4166~) · PC 사이드바(ConsoleSidebar) 는 미접촉. */}
        </div>

        {/* Desktop right console (always visible on lg+) · STEP LIVE-STUDIO-PC-UI-2 · 시안 매칭. */}
        <div className="hidden lg:flex flex-col w-[360px] xl:w-[400px] shrink-0 border-l border-[#E5E7EB] dark:border-white/10 bg-white dark:bg-[#0F0F0F] overflow-hidden"
              data-testid="live-console-desktop-panel">
          {/* STEP LIVE-STUDIO-PC-UI-2 · 상품/댓글/주문 탭 (시안: 상품 (12) 댓글 (124) 주문 (36)).
              카운트는 실 데이터 기반 (fabrication 금지) — 상품=live_products.length, 댓글=sharedMessages.length,
              주문=comment-order candidate 수 (CommentsOrdersTab 내부 polling SoT).  총 카운트가 아직 로드되지 않으면
              숫자 영역을 비워 둠. */}
          <div className="flex items-stretch border-b border-[#E5E7EB] dark:border-white/10 bg-white dark:bg-[#0F0F0F]"
               data-testid="live-console-desktop-tabs">
            {[
              { key: 'products', label: '상품',    count: (liveCtx?.live_products || []).length },
              { key: 'comments', label: '댓글',    count: (sharedMessages || []).length },
              { key: 'status',   label: '방송상태', count: null },
            ].map((s) => {
              const isActive = activeTab === s.key;
              return (
                <button key={s.key} type="button" onClick={() => setActiveTab(s.key)}
                        className={`flex-1 py-3 text-[13px] font-black inline-flex items-center justify-center gap-1.5 transition border-b-2 ${
                          isActive
                            ? 'text-[#FF3B30] border-[#FF3B30] bg-[#FF3B30]/[0.03]'
                            : 'text-gray-500 border-transparent hover:text-black hover:bg-[#F7F8FA] dark:hover:text-white'
                        }`}
                        data-testid={`live-console-desktop-tab-${s.key}`}>
                  {s.label}
                  {Number.isFinite(s.count) && (
                    <span className={`text-[11px] font-black ${isActive ? 'text-[#FF3B30]' : 'text-gray-400'}`}>({s.count})</span>
                  )}
                </button>
              );
            })}
          </div>
          {/* STEP LIVE-STUDIO-PC-UI-2 · Products 탭에서만 노출되는 상단 액션 바.
              STEP LIVE-STUDIO-PC-PRODUCT-SELECT-UI-1 (2026-10-08):
                버튼 A = "기존 상품 불러오기" (shared AddProductModal · LIVE Console 과 동일 picker 재사용).
                버튼 B = "상품 빠른등록" (기존 QuickAddModal 그대로).
                두 버튼 모두 scheduleId 만 요구 (LIVE 전/중 모두 활성화). */}
          {activeTab === 'products' && (
            <div className="px-3 pt-3 pb-2 flex items-center gap-2 shrink-0"
                 data-testid="live-console-desktop-products-actions">
              <button type="button" onClick={() => setAddPickerOpen(true)}
                      disabled={!scheduleId}
                      title={!scheduleId ? '편성표 방송을 먼저 선택해 주세요' : '등록된 상품 중에서 방송에 올릴 상품을 선택합니다'}
                      className="flex-1 inline-flex items-center justify-center gap-1 py-2 rounded-lg bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-40 text-white text-[12px] font-black transition"
                      data-testid="live-console-desktop-import-existing">
                <Plus size={13} /> 기존 상품 불러오기
              </button>
              <button type="button" onClick={() => { if (previewOn) startQuickRegister(); else { toast.info('카메라가 꺼져 있어 사진 업로드로 등록해요', { duration: 4000 }); setQuickCaptureFile(null); setQuickAddOpen(true); } }}
                      disabled={!scheduleId}
                      title={!scheduleId ? '편성표 방송을 먼저 선택해 주세요' : '사진·음성·수동 입력으로 새 상품을 즉시 등록합니다'}
                      className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-40 text-[12px] font-black transition"
                      data-testid="live-console-desktop-quick-add">
                <Plus size={13} /> 상품 빠른등록
              </button>
            </div>
          )}
          {/* STEP 8-B · comments 탭은 자체 3-region flex 레이아웃 사용 → 외곽 overflow 제거.
              그 외 탭은 기존처럼 세로 스크롤 유지.
              STEP 8-E · Identity Lock — session.session_id 변경 시 각 탭 컴포넌트
              완전 remount 하여 comments/candidates/pinned/activeProduct 잔재를 즉시 clear.
              같은 session 이면 key 동일 → 재접속에서 폴링 유지 (연속성 확보). */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {activeTab === 'products' && (
              <div className="flex-1 min-h-0 overflow-y-auto">
                {/* STEP STUDIO-PRODUCT-FLICKER-RCA-2 · key 에서 session?.session_id 제거.
                    · 원인: refreshServerSession (6s polling) 가 setSession merge 로
                      session_id 를 바꾸면 기존 key `p:${sid}::${scheduleId}` 가 변경되어
                      ProductsTab 이 unmount→remount → useState(null) 초기화 →
                      "방송 정보를 불러오는 중…" 뷰 깜빡임 (2~3s 주기).
                    · 수정: 같은 schedule 안에서 session 이 재접속되어도 상품 목록은
                      동일 (schedule 단위 broadcast_products) 이므로 scheduleId 만으로
                      identity 를 결정. 다른 schedule 로 전환 시에는 scheduleId 가 바뀌어
                      자연스레 remount. sessionId 는 prop 으로 계속 전달 → 내부 useEffect 가
                      session 변동 시 live-context 를 새 sid 로 폴링. */}
                <ProductsTab key={`p::${scheduleId || 'nil'}`}
                              sessionId={session?.session_id} scheduleId={scheduleId} isLive={isLive}
                              sessionWarning={session?.warning}
                              openNoticeEditor={openNoticeEditor}
                              onOpenQuickAdd={() => setQuickAddOpen(true)}
                              draftPreset={draftPreset}
                              isTestMode={isTestMode}
                              externalCtx={isTestMode ? liveCtx : null}
                              setExternalCtx={isTestMode ? setLiveCtx : null}
                              isProductPinned={pinMode === 'product'}
                              onProductPinToggle={() => setPinMode((m) => m === 'product' ? 'none' : 'product')}
                              onOpenOverlaySettings={() => setOverlaySettingsOpen(true)}
                              pastSlider={pastSlider}
                              liveNotice={liveNotice} />
              </div>
            )}
            {activeTab === 'comments' && (
              <CommentsOrdersTab key={`c:${session?.session_id || 'nil'}::${scheduleId || 'nil'}`}
                                 sharedMessages={sharedMessages}
                                 isPinned={pinMode === 'comments'}
                                 onPinToggle={() => setPinMode((m) => m === 'comments' ? 'none' : 'comments')}
                                  sessionId={session?.session_id} scheduleId={scheduleId} channels={channels} isLive={isLive} isOwner={isOwner} />
            )}
            {activeTab === 'status' && (
              <div className="flex-1 min-h-0 overflow-y-auto">
                {!isTestMode && scheduleId && (
                  <div className="m-3 p-3 rounded-xl border border-black/10 dark:border-white/10" data-testid="studio-rec-overview">
                    <RecordingOverview scheduleId={scheduleId} reloadKey={session?.streaming_status} />
                  </div>
                )}
                <StatusTab liveKitState={publishState} sessionId={session?.session_id} sessionStatus={session?.streaming_status} channels={channels} refreshChannels={refreshChannels} egressStatus={egressStatus} egressCapability={egressCapability} onOpenYtEgress={() => setYtEgressOpen(true)} onStopYtEgress={stopYtEgress} onOpenYtComment={() => setYtCommentOpen(true)} ytStatus={ytStatus} onOpenIgEgress={() => setIgEgressOpen(true)} onStopIgEgress={stopIgEgress} />
              </div>
            )}
            {activeTab === 'live' && (
              <div className="flex-1 min-h-0 overflow-y-auto p-4 text-xs text-gray-500 space-y-2" data-testid="live-console-tab-live-info">
                <p className="font-black text-black dark:text-white text-sm">현재 방송</p>
                <p>session_id: <b>{session?.session_id || '준비 전'}</b></p>
                <p>상태: <b>{session?.streaming_status || '준비 중'}</b></p>
                <p>publish: <b>{publishState}</b></p>
                <p>진행: <b>{_fmt(elapsed)}</b></p>
              </div>
            )}
          </div>
          {/* STEP LIVE-STUDIO-PC-UI-2 · 방송 상태 + 스트림 정보 고정 카드 (시안 우측 하단).
              데이터 fabrication 금지 — publishState / session_id 를 그대로 노출.  해상도/프레임/
              지연시간은 LiveKit publisher 가 현재 실 반환하지 않아 "—" placeholder (실 engine 변경 시
              노출).  Copy 아이콘은 session_id 만 지원 (실 값만 있는 경우). */}
          <div className="shrink-0 border-t border-[#E5E7EB] dark:border-white/10 bg-[#FAFBFC] dark:bg-[#0A0A0A] p-3 space-y-2"
               data-testid="live-console-desktop-status-cards">
            <div className="rounded-xl bg-white dark:bg-[#141414] border border-[#E5E7EB] dark:border-white/10 p-3"
                 data-testid="live-console-desktop-broadcast-status">
              <p className="inline-flex items-center gap-1 text-[11px] font-black text-gray-500 mb-1.5">
                <Wifi size={11} /> 방송 상태
              </p>
              <div className="inline-flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${
                  isLive ? 'bg-emerald-500 animate-pulse'
                    : phase === 'countdown' ? 'bg-amber-500 animate-pulse'
                    : phase === 'ended' ? 'bg-gray-400'
                    : 'bg-gray-300'
                }`} />
                <span className="text-[12px] font-black text-black dark:text-white"
                      data-testid="live-console-desktop-status-text">
                  {isLive ? '안정적으로 방송 중입니다'
                    : phase === 'countdown' ? '방송 시작 준비 중…'
                    : phase === 'ended' ? '방송 종료됨'
                    : previewOn ? '카메라 대기 중' : '카메라 꺼짐'}
                </span>
              </div>
            </div>
            <div className="rounded-xl bg-white dark:bg-[#141414] border border-[#E5E7EB] dark:border-white/10 p-3 space-y-1.5"
                 data-testid="live-console-desktop-stream-info">
              <p className="text-[11px] font-black text-gray-500 mb-0.5">방송 키 / 스트림 정보</p>
              {[
                { k: '세션 ID', v: session?.session_id || '—', copyable: !!session?.session_id, tid: 'session' },
                { k: '해상도',  v: '—',                        copyable: false, tid: 'resolution' },
                { k: '프레임',  v: '—',                        copyable: false, tid: 'frame' },
                { k: '지연시간', v: '—',                        copyable: false, tid: 'latency' },
              ].map((row) => (
                <div key={row.k} className="flex items-center gap-2 text-[11px]"
                     data-testid={`live-console-desktop-stream-${row.tid}`}>
                  <span className="shrink-0 w-14 text-gray-500">{row.k}</span>
                  <span className="flex-1 min-w-0 truncate font-black text-black dark:text-white font-mono text-[11px]">
                    {row.v}
                  </span>
                  {row.copyable ? (
                    <button type="button"
                            onClick={() => {
                              try { navigator.clipboard.writeText(String(row.v)); toast.success('복사됨'); } catch {}
                            }}
                            className="p-1 rounded hover:bg-[#F7F8FA] dark:hover:bg-white/10 text-gray-400 hover:text-black dark:hover:text-white"
                            aria-label={`${row.k} 복사`}
                            data-testid={`live-console-desktop-stream-copy-${row.tid}`}>
                      <Copy size={11} />
                    </button>
                  ) : (
                    <span className="w-5" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Mobile Bottom Sheet · STEP OVERLAY-UX-FINAL-2 / 2B · 아주 연한 반투명 +
          swipe-down dismiss.  대표 요구: 뒤의 LIVE 영상이 아주 살짝 비치되
          패널은 밝고 가벼운 느낌 유지 · 강한 blur·검은 반투명 금지.
          dismiss 는 panel UI 상태만 변경 · LiveKit/camera/mic lifecycle 미호출.

          STEP OVERLAY-UX-FINAL-2B 보완:
            · bg 를 /85 로 살짝 더 투명 (텍스트/버튼 가독성 유지 위해 /50 미만 금지).
            · 내부 scrollable 영역의 scrollTop > 0 이면 downward swipe-dismiss 시작 금지.
            · drag handle 에서 시작한 touch 는 scroll 상태와 무관하게 dismiss 허용. */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="live-console-mobile-sheet">
          {/* STEP STUDIO-MOBILE-UI-2 · dimmer 를 bg-white/10 + 미세 blur 로 변경 →
              영상이 거의 그대로 보이는 밝은 반투명.  하단 조작바와의 click-through 는
              z-40 overlay 로 자연스럽게 차단 (dimmer 가 전체 inset-0 를 덮기 때문). */}
          <div className="absolute inset-0 bg-white/10 backdrop-blur-[2px]" onClick={() => setDrawerOpen(false)}
                data-testid="live-console-sheet-dimmer" />
          <div className="absolute inset-x-0 bottom-0 max-h-[75vh] rounded-t-2xl flex flex-col animate-in slide-in-from-bottom duration-200 shadow-2xl bg-white/85 dark:bg-[#141414]/85 touch-pan-y"
                style={{ WebkitBackdropFilter: 'saturate(1.05)',
                           backdropFilter: 'saturate(1.05)' }}
                onTouchStart={(e) => {
                  try {
                    const t = e.touches[0];
                    const el = e.currentTarget;
                    // STEP OVERLAY-UX-FINAL-2B · scroll/swipe conflict guard.
                    // drag handle 에서 시작한 touch 는 항상 dismiss 허용.
                    // 그 외 영역: 가장 가까운 overflow-y-auto 조상의 scrollTop
                    // 을 확인 → 0 이 아니면 panel dismiss 시작 금지 (리스트가
                    // 스크롤되게 양보).  이 가드는 scrollTop===0 으로 복귀해
                    // 다시 touchstart 가 발생하면 자연스럽게 해제된다.
                    const isHandle = e.target && (
                      e.target.closest && e.target.closest('[data-sheet-drag-handle="1"]'));
                    if (!isHandle) {
                      let n = e.target;
                      while (n && n !== el) {
                        try {
                          const s = window.getComputedStyle(n);
                          if (s && (s.overflowY === 'auto' || s.overflowY === 'scroll')) {
                            if (n.scrollTop > 0) {
                              el.dataset._dragging = '0';
                              return;
                            }
                            break;
                          }
                        } catch (_e) {}
                        n = n.parentElement;
                      }
                    }
                    el.dataset._dragStartY = String(t.clientY);
                    el.dataset._dragging = '1';
                  } catch (_e) {}
                }}
                onTouchMove={(e) => {
                  const el = e.currentTarget;
                  if (el.dataset._dragging !== '1') return;
                  const y0 = parseFloat(el.dataset._dragStartY || '0');
                  const dy = e.touches[0].clientY - y0;
                  if (dy > 0) {
                    el.style.transform = `translateY(${Math.min(dy, 400)}px)`;
                    el.style.transition = 'none';
                  }
                }}
                onTouchEnd={(e) => {
                  const el = e.currentTarget;
                  if (el.dataset._dragging !== '1') {
                    // 스크롤 중이었던 touch — transform 복원 불필요.
                    return;
                  }
                  const y0 = parseFloat(el.dataset._dragStartY || '0');
                  const yEnd = (e.changedTouches && e.changedTouches[0])
                                 ? e.changedTouches[0].clientY : y0;
                  const dy = yEnd - y0;
                  el.dataset._dragging = '0';
                  el.style.transition = 'transform 180ms ease';
                  // > 120px 아래로 끌면 close · 그 외는 원위치.
                  // close path 는 drawer UI state 만 변경 · broadcast/LiveKit
                  // lifecycle 함수는 절대 호출하지 않는다.
                  if (dy > 120) {
                    el.style.transform = 'translateY(100%)';
                    setTimeout(() => setDrawerOpen(false), 180);
                  } else {
                    el.style.transform = 'translateY(0)';
                  }
                }}
                data-testid="live-console-sheet-panel">
            {/* STEP OVERLAY-UX-FINAL-2 / 2B · drag handle (큰 touch target).
                swipe-down dismiss 전용 affordance · data-sheet-drag-handle 로
                scroll guard 가 이 영역의 touch 를 항상 dismiss 로 승격시킨다. */}
            <div className="pt-2.5 pb-1.5 flex justify-center shrink-0"
                  data-sheet-drag-handle="1"
                  data-testid="live-console-sheet-drag-handle">
              <span className="w-12 h-1.5 rounded-full bg-black/20 dark:bg-white/20" />
            </div>
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E7EB]/70 dark:border-white/10 bg-white/55 dark:bg-[#0F0F0F]/55 rounded-t-2xl">
              <div className="flex items-stretch gap-1">
                {SIDE_ITEMS.filter((s) => s.key !== 'live').map((s) => (
                  <button key={s.key} type="button" onClick={() => setActiveTab(s.key)}
                          className={`min-h-[36px] px-3 rounded-full text-[11px] font-black transition inline-flex items-center gap-1 ${
                            activeTab === s.key
                              ? 'bg-[#FF3B30] text-white shadow-sm'
                              : 'bg-white border border-[#E5E7EB] text-gray-600 dark:bg-white/10 dark:border-transparent dark:text-gray-300'
                          }`}
                          data-testid={`live-console-sheet-tab-${s.key}`}>
                    <s.icon size={12} />
                    {s.label}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setDrawerOpen(false)}
                      className="min-w-[40px] min-h-[40px] p-2 rounded-full hover:bg-white/60 inline-flex items-center justify-center"
                      aria-label="조작 패널 닫기"
                      data-testid="live-console-sheet-close">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden" style={{ overscrollBehavior: 'contain' }}>
              {activeTab === 'products' && (
                <div className="flex-1 min-h-0 overflow-y-auto" style={{ overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
                  {/* STEP STUDIO-PRODUCT-FLICKER-RCA-2 · 모바일 ProductsTab 도 동일 key 수정. */}
                  <ProductsTab key={`mp::${scheduleId || 'nil'}`}
                                sessionId={session?.session_id} scheduleId={scheduleId} isLive={isLive}
                                sessionWarning={session?.warning}
                                openNoticeEditor={openNoticeEditor}
                                onOpenQuickAdd={() => setQuickAddOpen(true)}
                                draftPreset={draftPreset}
                                isTestMode={isTestMode}
                                externalCtx={isTestMode ? liveCtx : null}
                                setExternalCtx={isTestMode ? setLiveCtx : null}
                                isProductPinned={pinMode === 'product'}
                                onProductPinToggle={() => setPinMode((m) => m === 'product' ? 'none' : 'product')}
                                onOpenOverlaySettings={() => { setDrawerOpen(false); setOverlaySettingsOpen(true); }}
                                pastSlider={pastSlider}
                                remoteCursorPid={remoteOn ? remoteCursorPid : null}
                                remoteMenu={isTestMode}
                                liveNotice={liveNotice} />
                </div>
              )}
              {activeTab === 'comments' && (
                <CommentsOrdersTab key={`mc:${session?.session_id || 'nil'}::${scheduleId || 'nil'}`}
                                   sharedMessages={sharedMessages}
                                   isPinned={pinMode === 'comments'}
                                   onPinToggle={() => setPinMode((m) => m === 'comments' ? 'none' : 'comments')}
                                    sessionId={session?.session_id} scheduleId={scheduleId} channels={channels} isLive={isLive} isOwner={isOwner} />
              )}
              {activeTab === 'status' && (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {!isTestMode && scheduleId && (
                    <div className="m-3 p-3 rounded-xl border border-black/10 dark:border-white/10" data-testid="studio-rec-overview-pc">
                      <RecordingOverview scheduleId={scheduleId} reloadKey={session?.streaming_status} />
                    </div>
                  )}
                  <StatusTab liveKitState={publishState} sessionId={session?.session_id} sessionStatus={session?.streaming_status} channels={channels} refreshChannels={refreshChannels} egressStatus={egressStatus} egressCapability={egressCapability} onOpenYtEgress={() => setYtEgressOpen(true)} onStopYtEgress={stopYtEgress} onOpenYtComment={() => setYtCommentOpen(true)} ytStatus={ytStatus} onOpenIgEgress={() => setIgEgressOpen(true)} onStopIgEgress={stopIgEgress} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* STEP 8-I · Mobile Filter Bottom Sheet · PREVIEW ONLY */}
      {filterOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="live-console-filter-sheet">
          <div className="absolute inset-0 bg-black/30" onClick={() => setFilterOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white dark:bg-[#141414] flex flex-col animate-in slide-in-from-bottom duration-200 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between px-4 pt-4 pb-3">
              <div className="inline-flex items-center gap-2">
                <Sparkles size={16} className="text-[#FF3B30]" />
                <p className="text-sm font-black text-black dark:text-white">카메라 필터</p>
              </div>
              <button type="button" onClick={() => setFilterOpen(false)} className="p-1.5"
                      data-testid="live-console-filter-close">
                <X size={18} />
              </button>
            </div>
            <div className="px-4 pb-2">
              <div className="grid grid-cols-5 gap-2" data-testid="live-console-filter-presets">
                {FILTER_PRESETS.map((p) => (
                  <button key={p.key} type="button" onClick={() => setFilterMode(p.key)}
                          className={`min-h-[44px] px-2 py-2 rounded-xl text-xs font-black transition-all ${
                            filterMode === p.key
                              ? 'bg-[#FF3B30] text-white ring-2 ring-[#FF3B30]/40'
                              : 'bg-black/[0.05] dark:bg-white/10 text-black dark:text-white'
                          }`}
                          data-testid={`live-console-filter-preset-${p.key}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {filterMode !== 'original' && (
              <div className="px-4 py-3 space-y-2" data-testid="live-console-filter-intensity-box">
                <div className="flex items-center justify-between text-[11px] font-bold">
                  <span className="text-gray-500">약하게</span>
                  <span className="text-black dark:text-white">강도 {filterIntensity}</span>
                  <span className="text-gray-500">강하게</span>
                </div>
                <input type="range" min="0" max="100" step="5"
                        value={filterIntensity}
                        onChange={(e) => setFilterIntensity(Number(e.target.value))}
                        className="w-full accent-[#FF3B30]"
                        aria-label="필터 강도"
                        data-testid="live-console-filter-intensity" />
              </div>
            )}
            <div className="mx-4 mb-3 mt-1 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30"
                  data-testid="live-console-filter-disclaimer">
              <p className="text-[11px] leading-relaxed text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
                <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                이 필터는 판매자 화면에만 적용돼요. 시청자 화면 · YouTube 송출에는 다음 업데이트에서 지원돼요.
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 pb-2">
              <button type="button"
                      onClick={() => { setFilterMode('original'); setFilterIntensity(60); }}
                      className="flex-1 min-h-[44px] px-3 py-2.5 rounded-full bg-black/[0.05] dark:bg-white/10 text-black dark:text-white text-xs font-black"
                      data-testid="live-console-filter-reset">
                초기화
              </button>
              <button type="button" onClick={() => setFilterOpen(false)}
                      className="flex-1 min-h-[44px] px-3 py-2.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black"
                      data-testid="live-console-filter-apply">
                적용
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP STUDIO-MOBILE-RAIL-ROUTING-FIX-1 · 타이머 전용 Bottom Sheet.
          기존 OverlayTimerControl 100% REUSE (신규 engine/API 0건).  rail 타이머 버튼
          에서만 열리며, 열려 있을 때 rail 은 숨기므로 다른 패널과 충돌 없음. */}
      {timerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="live-console-timer-sheet">
          <div className="absolute inset-0 bg-black/30" onClick={() => setTimerOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-[#141414] flex flex-col animate-in slide-in-from-bottom duration-200 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between px-4 pt-4 pb-3 sticky top-0 bg-white dark:bg-[#141414] border-b border-black/5 dark:border-white/5">
              <p className="text-sm font-black text-black dark:text-white inline-flex items-center gap-2">
                {isTestMode ? <><Megaphone size={16} className="text-[#FF3B30]" /> 공지 · 타이머</> : <><Clock size={16} className="text-[#FF3B30]" /> 타이머</>}
              </p>
              <button type="button" onClick={() => setTimerOpen(false)} className="p-1.5"
                      data-testid="live-console-timer-close">
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-3 space-y-3">
              {/* STEP STUDIO-MOBILE-NOTICE-TIMER-1 · 방송 알림 (NOTICE) 섹션 — 기존
                  openNoticeEditor 모달 100% REUSE.  데이터는 liveCtx.live_notice (SoT).
                  타이머와 공지는 독립적으로 작동 (서로 자동 삭제·충돌 없음). */}
              <section className="rounded-xl border border-black/10 dark:border-white/10 p-3 space-y-2 bg-black/[0.02] dark:bg-white/[0.02]"
                       data-testid="live-console-timer-sheet-notice">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-black text-black dark:text-white inline-flex items-center gap-1.5">
                    <span className="text-base">📢</span> 공지
                    {isTestMode && <span className="text-[10px] font-bold text-gray-500" data-testid="live-console-timer-sheet-notice-scope">자사라이브용 공지</span>}
                  </p>
                  {liveNotice?.updated_at && (
                    <span className="text-[10px] text-gray-400">
                      {new Date(liveNotice.updated_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                {(liveNotice?.text || liveNotice?.image_url) ? (
                  <div className="flex items-center gap-2">
                    {liveNotice?.image_url && !isTestMode && (
                      <img src={liveNotice.image_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                    )}
                    <p className="text-xs text-gray-700 dark:text-gray-200 flex-1 min-w-0 truncate"
                       data-testid="live-console-timer-sheet-notice-text">
                      {liveNotice?.text || '(이미지 공지)'}
                    </p>
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-500" data-testid="live-console-timer-sheet-notice-empty">
                    {isTestMode ? '아직 공지가 없어요. 문구를 추가해 보세요.' : '아직 공지가 없어요. 문구·이미지를 추가해 보세요.'}
                  </p>
                )}
                <div className="flex gap-2">
                  <button type="button"
                          onClick={() => { setTimerOpen(false); openNoticeEditor(); }}
                          disabled={!isLive}
                          className="flex-1 min-h-[40px] rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-black disabled:opacity-40"
                          data-testid="live-console-timer-sheet-notice-edit">
                    {(liveNotice?.text || liveNotice?.image_url) ? '공지 수정' : '공지 작성'}
                  </button>
                  {(liveNotice?.text || liveNotice?.image_url) && (
                    <button type="button"
                            onClick={clearLiveNotice}
                            disabled={!isLive || noticeBusy}
                            className="min-h-[40px] px-3 rounded-full border-2 border-[#FF3B30] text-[#FF3B30] text-xs font-black hover:bg-[#FF3B30]/10 disabled:opacity-40"
                            data-testid="live-console-timer-sheet-notice-clear">
                      공지 내리기
                    </button>
                  )}
                  {!isTestMode && <TimerNoticeHideBtn />}
                </div>
                {!isTestMode && <p className="text-[11px] text-gray-500" data-testid="live-console-timer-sheet-notice-hide-hint">외부 송출 유지 · 판매자 화면만 숨김</p>}
                <StudioNoticeLibraryList activeId={liveNotice?.notice_id || null}
                    onSelect={(n) => {
                      remoteOps.notice(n);
                      if (isTestMode) toast.success('공지를 표시했어요.');
                    }} />
              </section>

              {/* STEP STUDIO-MOBILE-NOTICE-TIMER-1 · 카운트다운 타이머 — 기존 OverlayTimerControl REUSE. */}
              <section className="rounded-xl border border-black/10 dark:border-white/10 p-3 bg-black/[0.02] dark:bg-white/[0.02]"
                       data-testid="live-console-timer-sheet-timer">
                <p className="text-xs font-black text-black dark:text-white inline-flex items-center gap-1.5 mb-2">
                  <Clock size={14} className="text-[#FF3B30]" /> 카운트다운 타이머
                </p>
                {scheduleId ? (
                  <OverlayTimerControl scheduleId={scheduleId}
                                       timer={liveCtx?.timer}
                                       disabled={!isLive}
                                       onLocalAction={isTestMode ? _testTimerAction(setLiveCtx) : undefined} />
                ) : (
                  <p className="text-xs text-gray-500 text-center py-2" data-testid="live-console-timer-empty">
                    편성표 방송이 아니어서 타이머를 사용할 수 없어요.
                  </p>
                )}
                {showPresets && (
                  <div className="mt-2">
                    <LivePresetPanel ctx={presetCtx} apply={presetApply} canStart={isLive} collapsible onStartTimer={presetStart} connected={!isTestMode} />
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}

      {/* STEP STUDIO-MOBILE-PANEL-VISIBILITY-1 · 조작부 숨김/복원 토글 pill.
          화면 좌측 상단 (safe-area 아래) 에 고정.  작은 반투명 pill. */}
      {isLive && (
        <button type="button"
                onClick={() => setControlsVisible((v) => !v)}
                className="lg:hidden absolute left-2 z-40 w-10 h-10 rounded-full backdrop-blur-md shadow-lg bg-white/85 dark:bg-black/55 text-gray-800 dark:text-white border border-white/70 dark:border-white/15 inline-flex items-center justify-center transition pointer-events-auto"
                style={{ top: 'calc(env(safe-area-inset-top) + 4rem)' }}
                aria-label={controlsVisible ? '조작부 숨기기' : '조작부 보이기'}
                data-testid="live-console-controls-toggle">
          {controlsVisible ? <EyeOff size={16} /> : <Settings size={16} />}
        </button>
      )}

      {/* STEP STUDIO-MOBILE-PANEL-VISIBILITY-1 · 댓글 고정 floating 패널.
          sharedMessages 재사용 (중복 폴링 0 · 신규 subscription 0).
          size: sm=4 / md=6 / lg=10 (유동) · scroll stuck-to-bottom + 사용자 수동 스크롤 감지. */}
      {isLive && pinMode === 'comments' && (() => {
        const limit = commentsPinSize === 'sm' ? 4 : commentsPinSize === 'lg' ? 10 : 6;
        const visible = (sharedMessages || []).slice(-limit);
        const nextSize = { sm: 'md', md: 'lg', lg: 'sm' }[commentsPinSize];
        const heightCls = commentsPinSize === 'sm' ? 'max-h-[9rem]'
                        : commentsPinSize === 'lg' ? 'max-h-[20rem]'
                        : 'max-h-[14rem]';
        return (
          <div className="lg:hidden absolute left-2 z-30 w-[62%] rounded-xl bg-black/55 backdrop-blur-md shadow-lg border border-white/15 overflow-hidden pointer-events-auto"
               style={{ bottom: controlsVisible && bottomBarOpen ? 'calc(env(safe-area-inset-bottom) + 7.5rem)' : 'calc(env(safe-area-inset-bottom) + 2.5rem)' }}
               data-testid="live-console-pin-comments">
            <div className="flex items-center justify-between gap-1 px-2 py-1 bg-black/40">
              <span className="text-[10px] font-black text-white inline-flex items-center gap-1">
                <MessageCircle size={11} /> 댓글 고정
              </span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setCommentsPinSize(nextSize)}
                        className="text-[9px] font-black text-white/80 px-1.5 py-0.5 rounded border border-white/25 hover:bg-white/10"
                        aria-label="크기 변경"
                        data-testid="live-console-pin-comments-size">
                  {commentsPinSize === 'sm' ? '작게' : commentsPinSize === 'lg' ? '크게' : '보통'}
                </button>
                <button type="button" onClick={() => setPinMode('none')}
                        className="w-5 h-5 inline-flex items-center justify-center text-white/80 hover:text-white"
                        aria-label="고정 해제"
                        data-testid="live-console-pin-comments-close">
                  <X size={11} />
                </button>
              </div>
            </div>
            <div className={`${heightCls} overflow-y-auto px-2 py-1 space-y-0.5`} data-testid="live-console-pin-comments-scroll">
              {visible.length === 0 ? (
                <p className="text-[10px] text-white/60 text-center py-2" data-testid="live-console-pin-comments-empty">
                  댓글이 아직 없어요.
                </p>
              ) : visible.map((m) => {
                const author = m.author_display || m.author_masked || '익명';
                return (
                  <div key={m.message_id} className="text-[11px] text-white/95 leading-snug break-words"
                       data-testid={`live-console-pin-msg-${m.message_id}`}>
                    <span className={`font-black mr-1 ${m.author_role === 'seller' ? 'text-[#FFB4A8]' : 'text-white'}`}>
                      {author}
                    </span>
                    <span>{(m.text || '').replace(/^🎉\s*/, '')}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* STEP STUDIO-MOBILE-PANEL-VISIBILITY-1 · 상품 고정 floating 패널.
          liveCtx.active_product 재사용 — 소개 변경/종료 즉시 반영.  tap → 상품 패널 열기. */}
      {isLive && pinMode === 'product' && liveCtx?.active_product && (
        <button type="button"
                onClick={() => { setActiveTab('products'); setDrawerOpen(true); }}
                className="lg:hidden absolute left-2 z-30 w-[62%] rounded-xl bg-black/55 backdrop-blur-md shadow-lg border border-[#FF3B30]/50 p-2 pointer-events-auto text-left"
                style={{ bottom: (() => {
                  const base = controlsVisible && bottomBarOpen ? 'calc(env(safe-area-inset-bottom) + 7.5rem)' : 'calc(env(safe-area-inset-bottom) + 2.5rem)';
                  // STUDIO-BR-TX3-SHUTTER-CAPTURE-1 · 캡처 버튼과 겹치면 고정 카드만 버튼 위로 올림 (송출 오버레이 무관).
                  const r = shutterBtn.rect;
                  const basePx = controlsVisible && bottomBarOpen ? 120 : 40;
                  const vh = window.innerHeight;
                  const overlap = isTestMode && r.left < window.innerWidth * 0.62 + 8 && r.bottom > vh - basePx - 96 && r.top < vh - basePx;
                  return overlap ? `${vh - r.top + 8}px` : base;
                })() }}
                data-testid="live-console-pin-product">
          <div className="flex items-center justify-between gap-1 mb-1">
            <span className="text-[10px] font-black text-[#FFB4A8] inline-flex items-center gap-1">
              <Package size={11} /> 소개 중 · Slot {liveCtx.active_product.slot_number}
            </span>
            <span onClick={(e) => { e.stopPropagation(); setPinMode('none'); }}
                  className="w-5 h-5 inline-flex items-center justify-center text-white/80 hover:text-white cursor-pointer"
                  role="button" aria-label="고정 해제"
                  data-testid="live-console-pin-product-close">
              <X size={11} />
            </span>
          </div>
          <div className="flex items-center gap-2">
            {liveCtx.active_product.image && (
              <img src={liveCtx.active_product.image} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-black text-white truncate">
                {liveCtx.active_product.name}
              </p>
              <p className="text-[11px] font-black text-[#FF9E8E]">
                {Number(liveCtx.active_product.sale_price || 0).toLocaleString('ko-KR')}원
              </p>
            </div>
          </div>
        </button>
      )}

      {/* STEP STUDIO-MOBILE-CONTROLS-UI-1 (2026-10-12) · 전용 Settings bottom sheet.
          촬영 환경 설정 전용 — 카메라 전환 · 카메라 필터 · 상품 Overlay 바로가기.
          기존 flipCamera · setFilterOpen · setActiveTab('products') 100% REUSE. */}
      {settingsOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="live-console-settings-sheet">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSettingsOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-[#141414] flex flex-col animate-in slide-in-from-bottom duration-200 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between px-4 pt-4 pb-3 sticky top-0 bg-white dark:bg-[#141414] border-b border-black/5 dark:border-white/5">
              <p className="text-sm font-black text-black dark:text-white inline-flex items-center gap-2">
                <Settings size={16} className="text-[#FF3B30]" /> 촬영 환경 설정
              </p>
              <button type="button" onClick={() => setSettingsOpen(false)} className="p-1.5"
                      data-testid="live-console-settings-close">
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-3 space-y-2">
              <button type="button"
                      onClick={() => { setSettingsOpen(false); flipCamera(); }}
                      className="w-full min-h-[48px] px-3 py-2.5 rounded-xl bg-black/[0.05] dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 text-black dark:text-white text-xs font-bold inline-flex items-center gap-2"
                      data-testid="live-console-settings-flip">
                <RefreshCw size={14} /> 카메라 전환 (전면·후면)
                <span className="ml-auto text-[10px] text-gray-500">실 하드웨어 전환</span>
              </button>
              <button type="button"
                      onClick={() => { setSettingsOpen(false); setFilterOpen(true); }}
                      className="w-full min-h-[48px] px-3 py-2.5 rounded-xl bg-black/[0.05] dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 text-black dark:text-white text-xs font-bold inline-flex items-center gap-2"
                      data-testid="live-console-settings-filter">
                <Sparkles size={14} /> 카메라 필터
                {filterMode !== 'original' && (
                  <span className="ml-auto px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px]">
                    {FILTER_PRESETS.find((p) => p.key === filterMode)?.label}
                  </span>
                )}
              </button>
              <button type="button"
                      onClick={() => { setSettingsOpen(false); setOverlaySettingsOpen(true); }}
                      className="w-full min-h-[52px] px-3 py-2.5 rounded-xl bg-[#FF3B30]/10 hover:bg-[#FF3B30]/15 text-[#FF3B30] text-sm font-black inline-flex items-center gap-2 border border-[#FF3B30]/20"
                      data-testid="live-console-settings-overlay-unified">
                <Layers size={16} /> 화면 프리셋 · Overlay
                <span className="ml-auto text-[10px] text-[#FF3B30]/80">프리셋 · 동시 표시 · 배치</span>
              </button>
              {process.env.REACT_APP_STUDIO_TEST_ENTRY === '1' && !isTestMode && !isLive && (
                <button type="button"
                        onClick={() => { setSettingsOpen(false); navigate('/seller/live-console?testmode=1'); }}
                        className="w-full min-h-[44px] px-3 py-2.5 rounded-xl border border-amber-400 bg-amber-50 text-amber-700 text-xs font-black inline-flex items-center gap-2"
                        data-testid="live-console-settings-test-entry">
                  <FlaskConical size={14} /> 방송 테스트 (송출 없음)
                  <span className="ml-auto text-[10px] text-amber-700/80">테스트 모드로 이동</span>
                </button>
              )}
              {isTestMode && (
                <button type="button"
                        onClick={() => { setSettingsOpen(false); setRemoteDiagOpen(true); }}
                        className="w-full min-h-[44px] px-3 py-2.5 rounded-xl border border-black/10 dark:border-white/10 bg-[#F7F8FA] dark:bg-white/5 text-gray-800 dark:text-white text-xs font-black inline-flex items-center gap-2"
                        data-testid="live-console-settings-remote-diag">
                  <Gamepad2 size={14} /> 리모컨 입력 확인
                  <span className="ml-auto text-[10px] text-gray-500">테스트 모드 전용 · 동작 없음</span>
                </button>
              )}
              {(isTestMode || isLive) && (
                <button type="button"
                        onClick={() => { setSettingsOpen(false); setRemoteOn((v) => !v); }}
                        className="w-full min-h-[44px] px-3 py-2.5 rounded-xl border border-[#0A84FF]/30 bg-[#0A84FF]/10 text-[#0A84FF] text-xs font-black inline-flex items-center gap-2"
                        data-testid="live-console-settings-remote-control">
                  <Gamepad2 size={14} /> 리모컨 상품 조작 {remoteOn ? 'ON' : 'OFF'}
                  <span className="ml-auto text-[10px] text-[#0A84FF]/80">{isTestMode ? '테스트 모드 · 펜 입력 차단' : '실제 방송 · 셔터 HOLD'}</span>
                </button>
              )}
              {isTestMode && (
                <div className="grid grid-cols-[1fr_auto] gap-1.5">
                  <button type="button"
                          onClick={() => { setSettingsOpen(false); setRemoteOn(false); setRemoteCursorPid(null); setShutterCalOpen(true); }}
                          className="min-h-[44px] px-3 py-2.5 rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/10 text-[#FF3B30] text-xs font-black inline-flex items-center gap-2"
                          data-testid="live-console-settings-shutter-calibrate">
                    <Camera size={14} /> 캡처 버튼 위치 맞추기
                    <span className="ml-auto text-[10px] text-[#FF3B30]/80">{shutterBtn.saved ? `저장됨 · ${shutterBtn.saved.type || ''}` : '기본 위치'}</span>
                  </button>
                  {shutterBtn.saved && (
                    <button type="button" onClick={() => shutterBtn.save(null)}
                            className="min-h-[44px] px-3 rounded-xl border border-black/10 text-[11px] font-black"
                            data-testid="live-console-settings-shutter-reset">초기화</button>
                  )}
                </div>
              )}
              <div className="w-full px-3 py-2.5 rounded-xl border border-black/10 dark:border-white/10 bg-[#F7F8FA] dark:bg-white/5" data-testid="live-console-settings-quickreg-mode">
                <p className="text-[11px] font-black text-gray-800 dark:text-white">캡처 상품 등록 담당</p>
                <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                  {[['self', '직접 등록'], ['assistant', '보조자 등록']].map(([m, l]) => (
                    <button key={m} type="button" onClick={() => setQuickRegMode(m)}
                            className={`min-h-[36px] rounded-lg text-xs font-black ${quickRegMode === m ? 'bg-[#FF3B30] text-white' : 'bg-white dark:bg-white/10 text-gray-700 dark:text-white border border-black/10'}`}
                            data-testid={`live-console-quickreg-mode-${m}`}>{l}</button>
                  ))}
                </div>
                <p className="mt-1 text-[10px] text-gray-500">{quickRegMode === 'assistant' ? '캡처를 같은 방송 콘솔로 보내요 · 이 화면에는 입력창이 열리지 않아요' : '캡처 후 이 화면에서 바로 입력해요'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTestMode && remoteDiagOpen && <RemoteInputDiagPanel onClose={() => setRemoteDiagOpen(false)} />}
      {isTestMode && !shutterCalOpen && !drawerOpen && (
        <ShutterCaptureButton rect={shutterBtn.rect} onCapture={startQuickRegister} countdown={quickCountdown} />
      )}
      {isTestMode && shutterCalOpen && (
        <ShutterCalibrator onDone={(p) => shutterBtn.save(p)} onClose={() => setShutterCalOpen(false)} />
      )}
      {remoteOn && !remoteDiagOpen && (isTestMode || isLive) && (
        <RemoteControlLayer
          onExit={() => { setRemoteOn(false); setRemoteCursorPid(null); }}
          items={liveCtx?.live_products || []}
          activePid={liveCtx?.active_product?.product_id || null}
          listOpen={drawerOpen && activeTab === 'products'}
          openList={() => { setActiveTab('products'); setDrawerOpen(true); }}
          closeList={() => setDrawerOpen(false)}
          introducePid={isTestMode ? (pid) => setLiveCtx((c) => applyTestIntroduce(c, pid, draftPreset))
            : (pid) => { Promise.resolve(realLiveOps.announce(pid, ['default', 'full', 'price', 'options', 'mini'].includes(draftPreset) ? draftPreset : undefined)).catch((e) => toast.error(e?.response?.data?.detail || '소개 변경 실패')); }}
          setCursorPid={setRemoteCursorPid}
          canIntroduce={isLive}
          onShutter={isTestMode ? startQuickRegister : null}
          ctx={isTestMode ? liveCtx : { ...liveCtx, ...realCtx }}
          ops={remoteOps}
          onQuickAdd={startQuickRegister}
          past={{
            mode: pastSliderMode, playing: previewPlaying, list: pastList,
            index: pastList.length ? previewIndex % pastList.length : 0,
            on: () => setPastSliderMode((m) => (m === 'off' ? 'loop' : m)),
            off: () => { setPastSliderMode('off'); setPreviewPlaying(false); },
            play: () => { setPreviewIndex(0); setPreviewPlaying(true); },
            stop: () => setPreviewPlaying(false),
            show: (i) => { setPreviewIndex(i); setPreviewPlaying(true); },
          }}
          touchBlock={!quickAddOpen}
          blocked={settingsOpen || overlaySettingsOpen || moreOpen || filterOpen || timerOpen || quickAddOpen || quickCountdown !== null} />
      )}

      {/* STEP STUDIO-OVERLAY-UX-FIX-1-STEP3 · 통합 Overlay 설정 (설정 · 상품 패널 공통 진입). */}
      {isTestMode && (
        <LivePreviewModal kind={previewKind} onClose={() => setPreviewKind(null)}
                          ctx={previewKind ? { ...(liveCtx || {}), overlay_layout: overlayLayout.layout } : null} />
      )}
      <OverlaySettingsSheet open={overlaySettingsOpen} onClose={() => setOverlaySettingsOpen(false)}
                            sc={showCombo} ol={overlayLayout} orient={overlayOrient} isLive={isLive}
                            isTestMode={isTestMode} canBroadcast={!!scheduleId}
                            extPreview={extPreview} setExtPreview={setExtPreview}
                            selfToggle={(
                              <div className="space-y-2">
                                <SelfLiveOverlayToggle title="상품 이미지·가격 카드" />
                                <SelfLiveOverlayToggle settingKey="self_live_desc_enabled" title="상품설명 (사이즈·소재·기타)"
                                                       onText="상품설명 표시를 켜면 소비자 LIVE 영상 위에도 표시됩니다."
                                                       offText="소비자 LIVE 영상 위에 상품설명을 표시하지 않습니다 (기본값)."
                                                       testid="self-live-desc-toggle" />
                              </div>
                            )}
                            liveNotice={liveNotice} onOpenNotice={openNoticeEditor} onClearNotice={clearLiveNotice}
                            onEditLayout={startLayoutEdit}
                            presetPicker={
                              <OverlayPresetPicker scheduleId={scheduleId} liveCtx={liveCtx} isLive={isLive}
                                                   draftPreset={draftPreset} setDraftPreset={setDraftPreset}
                                                   isTestMode={isTestMode} setLiveCtx={setLiveCtx} variant="mobile" hideSelfToggle />}
                            timerNode={scheduleId ? (
                              <>
                              <OverlayTimerControl scheduleId={scheduleId} timer={liveCtx?.timer} disabled={!isLive} compact
                                                   data-testid="ovs-timer-control"
                                                   onLocalAction={isTestMode ? _testTimerAction(setLiveCtx) : undefined} />
                              {showPresets && <LivePresetPanel ctx={presetCtx} apply={presetApply} canStart={isLive} collapsible onStartTimer={presetStart} connected={!isTestMode} />}
                              </>
                            ) : null} />

      {/* STEP 8-I · Mobile More Bottom Sheet · 기존 YouTube Egress/Comment/방송종료 REUSE */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="live-console-more-sheet">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMoreOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-[#141414] flex flex-col animate-in slide-in-from-bottom duration-200 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between px-4 pt-4 pb-3 sticky top-0 bg-white dark:bg-[#141414] border-b border-black/5 dark:border-white/5">
              <p className="text-sm font-black text-black dark:text-white">방송 관리</p>
              <button type="button" onClick={() => setMoreOpen(false)} className="p-1.5"
                      data-testid="live-console-more-close">
                <X size={18} />
              </button>
            </div>
            {/* STEP 8-I-3 · 3-category 진입 카드 · 기존 drawer + activeTab 100% REUSE.
                방송상품 → ProductsTab · 댓글·주문 → CommentsOrdersTab · 방송상태 → StatusTab.
                신규 화면/handler/API 0건. STEP 8-I-5 · bright commerce cards.
                STEP STUDIO-MOBILE-CONTROLS-UI-1 (2026-10-12) · 상품·댓글 카드 제거 — rail
                전용 버튼으로 중복 접근 가능.  방송상태 (StatusTab) 만 유지. */}
            <div className="px-4 pt-3 pb-2 grid grid-cols-1 gap-2" data-testid="live-console-more-categories">
              <button type="button"
                      onClick={() => { setMoreOpen(false); setActiveTab('status'); setDrawerOpen(true); }}
                      className="min-h-[56px] flex items-center justify-start gap-2 px-4 rounded-2xl bg-white hover:bg-[#F7F8FA] border border-[#E5E7EB] text-gray-800 dark:bg-white/10 dark:border-transparent dark:text-white dark:hover:bg-white/20 font-black transition"
                      data-testid="live-console-more-cat-status">
                <Wifi size={18} />
                <span className="text-[12px]">방송상태 확인</span>
                <span className="ml-auto text-[10px] text-gray-400">LiveKit · 송출 · 뷰어</span>
              </button>
            </div>
            {/* YouTube 상태 요약 + 기존 modal 진입 (신규 handler 0건) */}
            <div className="px-4 py-3 space-y-2" data-testid="live-console-more-yt-box">
              <p className="text-[11px] font-black text-gray-500 uppercase tracking-wide">YouTube</p>
              {(() => {
                // STEP STUDIO-LIFECYCLE-STABILIZATION · use latest non-stopped
                // youtube destination (같은 세션에서 여러 개 시도됐을 때 stale
                // 을 "송출 중" 으로 오인하지 않도록).  Egress 유효 상태만 ON.
                const ytDest = (egressStatus?.destinations || [])
                  .filter((d) => d.platform === 'youtube' && d.status !== 'stopped')
                  .slice(-1)[0];
                const ytEgressOn = ytDest && ['active', 'starting', 'running'].includes(ytDest.status);
                const ytSess = ytStatus?.session || null;
                const ytCommentOn = ytSess && ytSess.status === 'connected';
                return (
                  <>
                    <div className="flex items-center gap-2 text-[12px]">
                      <span className={`inline-flex w-2 h-2 rounded-full ${ytEgressOn ? 'bg-red-500 animate-pulse' : 'bg-gray-400'}`} />
                      <span className="font-bold text-black dark:text-white">
                        {ytEgressOn ? '송출중' : '송출 미연결'}
                      </span>
                      {!ytEgressOn ? (
                        <button type="button"
                                onClick={() => { setMoreOpen(false); setYtEgressOpen(true); }}
                                disabled={egressCapability?.available === false}
                                className="ml-auto min-h-[36px] px-3 py-1.5 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] disabled:opacity-40 text-white text-[11px] font-black"
                                data-testid="live-console-more-yt-egress-connect">
                          YouTube 송출 연결
                        </button>
                      ) : (
                        <button type="button"
                                onClick={() => { setMoreOpen(false); stopYtEgress(); }}
                                className="ml-auto min-h-[36px] px-3 py-1.5 rounded-full bg-black/[0.06] dark:bg-white/10 hover:bg-red-500/10 text-red-500 text-[11px] font-black"
                                data-testid="live-console-more-yt-egress-stop">
                          송출 중지
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[12px]">
                      <span className={`inline-flex w-2 h-2 rounded-full ${ytCommentOn ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
                      <span className="font-bold text-black dark:text-white">
                        {ytCommentOn ? '댓글 연결됨' : '댓글 미연결'}
                      </span>
                      {!ytCommentOn && (
                        <button type="button"
                                onClick={() => { setMoreOpen(false); setYtCommentOpen(true); }}
                                className="ml-auto min-h-[36px] px-3 py-1.5 rounded-full bg-black/[0.06] dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 text-black dark:text-white text-[11px] font-black"
                                data-testid="live-console-more-yt-comment-connect">
                          YouTube 댓글 연결
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            {/* STEP STUDIO-MOBILE-CONTROLS-UI-1 (2026-10-12) · 카메라 전환/필터는
                설정 sheet (live-console-settings-sheet) 로 이관.  MorePanel 에서는 중복
                제거하여 '방송 운영' 전용 (상태 · 외부 플랫폼 · 공지 · 종료) 으로 압축. */}
            {/* 방송 종료 · 기존 endLive handler REUSE */}
            <div className="px-4 py-3 border-t border-black/5 dark:border-white/5">
              {/* STEP 8-I-5A · LIVE 공지 관리 항목 (More Sheet 안에 · 신규 페이지 X · 기존 modal REUSE) */}
              <button type="button"
                      onClick={() => { setMoreOpen(false); openNoticeEditor(); }}
                      className="w-full min-h-[44px] px-3 py-2.5 rounded-xl bg-[#FFF4F1] hover:bg-[#FFEAE6] border border-[#FFD6CE] text-[#17181C] text-xs font-black inline-flex items-center gap-2 mb-2"
                      data-testid="live-console-more-notice">
                <span className="text-base">📢</span>
                <span className="flex-1 text-left truncate">
                  {liveNotice?.text ? `공지중 · ${liveNotice.text}` : '방송 공지 작성'}
                </span>
                <span className="text-[10px] text-[#FF4F3D] shrink-0">{liveNotice?.text ? '수정' : '작성'}</span>
              </button>
              <button type="button"
                      onClick={() => { setMoreOpen(false); endLive(); }}
                      disabled={busy}
                      className="w-full min-h-[44px] px-3 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-40 text-white text-xs font-black inline-flex items-center justify-center gap-2"
                      data-testid="live-console-more-end">
                <StopCircle size={14} /> 방송 종료
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP MP-EGRESS-PREP-1 · Instagram Manual Stream-Key Modal · YouTube modal 미러. */}
      {igEgressOpen && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
              onClick={() => !igBusy && setIgEgressOpen(false)}
              data-testid="live-console-ig-egress-modal">
          <div className="bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 space-y-3"
                onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Radio size={16} className="text-[#E1306C]" />
              <h3 className="text-base font-black text-black dark:text-white">Instagram 송출 연결</h3>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Instagram Live Producer 에서<br />
              <b>Stream URL</b> 과 <b>Stream Key</b> 를 복사해주세요.
              <br /><span className="text-[10px] text-gray-400">스트림 키는 저장·로그·응답에 재노출되지 않아요.</span>
            </p>
            <label className="block">
              <span className="text-[11px] font-black text-gray-600 dark:text-gray-300">RTMPS URL</span>
              <input type="text" value={igUrl} onChange={(e) => setIgUrl(e.target.value)}
                      placeholder="rtmps://live-api-s.facebook.com:443/rtmp/"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/15 text-xs bg-transparent text-black dark:text-white focus:outline-none focus:border-[#E1306C]"
                      data-testid="live-console-ig-url-input" />
            </label>
            <label className="block">
              <span className="text-[11px] font-black text-gray-600 dark:text-gray-300">스트림 키</span>
              <input type="password" value={igKey} onChange={(e) => setIgKey(e.target.value)}
                      placeholder="FB-XXXXXXXXXX-0-Ab..."
                      autoComplete="off" autoCorrect="off" spellCheck="false"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/15 text-xs bg-transparent text-black dark:text-white focus:outline-none focus:border-[#E1306C] font-mono"
                      data-testid="live-console-ig-key-input" />
            </label>
            <p className="text-[10px] text-gray-400">
              LIVEMOA LiveKit → LiveKit Egress → Instagram Live. 실패해도 LIVEMOA 원본 방송은 계속돼요.
              <br />OAuth/Reels/Webhook 은 여기와 독립 · Meta 심사 통과 여부와 무관하게 판매자 개별 계정으로 송출.
            </p>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setIgEgressOpen(false)} disabled={igBusy}
                      className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/15 text-xs font-bold text-gray-700 dark:text-gray-200 disabled:opacity-40"
                      data-testid="live-console-ig-cancel">
                취소
              </button>
              <button type="button" onClick={submitIgEgress} disabled={igBusy}
                      className="flex-1 py-2 rounded-full bg-[#E1306C] text-white text-xs font-black hover:bg-[#C13584] disabled:opacity-40 inline-flex items-center justify-center gap-1"
                      data-testid="live-console-ig-submit">
                <Radio size={12} /> {igBusy ? '연결 중…' : '연결 시작'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 8-LIVE-MULTISTREAM-B/C · YouTube Manual Stream-Key Modal */}
      {ytEgressOpen && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
              onClick={() => !ytBusy && setYtEgressOpen(false)}
              data-testid="live-console-yt-egress-modal">
          <div className="bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5 space-y-3"
                onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Radio size={16} className="text-red-500" />
              <h3 className="text-base font-black text-black dark:text-white">YouTube 송출 연결</h3>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              YouTube Studio &gt; 라이브 스트리밍에서<br />
              <b>스트림 URL</b>과 <b>스트림 키</b>를 복사해주세요.
              <br /><span className="text-[10px] text-gray-400">스트림 키는 저장·로그·응답에 재노출되지 않아요.</span>
            </p>
            {/* STEP YT-AUTO-10 · YouTube 자동 연결 (OAuth 로 직접 준비 · Stream Key 복사 불필요) */}
            <div className="rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-3 space-y-2"
                 data-testid="live-console-yt-auto-block">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-black text-black dark:text-white">
                  YouTube 자동 연결
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  OAuth 로 즉시 준비
                </span>
              </div>
              <p className="text-[10px] text-gray-500 leading-relaxed">
                YouTube 계정이 연결되어 있으면 스트림 URL / 스트림 키를 직접 붙여넣지 않아도 돼요.
                LIVEMOA 가 방송을 자동으로 준비하고 LiveKit Egress 로 연결해 드려요.
              </p>
              <button type="button" onClick={submitYtAutoConnect} disabled={ytBusy}
                      className="w-full py-2 rounded-full bg-[#FF3B30] text-white text-xs font-black hover:bg-[#D6291F] disabled:opacity-40 inline-flex items-center justify-center gap-1"
                      data-testid="live-console-yt-auto-connect">
                <Radio size={12} /> {ytBusy ? '연결 중…' : 'YouTube 자동 연결'}
              </button>
            </div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-wider pt-1">
              또는 직접 입력
            </div>
            <label className="block">
              <span className="text-[11px] font-black text-gray-600 dark:text-gray-300">RTMPS URL</span>
              <input type="text" value={ytUrl} onChange={(e) => setYtUrl(e.target.value)}
                      placeholder="rtmps://a.rtmps.youtube.com:443/live2"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/15 text-xs bg-transparent text-black dark:text-white focus:outline-none focus:border-[#FF3B30]"
                      data-testid="live-console-yt-url-input" />
            </label>
            <label className="block">
              <span className="text-[11px] font-black text-gray-600 dark:text-gray-300">스트림 키</span>
              <input type="password" value={ytKey} onChange={(e) => setYtKey(e.target.value)}
                      placeholder="xxxx-xxxx-xxxx-xxxx"
                      autoComplete="off" autoCorrect="off" spellCheck="false"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/15 text-xs bg-transparent text-black dark:text-white focus:outline-none focus:border-[#FF3B30] font-mono"
                      data-testid="live-console-yt-key-input" />
            </label>
            {/* STEP 8-H-2 · YouTube 댓글 수신용 시청 URL · Egress (video 전송) 와 별개 endpoint */}
            <label className="block">
              <span className="text-[11px] font-black text-gray-600 dark:text-gray-300">
                YouTube LIVE 시청 URL <span className="text-gray-400 font-medium">(선택 · 댓글 수신)</span>
              </span>
              <input type="text" value={ytWatchUrl} onChange={(e) => setYtWatchUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      autoComplete="off"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/15 text-xs bg-transparent text-black dark:text-white focus:outline-none focus:border-[#FF3B30]"
                      data-testid="live-console-yt-watch-url-input" />
              <span className="mt-1 block text-[10px] text-gray-400 leading-relaxed">
                YouTube LIVE 페이지 URL 을 입력하면 시청자 댓글이 Seller Console 통합댓글에 실시간으로 표시돼요.
                입력하지 않으면 영상만 송출되고 댓글은 수집되지 않아요.
              </span>
            </label>
            <p className="text-[10px] text-gray-400">
              LIVEMOA LiveKit → LiveKit Egress → YouTube LIVE. 실패해도 LIVEMOA 원본 방송은 계속돼요.
            </p>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setYtEgressOpen(false)} disabled={ytBusy}
                      className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/15 text-xs font-bold text-gray-700 dark:text-gray-200 disabled:opacity-40"
                      data-testid="live-console-yt-cancel">
                취소
              </button>
              <button type="button" onClick={submitYtEgress} disabled={ytBusy}
                      className="flex-1 py-2 rounded-full bg-red-500 text-white text-xs font-black hover:bg-red-600 disabled:opacity-40 inline-flex items-center justify-center gap-1"
                      data-testid="live-console-yt-submit">
                <Radio size={12} /> {ytBusy ? '연결 중…' : '연결 시작'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 8-H-3 · Comment-only Modal · Egress 를 절대 재호출/중단하지 않음.
          이미 YouTube 송출 중이어도 시청 URL 만 넣으면 poller 를 시작. */}
      {ytCommentOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setYtCommentOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-[#141414] p-5 space-y-3"
                data-testid="live-console-yt-comment-modal">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-black text-black dark:text-white">YouTube 댓글 연결</p>
                <p className="text-[10px] text-gray-400 mt-0.5">송출과 독립 · Stream Key 다시 필요 없음</p>
              </div>
              <button type="button" onClick={() => setYtCommentOpen(false)} className="p-1"
                      data-testid="live-console-yt-comment-close">
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              YouTube LIVE 시청 페이지 URL 을 입력하면 시청자 댓글이 Seller Console 통합댓글에 실시간으로 표시돼요.
              <br /><span className="text-[10px] text-gray-400">현재 YouTube 송출은 이 작업으로 절대 중단되지 않아요.</span>
            </p>
            <label className="block">
              <span className="text-[11px] font-black text-gray-600 dark:text-gray-300">
                YouTube LIVE 시청 URL
              </span>
              <input type="text" value={ytCommentUrl}
                      onChange={(e) => setYtCommentUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      autoComplete="off"
                      className="mt-1 w-full px-3 py-2 rounded-lg border border-black/10 dark:border-white/15 text-xs bg-transparent text-black dark:text-white focus:outline-none focus:border-[#FF3B30]"
                      data-testid="live-console-yt-comment-url-input" />
            </label>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setYtCommentOpen(false)} disabled={ytCommentBusy}
                      className="flex-1 py-2 rounded-full border border-black/10 dark:border-white/15 text-xs font-bold text-gray-700 dark:text-gray-200 disabled:opacity-40"
                      data-testid="live-console-yt-comment-cancel">
                취소
              </button>
              <button type="button" onClick={submitYtCommentConnect} disabled={ytCommentBusy}
                      className="flex-1 py-2 rounded-full bg-red-500 text-white text-xs font-black hover:bg-red-600 disabled:opacity-40 inline-flex items-center justify-center gap-1"
                      data-testid="live-console-yt-comment-submit">
                <MessageCircle size={12} /> {ytCommentBusy ? '연결 중…' : '댓글 연결'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 8-I-5A · LIVE Notice Editor Modal — 신규 endpoint 2건 REUSE (PUT/DELETE). */}
      {noticeEditOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
             data-testid="live-console-notice-modal">
          <div className="absolute inset-0 bg-black/30" onClick={() => !noticeBusy && setNoticeEditOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-5 space-y-3 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2">
                <span className="text-lg">📢</span>
                <p className="text-sm font-black text-[#17181C]">방송 공지</p>
              </div>
              <button type="button" onClick={() => setNoticeEditOpen(false)} disabled={noticeBusy}
                      className="p-1 rounded-full hover:bg-[#F7F8FA]"
                      data-testid="live-console-notice-close">
                <X size={16} />
              </button>
            </div>
            <p className="text-[11px] text-gray-500 leading-relaxed">
              {isTestMode ? '방송 화면에 공지 문구를 표시해요. 최대 200자.' : '방송 화면에 공지 문구와 안내 이미지를 함께 표시해요. 최대 200자.'}
              <br/><span className="text-[10px] text-gray-400">예: 이벤트 안내 · 배송 공지 · 사이즈표 · 구매 안내</span>
            </p>
            <textarea value={noticeDraft} maxLength={200} rows={3}
                      onChange={(e) => setNoticeDraft(e.target.value)}
                      placeholder="예: 오늘 전상품 무료배송 · 사이즈표는 이미지 참고"
                      className="w-full px-3 py-2 rounded-lg border border-[#E5E7EB] text-sm text-[#17181C] placeholder-gray-400 focus:outline-none focus:border-[#FF4F3D]"
                      data-testid="live-console-notice-textarea" />
            {/* STEP OVERLAY-REAL-UI-POLISH · Notice 이미지 additive. SELF-NOTICE-IMAGE-REMOVE-1 · TEST 에서는 숨김. */}
            {NOTICE_IMAGE_UI && (
            <div className="space-y-2 border-t border-[#E5E7EB] pt-2"
                 data-testid="live-console-notice-image-section">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-black text-[#17181C]">이미지 (선택)</span>
                <span className="text-[10px] text-gray-400">최대 5MB · JPG/PNG/WEBP</span>
              </div>
              {!noticeImageUrl && (
                <label className="flex items-center justify-center gap-2 w-full py-3 rounded-lg border border-dashed border-[#E5E7EB] text-xs font-bold text-gray-600 hover:bg-[#F7F8FA] cursor-pointer"
                       data-testid="live-console-notice-image-add">
                  <input type="file" accept="image/*" className="hidden"
                         disabled={noticeImageUploading}
                         onChange={(e) => uploadNoticeImage(e.target.files?.[0])}
                         data-testid="live-console-notice-image-input" />
                  {noticeImageUploading ? '업로드 중…' : '📷 이미지 추가'}
                </label>
              )}
              {noticeImageUrl && (
                <div className="space-y-2">
                  <div className="relative rounded-lg overflow-hidden border border-[#E5E7EB] bg-[#F7F8FA]"
                       data-testid="live-console-notice-image-preview-wrap">
                    <img src={noticeImageUrl} alt="공지 이미지"
                         className="w-full max-h-40 object-contain"
                         data-testid="live-console-notice-image-preview" />
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-[10px] text-gray-500 mr-1">표시 크기</span>
                    {[['small', '작게'], ['medium', '보통'], ['large', '크게']].map(([sz, label]) => (
                      <button key={sz} type="button"
                              onClick={() => setNoticeImageSize(sz)}
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                                noticeImageSize === sz
                                  ? 'bg-black text-white border-black'
                                  : 'border-[#E5E7EB] text-gray-700 hover:bg-[#F7F8FA]'
                              }`}
                              data-testid={`live-console-notice-image-size-${sz}`}>{label}</button>
                    ))}
                    <label className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold border border-[#E5E7EB] text-gray-700 hover:bg-[#F7F8FA] cursor-pointer"
                           data-testid="live-console-notice-image-change">
                      <input type="file" accept="image/*" className="hidden"
                             disabled={noticeImageUploading}
                             onChange={(e) => uploadNoticeImage(e.target.files?.[0])} />
                      변경
                    </label>
                    <button type="button" onClick={() => { setNoticeImageUrl(''); setNoticeImageSize('medium'); }}
                            className="px-2 py-0.5 rounded-full text-[10px] font-bold border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                            data-testid="live-console-notice-image-remove">삭제</button>
                  </div>
                </div>
              )}
            </div>
            )}
            <div className="flex items-center justify-between text-[10px] text-gray-400">
              <span>{noticeDraft.length}/200</span>
              {liveNotice?.updated_at && (
                <span>업데이트 {new Date(liveNotice.updated_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</span>
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              {liveNotice?.text && (
                <button type="button" onClick={clearLiveNotice} disabled={noticeBusy}
                        className="px-3 py-2 rounded-full text-xs font-black border border-[#E5E7EB] text-gray-600 hover:bg-[#F7F8FA] disabled:opacity-40"
                        data-testid="live-console-notice-delete">
                  삭제
                </button>
              )}
              <button type="button" onClick={() => setNoticeEditOpen(false)} disabled={noticeBusy}
                      className="ml-auto px-3 py-2 rounded-full text-xs font-black border border-[#E5E7EB] text-gray-600 hover:bg-[#F7F8FA] disabled:opacity-40"
                      data-testid="live-console-notice-cancel">
                취소
              </button>
              <button type="button" onClick={saveLiveNotice}
                      disabled={noticeBusy || (!noticeDraft.trim() && !noticeImageUrl)}
                      className="px-4 py-2 rounded-full bg-[#FF4F3D] hover:bg-[#D92F27] disabled:opacity-40 text-white text-xs font-black inline-flex items-center gap-1 shadow-sm"
                      data-testid="live-console-notice-save">
                {noticeBusy ? '저장 중…' : ((liveNotice?.text || liveNotice?.image_url) ? '수정 저장' : '공지 등록')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 자동 캡처 카운트다운 overlay.
          video 위에만 보이고 캡처 자체는 video element 를 drawImage 로 추출하므로
          overlay 가 캡처 결과에 포함되지 않음. */}
      {quickCountdown !== null && (
        <div className="fixed inset-0 z-[55] pointer-events-none flex items-center justify-center"
             data-testid="live-console-quick-countdown">
          <div className="w-32 h-32 rounded-full bg-black/70 flex items-center justify-center">
            <span className="text-7xl font-black text-white tabular-nums">
              {quickCountdown > 0 ? quickCountdown : '📸'}
            </span>
          </div>
        </div>
      )}

      {/* STEP LIVE-STUDIO-CONSOLE-SPLIT-3 · Shared QuickAddModal mount.
          STEP STUDIO-MOBILE-QUICK-REGISTER-1 · 캡처 File + 테스트 모드 가상 등록 props. */}
      <QuickAddModal open={quickAddOpen}
                     onClose={() => { setQuickAddOpen(false); setQuickCaptureFile(null); }}
                     scheduleId={scheduleId}
                     initialImageFile={quickCaptureFile}
                     isTestMode={isTestMode}
                     uploadInTest
                     onTestRegister={isTestMode ? handleTestQuickRegister : null}
                     onCreated={() => { /* ProductsTab 자체 polling 이 반영 */ }} />
      {/* STEP LIVE-STUDIO-PC-PRODUCT-SELECT-UI-1 · 공통 "전체 상품에서 등록" picker.
          LIVE Console 과 동일한 component · 동일 API (/seller/broadcasts/{sid}/live-products idempotent).
          성공 후 /live-context polling 이 신규 live_products 를 자동 반영. */}
      <AddProductModal open={addPickerOpen}
                       onClose={() => setAddPickerOpen(false)}
                       scheduleId={scheduleId}
                       currentIds={new Set((liveCtx?.live_products || []).map((p) => p.product_id).filter(Boolean))}
                       onAdded={() => { /* ProductsTab 자체 polling 이 반영 */ }} />
    </div>
  );
};

export default SellerLiveConsole;
